import type { FlagDefinition, FlagType, FlagValue } from './types.js';

/**
 * The flag registry — the authoritative list of feature flags (ADR-0022).
 *
 * Adding a flag means adding a definition here; the DB needs no row until an
 * operator sets a non-default value. Removing a flag from this list makes any
 * surviving DB rows inert (evaluation only considers registered keys), so
 * retire flags by deleting the definition and archiving the row.
 *
 * Each defaultValue is the flag's DECLARED SAFE VALUE — the behaviour a
 * consumer must fall back to when it can't reach the DB or receives no flag
 * map. For the current gates that is the dark/off direction, but "off" is not
 * universally safe; argue the safe direction in PR review per ADR-0022.
 */
export const FLAG_REGISTRY: readonly FlagDefinition[] = [
  {
    key: 'impersonation-enabled',
    description:
      'Kill switch for admin impersonation (ENG-9080), filed during the 18 Aug ' +
      'cross-tenant identity incident. ON (default) = impersonation works as ' +
      'today. OFF = both halves of the flow refuse: the mint route stops issuing ' +
      'redeem URLs, and — the half that actually matters — the REDEEM route ' +
      'refuses before it inserts the nonce or calls generateLink, so a URL that ' +
      'was already issued cannot still mint a session. Redeem tokens live ' +
      'REDEEM_TTL_SECONDS = 300, so gating only the mint route would leave five ' +
      'minutes in which new sessions keep appearing after an operator believes ' +
      'they have stopped it. Evaluated as a GLOBAL stage value, read UNCACHED on ' +
      'the redeem path (see readImpersonationEnabled): a kill switch that takes a ' +
      'cache TTL to bite is not a kill switch. Containment only — it does not fix ' +
      'the underlying defect that redeem writes the target\'s cookies into ' +
      'whatever browser context opens the URL (ENG-8923 / ENG-9020).',
    flagType: 'boolean',
    // Declared safe value is `true` = impersonation available, i.e. today's
    // behaviour, so merging this changes nothing until an operator flips it.
    //
    // NOTE the asymmetry with the other gates in this file, and it is
    // deliberate: their safe direction is "off / don't do the new thing"
    // because they gate NEW behaviour. This one gates EXISTING behaviour, so
    // the no-op default is `true`. The READ direction is where the safety lives
    // instead — an unreachable flag store resolves to DISABLED, not to this
    // default. See readImpersonationEnabled for that argument.
    defaultValue: true,
    // Enforcement gate (ADR-0022 section 4): re-enabling impersonation is
    // "relaxing a security control", exactly the mutation that must not happen
    // on a single unguarded write. The emergency direction (turning it OFF)
    // costs one confirmation, which is the right price.
    sensitive: true,
    // Deliberately NOT public. A browser has no business evaluating whether
    // impersonation is available; both consumers are server-side route
    // handlers.
  },
  {
    key: 'at-rest-v2-writes',
    description:
      'ADR-0061 A2→arm cutover (ENG-8944, parent ENG-8764). Gates whether the ' +
      'at-rest writer (encryptSecret/encryptAtRest) emits the v2 AAD-bound ' +
      'envelope when a DecryptContext is supplied; OFF (default) = the legacy v1 ' +
      'envelope (pre-ADR-0061 behaviour). Resolved FLEET-WIDE and process-global ' +
      'via setV2WritesArmed at the API + webapp + cron bootstrap seams, so ONLY ' +
      'the stage value applies — per-org overrides do not (the crypto writer ' +
      'carries no request/org context). Ships dark. Flip on only once every ' +
      'reader parses v2 (fleet-wide since release A1) AND the re-seal engine can ' +
      're-seal v2 blobs before any AUTH_ENCRYPTION_KEY rotation (slice 5b). ' +
      'Reversible without redeploy: clearing it returns the writer to v1 within ' +
      'the flag cache TTL; already-written v2 blobs stay readable.',
    flagType: 'boolean',
    // Declared safe value is `false` = v1 writes, the pre-ADR-0061 behaviour. A
    // flag-read error must never arm v2 on its own (fail-safe = keep writing v1).
    defaultValue: false,
    // Deliberately NOT public (a server/process gate, never read client-side) and
    // NO envVar (the consumers — API + webapp + cron bootstraps — evaluate the
    // flag directly; a bare AGT_*_ENABLED env gate would trip check-new-env-gates.sh).
    // Sensitive: arming changes the durable at-rest ciphertext format fleet-wide, a
    // deliberate staged security flip, so mutations require explicit confirmation.
    sensitive: true,
  },
  {
    key: 'opencode-host-wizard',
    description:
      'Show a framework selector (Claude Code | opencode) in the new-host wizard, plus the ' +
      'opencode auth mode (xAI/Grok, per-agent minted keys). Off = the wizard only creates ' +
      'Claude Code hosts (opencode host creation stays CLI/programmatic). Additive UI gate; ' +
      'ships dark. The backend mint + claude_auth_mode=xai are enabled separately.',
    flagType: 'boolean',
    defaultValue: false,
    // Read client-side by the new-host wizard (usePublicBooleanFlag), so it must
    // be in the browser-exposed public map.
    public: true,
  },
  {
    key: 'opencode-live-view',
    description:
      'opencode-native Live View / Diagnostics (ENG-7927): the agent Diagnostics tab renders a ' +
      'structured session transcript (messages + tool calls + reasoning) for opencode agents ' +
      'instead of the tmux pane, by polling GET /admin/agents/:id/opencode-transcript (which ' +
      'SSM-reads a redacted snapshot the manager writes) ~every 3s for a bounded window. Boolean ' +
      'gate; ships dark; when OFF the route returns feature_disabled and opencode agents show the ' +
      'static diagnostics snapshot. Claude Code agents are unaffected (they keep the tmux pane).',
    flagType: 'boolean',
    defaultValue: false,
    // Read client-side by the Diagnostics tab (usePublicBooleanFlag), so it must
    // be in the browser-exposed public map. Additive viewer gate (like
    // admin-live-pane): not sensitive.
    public: true,
  },
  {
    key: 'github-backup',
    description:
      'Org-level GitHub daily auto-backup (ENG-7646, ENG-7582 Part 2). Rollout kill-switch; ' +
      'ships dark. The enterprise ENTITLEMENT is enforced separately via plans.github_backup ' +
      '(getPlanForOrg); this flag only stages rollout on top of that entitlement.',
    flagType: 'boolean',
    defaultValue: false,
  },
  {
    key: 'id-keyed-layout-migration',
    description:
      'Dark migration engine (ENG-7891 / ADR-0049 slice 3): convert an EXISTING legacy ' +
      'codename-keyed agent host dir to the agent_id-keyed layout (real ~/.augmented/{agent_id} ' +
      'dir + codename compatibility symlink) at the next pre-spawn tick, moving the Claude ' +
      'transcript store so the agent keeps its history. Resolved PER-AGENT (getBooleanForAgent) ' +
      'so a one-way-door migration can be armed one agent at a time. Ships dark; NOT armed until ' +
      'a test-host rehearsal passes (the last ENG-7891 acceptance gate). Crash recovery + the ' +
      'per-agent preconditions (session-down, WhatsApp-writer quiesce, id-aware hook regen by the ' +
      'spawn funnel) are always-on and flag-independent; this flag only gates INITIATING a fresh ' +
      'conversion. Deliberately no host-wide env override - arming is per-agent to avoid flipping ' +
      'every legacy agent on a host at once.',
    flagType: 'boolean',
    // Declared safe value is `false` = no migration. A flag-DB read error must
    // never move a customer agent's on-disk state on its own (fail-safe = leave
    // the legacy layout untouched).
    defaultValue: false,
    // One-way-door filesystem migration of a live agent's durable state: arming
    // it MOVES data, so mutations require explicit confirmation (ADR-0022 §4).
    sensitive: true,
  },
  {
    key: 'per-agent-host-credentials',
    description:
      'Per-agent host credentials on DEDICATED hosts (ENG-8992, extending ADR-0042 pt3b). ' +
      'When ON, the manager mints a per-agent org-scoped `tlk_` key at spawn and injects it as ' +
      'that container\'s AGT_API_KEY, instead of forwarding the one HOST-WIDE key into every ' +
      'agent. Today every container on a dedicated host holds the same credential, so an agent ' +
      'can exchange a claim-less token and read a SIBLING agent\'s decrypted integration ' +
      'credentials via POST /host/agent-integrations. The request-time guard that stops this ' +
      '(verifyHostAgentAccess reading the token\'s agent_id claim, ENG-7449) is ALREADY live in ' +
      'production - it no-ops only because no container ever carries a scoped key, so this flag ' +
      'gives the existing gate something to check rather than adding a new one. ' +
      'POOL hosts are NOT governed by this flag: per-agent scope is mandatory there (ENG-7489) ' +
      'and stays unconditional. Read by the MANAGER at spawn (hostFlagStore), so a central flip ' +
      'applies to subsequent spawns - already-running agents keep their current credential until ' +
      'they respawn, which is what makes the rollout staged rather than a fleet-wide cutover. ' +
      'Ships dark (default off). AGT_PER_AGENT_HOST_CREDENTIALS_ENABLED is the per-host escape ' +
      'hatch and kill switch; precedence is env override > flag value > this default.',
    flagType: 'boolean',
    // Declared safe value is `false` = keep forwarding the shared host key. A
    // flag-read error must not change which credential a container is spawned
    // with; the fail-safe direction here is "no behaviour change", and a mint
    // FAILURE (once armed) separately fails the spawn CLOSED rather than
    // falling back to the shared key.
    defaultValue: false,
    envVar: 'AGT_PER_AGENT_HOST_CREDENTIALS_ENABLED',
    // Disabling this RESTORES the shared host-wide key to every container, and
    // with it the cross-agent credential read ENG-8992 closes. A credential-scope
    // reduction should not be one click away without confirmation — same posture
    // as `github-broker-credentials`, the other credential-delivery flag.
    sensitive: true,
    // The reader is host-side (`hostFlagStore().getBoolean(...)` in the manager),
    // so a host on an older agt-cli has no consumer code: it keeps forwarding the
    // shared key no matter what the flag says. Without `since`, the flip-reach
    // modal would report every recently-seen host as honoring the flip — the
    // dangerous direction, because it reads as "armed" when it is not.
    //
    // Resolved from the PUBLISHED artifacts, not from the merge timestamp: the
    // tarballs for 0.28.617 and 0.28.618 were unpacked and searched for the
    // `-e AGT_API_KEY=` push in `spawnSession`. 0.28.617 does not contain it;
    // 0.28.618 does, along with the flag key, `dedicatedEnabled` and the
    // `skipped-per-agent-key-mint-failed` decision — so the whole reader
    // shipped together and core was not stale in that bundle.
    //
    // Bisected rather than inferred because the CLI auto-publishes on merge and
    // several unrelated versions landed within minutes either side; picking the
    // version whose timestamp merely looked closest would have been a guess
    // wearing a precise number, which is the failure this field invites.
    since: '0.28.618',
  },
  {
    key: 'agent-hours-billing-enabled',
    description:
      'Agent-hours (utilisation) billing (ENG-7910). Per-org gate; ships dark. When on for an ' +
      'org, its closed billing periods are settled into org_agent_hours_settlements from the ' +
      'plan/override agent-hours terms, and the customer billing surface shows used-vs-included ' +
      'hours + projected overage. Off = no settlement written, no charge, surface hidden. Launch ' +
      'prices are seeded in plan_billing_terms; flip on per-org for a controlled rollout.',
    flagType: 'boolean',
    defaultValue: false,
    // Billing-enablement gate: turning it ON starts charging an org for
    // utilisation, so mutations require explicit confirmation.
    sensitive: true,
  },
  {
    key: 'account-enforcement-auto-billing',
    description:
      'Auto-trip the ENG-7909 account-enforcement ladder on billing delinquency (the deferred ' +
      'auto-wire). When ON for an org, a Stripe subscription entering `past_due` (payment failed) ' +
      'creates an org-scoped kill_switches row at mode=warn, source=auto (agents keep running; the ' +
      'ladder appends the "there is an issue with your account" support notice); after the grace ' +
      'window (BillingEnforcementSweep, default 7d still past_due) it escalates warn→mute (inbound ' +
      'intercepted). It NEVER auto-halts - halt stays a manual break-glass. Recovery (status back to ' +
      'active/trialing) clears the auto switch; a MANUAL switch is never touched. Off (default) = no ' +
      'auto switch is ever written, so past_due only tightens plan-cap entitlements as today. The ' +
      'mechanism (mode ladder, manual selector) shipped in Slices 1-3; this is the automatic trigger, ' +
      'so it ships dark - flip on per org from the admin Feature Flags page. Evaluated API-side only ' +
      '(webhook + cron); no host-side envVar (the manager materializes the resulting kill_switch, not ' +
      'this flag).',
    flagType: 'boolean',
    // Declared safe value is `false` = no auto enforcement. Fail-safe direction: a
    // flag-DB read error must never start muting a customer's agents on its own.
    defaultValue: false,
    // Enforcement gate: turning it ON lets a billing event stop a customer's
    // inbound (mute) without an operator in the loop, so mutations require
    // explicit confirmation (ADR-0022 §4).
    sensitive: true,
  },
  {
    key: 'auto-pause',
    description:
      'Auto-pause agents on sustained hourly-cost breach (ENG-5561). Boolean gate; ships dark.',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AUGMENTED_AUTO_PAUSE_ENABLED',
    // Cost-enforcement gate: relaxing it (turning auto-pause off) removes a
    // spend control, so mutations require explicit confirmation.
    sensitive: true,
  },
  // ENG-7754: 'team-scoped-visibility' (ENG-7122) archived - the restriction it
  // gated is now the unconditional default for every org (a plain org
  // member/viewer only sees/resolves teams they directly belong to; owner/admin
  // keep cross-team reach). Enforced in code (routes/teams.ts, middleware/auth.ts,
  // lib/team-resolver.ts), no longer flag-gated. Override rows dropped by
  // migration 20260714000005. flag-archive-allow: team-scoped-visibility superseded by unconditional default (ENG-7754)
  {
    key: 'channel-busy-ack',
    description:
      'Busy-but-alive ack notices when an agent is mid-task (ENG-6180). ' +
      'Default ON (ENG-8039: the feature was introduced dark on 2026-07-16 and never fired in prod). ' +
      'Set AGT_CHANNEL_BUSY_ACK_ENABLED=false or override via the Feature Flags admin page to disable per-host.',
    flagType: 'boolean',
    defaultValue: true,
    envVar: 'AGT_CHANNEL_BUSY_ACK_ENABLED',
  },
  {
    key: 'live-page-comments',
    description:
      'Text-selection commenting on public Augmented Live pages for authenticated team members ' +
      '(ENG-6788). When on, a member viewing live.augmented.team/{slug} can select text and send ' +
      'a comment to the agent via the relay bridge; evaluated per-org in GET /artifacts/:slug/' +
      'comment-access. Additive feature, not an enforcement control. Boolean gate; ships dark.',
    flagType: 'boolean',
    defaultValue: false,
  },
  {
    key: 'integration-multi-connection',
    description:
      'Allow adding a SECOND+ connection of one managed integration on the same agent ' +
      '(ENG-7543 / ADR-0045 Phase 3), discriminated by connection_key (e.g. two Gmail ' +
      'mailboxes). When OFF, only the default connection can be created (today\'s behaviour: ' +
      'a duplicate 409s). Gates 2nd-connection CREATION only; runtime N-server surfacing is a ' +
      'later phase. Evaluated per-org. Additive capability, not an enforcement control. ' +
      'Boolean gate; ships dark.',
    flagType: 'boolean',
    defaultValue: false,
    // Exposed to the browser so the add-integration dialog can show the
    // "Add another connection" affordance only when the org is enabled (rather
    // than rendering a control whose create POST would 403). Additive, not
    // sensitive.
    public: true,
  },
  {
    key: 'integration-cap-enforcement',
    description:
      'Enforce the per-agent integration cap (ENG-7180 / ENG-7639). When ON for an org, adding ' +
      'an integration to an agent already at its plan cap (plans.max_integrations_per_agent, ' +
      'default 10) is refused with a 409 across all three attach paths (POST /integrations, the ' +
      'OAuth callback, and the GitHub App manifest-init), and GET /integrations/agent-cap reports ' +
      'at_limit so the webapp Add control blocks. When OFF (default) the cap is not enforced - an ' +
      'agent can add integrations without limit and agent-cap reports at_limit=false. Evaluated ' +
      'per-org API-side via getEvaluatedFlags; the authoritative BEFORE INSERT DB trigger was ' +
      'removed in ENG-7639, so the flag-gated app-level pre-flights are the sole enforcement ' +
      'point. Boolean gate; ships OFF (enforcement disabled) - flip ON per org from the admin ' +
      'Feature Flags page to re-arm the cap.',
    flagType: 'boolean',
    // Declared safe value is `false` = NOT enforced. This is both the ENG-7639
    // intent (disable the cap for now) and the fail-safe direction for the route
    // read: a flag-DB error degrades to "don't enforce", so a flags outage never
    // blocks a customer's integration add. Flip on per org to re-arm enforcement.
    defaultValue: false,
    // Enforcement gate: leaving it OFF relaxes a plan-capacity control and turning
    // it ON blocks customer adds - either way it is a deliberate, audited decision,
    // so mutations require explicit confirmation (ADR-0022 §4).
    sensitive: true,
  },
  {
    key: 'kanban-push-status',
    description:
      'Write a kanban card\'s status back to the ticket it was imported from (ENG-8384). When ON ' +
      'for a team, a status change on an external-origin card (source=integration) is enqueued by ' +
      'a DB trigger and drained by the kanban-status-push cron, which calls the adapter\'s optional ' +
      'pushStatus - Linear resolves the target workflow state for the issue\'s own team and calls ' +
      'LINEAR_UPDATE_ISSUE. When OFF (default) the outbox rows are drained straight to skipped and ' +
      'the team sees ZERO upstream writes, which is the whole point of the gate: this is the first ' +
      'code path that MUTATES a customer\'s own Jira/Linear instance, so it must never be ' +
      'silently-on. Adapters that cannot honestly push (Jira has no transition tool in its ' +
      'Composio pack) omit pushStatus and their cards report local-only regardless of this flag. ' +
      'Boolean gate; ships dark; evaluated per-team at drain time.',
    flagType: 'boolean',
    // Declared safe value is `false` = do not write to customer systems. A flag
    // outage must degrade to "don't touch their tracker", never to an
    // unattributed mutation in someone else's tool.
    defaultValue: false,
    // Turning it ON starts writing into a customer-owned system. That is exactly
    // the deliberate, audited decision ADR-0022 §4 reserves confirmation for.
    sensitive: true,
  },
  {
    key: 'augmented-live-stream-producer',
    description:
      'Augmented Live live-preview streaming producer (ENG-7210). The receiver (codec, ' +
      'artifact-draft channel, stream route, console Live Preview tab) shipped under ENG-6234 ' +
      'but never engages because nothing writes the working file the manager scanner watches. ' +
      'When ON for an org, a host-side PostToolUse hook mirrors agt-live.publish/editing content ' +
      'to ~/.augmented/{codeName}/artifacts/<slug>/index.html, so the scanner mints a draft and ' +
      'streams a keyframe to the console Live Preview tab. Additive, best-effort. Boolean gate; ' +
      'ships dark. Materialized to the host flags-cache; the bash hook reads it (operator/canary ' +
      'override AGT_LIVE_STREAM_PRODUCER_ENABLED).',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AGT_LIVE_STREAM_PRODUCER_ENABLED',
  },
  {
    key: 'auto-provision-agt-live',
    description:
      'Auto-provision the Augmented Live integration (free tier, watermarked) into every NEW ' +
      'organization as an org-scoped install (ENG-7819). Consumed in TWO places: the ' +
      'seed_default_live_integration AFTER INSERT trigger on organizations reads the EXPLICIT ' +
      'feature_flags row (a plpgsql trigger cannot see this compiled default, so dark = no row ' +
      '= trigger no-ops; flipping the flag on this page writes the row the trigger reads - ' +
      'GLOBAL value only, per-org overrides are not consulted by the trigger), and the ' +
      'POST /organizations route evaluates it normally to send the owner provisioning notice. ' +
      'Restricted-posture orgs are skipped; opt-out (remove/block) is permanent - no reconciler. ' +
      'Boolean gate; ships dark. Pre-flip gates: docs/operator/default-integrations-admission.md.',
    flagType: 'boolean',
    defaultValue: false,
    // Grants every new open org a public-publishing capability by default;
    // flipping it on is fleet-shaping and worth an explicit confirm (ADR-0022
    // §4, same treatment as augmented-support-auto-provision).
    sensitive: true,
  },
  {
    key: 'admin-live-pane',
    description:
      'Live agent pane streaming on the platform-admin surfaces (ENG-6588): the agent ' +
      'Diagnostics tab and the /admin embed poll GET /admin/agents/:id/pane (tmux ' +
      'capture-pane over SSM) ~every 3s for a bounded window. Boolean gate; ships dark; ' +
      'when OFF the route returns feature_disabled and the UI falls back to the 30s static ' +
      'diagnostics snapshot. It is the no-deploy kill switch bounding the shared-SSM blast ' +
      'radius (SSM SendCommand throttles account-wide, shared with incident-response runbooks).',
    flagType: 'boolean',
    defaultValue: false,
    // No envVar: the DB stage/org override is the kill switch (ADR-0022), so there
    // is no host-side env override to materialise.
    //
    // Exposed to the browser so the admin UI can hide the live control when the
    // gate is off rather than rendering a button that 403s.
    public: true,
  },
  {
    key: 'admin-send-keys',
    description:
      'Interactive send-keys rescue on the live terminal (ENG-6611): POST ' +
      '/admin/agents/:id/send-keys runs a buttons-only, allowlisted tmux send-keys (Enter, ' +
      'Ctrl-C, Esc, arrows, single-char answer + Enter) over SSM to rescue a wedged agent. ' +
      'It is a customer-host WRITE, so it ships dark and the route ALSO hard-gates the target ' +
      "to an is_internal (IL-owned) org regardless of this flag - customer agents are a " +
      'separate later decision. Gate evaluated server-side at the send endpoint, independent ' +
      'of Diagnostics-tab visibility (the ENG-6639 firewall). No free-text (that is ENG-6643).',
    flagType: 'boolean',
    defaultValue: false,
    // Capability change on customer infrastructure; flipping it is worth an explicit confirm.
    sensitive: true,
    // Exposed to the browser so the live modal can show/hide the quick-action row.
    public: true,
  },
  {
    key: 'channel-replay',
    description:
      'Durable channel-inbound replay (ENG-5969): re-push an uncleared pending-inbound ' +
      'marker when the session is alive so a dropped fire-once notification is recovered ' +
      '(bounded by MAX_MARKER_REPLAYS). Now the fleet default (enabled fleet-wide by ' +
      'ENG-6354, promoted to the compiled default by ENG-6683). The flag and the ' +
      'AGT_CHANNEL_REPLAY_ENABLED env override are retained as the operational kill ' +
      'switch (it actively re-delivers inbound); precedence is env override > flag ' +
      'value > this default.',
    flagType: 'boolean',
    defaultValue: true,
    envVar: 'AGT_CHANNEL_REPLAY_ENABLED',
  },
  {
    key: 'restart-doorbell',
    description:
      'Fast agent-restart lane (ENG-7335): when ON, the manager subscribes to ' +
      'host_agents restart_requested_at changes over its existing Realtime channel ' +
      'and services a dashboard-issued restart on a narrow per-agent lane (kill + ' +
      'respawn the one agent) within a few seconds, instead of waiting up to 50-110s ' +
      'for the next full poll cycle to notice it. The slow poll remains the durable ' +
      'backstop, so a missed doorbell only costs latency, never correctness. Ships ' +
      'dark (default off); staged on the agt-aws-1 canary before fleet enable. The ' +
      'AGT_RESTART_DOORBELL_ENABLED env override is the per-host escape hatch and ' +
      'kill switch; precedence is env override > flag value > this default.',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AGT_RESTART_DOORBELL_ENABLED',
  },
  {
    key: 'fast-mcp-restart-respawn',
    description:
      'Fast MCP-restart respawn (ENG-7401 Phase 2): when ON, after the manager stops ' +
      'an agent session for an MCP-servers change (integration add/remove, channel-set ' +
      'change, managed-toolkit churn, bind remediation) it respawns that one agent ' +
      'immediately - reusing the same spawn path as the poll and the restart-doorbell ' +
      'fast lane - instead of waiting up to a full poll cycle (~50-110s) for the next ' +
      'health check to notice the session is down. .mcp.json was already re-rendered ' +
      'before the stop, so the immediate respawn picks up the new config. The slow ' +
      'poll remains the durable backstop, so a failed fast respawn only costs latency, ' +
      'never correctness. Ships dark (default off); staged on the agt-aws-1 canary ' +
      'before fleet enable. The AGT_FAST_MCP_RESTART_RESPAWN_ENABLED env override is ' +
      'the per-host escape hatch and kill switch; precedence is env override > flag ' +
      'value > this default.',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AGT_FAST_MCP_RESTART_RESPAWN_ENABLED',
  },
  {
    key: 'never-spawned-session-metric',
    description:
      'Never-spawned session metric (ENG-8204): when ON, the agent-heartbeat-monitor ' +
      'cron emits a `SessionAliveAgeSeconds` datapoint for an ACTIVE agent that has ' +
      'never had a verified session (`last_session_alive_at` null), aged from ' +
      '`created_at`. Without it those agents emit no datapoint at all, and because ' +
      'the session-stale alarm sets `TreatMissingData: notBreaching`, the one alarm ' +
      'built for "fresh heartbeat but no session" can never fire for the very case ' +
      'it describes — a newly-onboarded agent whose session never spawned. ' +
      'Observe-first rollout gate: the cron logs the never-spawned count and oldest ' +
      'age EVERY run regardless of this flag, so the backlog is measurable before ' +
      'the alarm is armed. The AUGMENTED_NEVER_SPAWNED_SESSION_METRIC_ENABLED env ' +
      'var is the highest-precedence operator override; precedence is env override > ' +
      'flag value > this default.',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AUGMENTED_NEVER_SPAWNED_SESSION_METRIC_ENABLED',
    // Enabling this pages on every active agent that has never had a session,
    // which on first flip may surface an existing backlog all at once. Read the
    // observe-phase log line before flipping.
    sensitive: true,
  },
  {
    key: 'channel-silent-loss-alarm',
    description:
      'Channel silent-loss alarm (ENG-6728): when ON, the responsiveness-probe route ' +
      'creates a per-agent CloudWatch alarm that pages on a `ChannelDeflections` ' +
      'Cause=replay_orphaned datapoint (a recoverable inbound aged out without ' +
      'delivery). Observe-first rollout gate, flipped stage-wide from the admin ' +
      'Feature Flags page; the underlying metric ships since ENG-6355. The ' +
      'AUGMENTED_CHANNEL_SILENT_LOSS_ALARM_ENABLED env var is retained as the ' +
      'highest-precedence operator override; precedence is env override > flag value > ' +
      'this default.',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AUGMENTED_CHANNEL_SILENT_LOSS_ALARM_ENABLED',
    // Enabling the alarm pages on customer-facing message loss; flipping it is an
    // operational decision with blast radius, so mutations require confirmation.
    sensitive: true,
  },
  {
    key: 'frozen-customer-waiting-page',
    description:
      'Frozen-prompt-with-a-customer-waiting PAGE (ENG-8683): when ON, the ' +
      'responsiveness-probe route opens a `warning` `agent_frozen_customer_waiting` ' +
      'alert for an agent whose input watchdog gave up on the session AFTER a message ' +
      'was queued, and whose message is still unanswered past the window. Both halves ' +
      'were already measured in the same request and both were `info` (agent_input_stuck, ' +
      'agent_pending_inbound), which alert-pager drops before routing — so the condition ' +
      'was detected twice a minute and paged nobody. Defaults ON, unlike the usual ' +
      'ships-dark posture for anything that pages, because both inputs have been in ' +
      'observe mode for months (the input-stuck alarm has defaulted ON since ENG-6055, ' +
      'the pending-inbound metric has been written since ENG-6017) and the conjunction is ' +
      'strictly narrower than either — shipping it dark would make the fix a fleet-wide ' +
      'no-op and leave the ticket open in everything but name. Flip OFF from the admin ' +
      'Feature Flags page to disarm stage-wide; the wait window is tuned with the ' +
      'AUGMENTED_FROZEN_CUSTOMER_WAITING_SECONDS API tunable (default 300). No envVar: an ' +
      'API-evaluated flag with a declared envVar risks the ENG-8303 sst.config.ts pin, and ' +
      'a new AUGMENTED_*_ENABLED gate is what check-new-env-gates.sh rejects.',
    flagType: 'boolean',
    defaultValue: true,
  },
  {
    key: 'promise-outstanding-alarm',
    description:
      'Outstanding-interim-promise alarm (ENG-8265): when ON, the responsiveness-probe ' +
      'route creates a per-agent CloudWatch alarm on `PromiseOutstandingOldestAgeSeconds` ' +
      '— an inbound the agent acknowledged with an interim reply ("on it") and never ' +
      'answered substantively. Closes the blind spot where an ack satisfied ' +
      'channel-silent-loss, refreshed pending-inbound, and left agent_stall with no ' +
      'in_progress card to see, so a broken promise looked like a served request. ' +
      'Defaults ON (armed on deploy) because the alert it opens is `info` severity — ' +
      'dashboard-only, never paged — so a day-one false positive costs a visible row, ' +
      'not a woken human. Flip OFF from the admin Feature Flags page to disarm ' +
      'stage-wide; the window itself is tuned with the ' +
      'AUGMENTED_PROMISE_OUTSTANDING_THRESHOLD_SECONDS API tunable (default 1800).',
    flagType: 'boolean',
    defaultValue: true,
  },
  {
    key: 'connectivity-probe',
    description:
      'Host-side rolling integration connectivity probe (ENG-5641): when ON, the ' +
      'manager probes each integration ~hourly using the agent\'s real creds/MCP and ' +
      'reports to /host/integration-connectivity, which populates last_connectivity_* ' +
      'and feeds the central escalation evaluator (integration-connectivity-escalation) ' +
      'and reconnect notifications. It is the only continuous writer of ' +
      'last_connectivity_status (the monitor cron only reads it). Migrated from the raw ' +
      'AGT_CONNECTIVITY_PROBE_ENABLED env gate to this registry flag (ENG-7220) so both ' +
      'halves of the connectivity subsystem are flag-managed; the env var is retained as ' +
      'the highest-precedence operator override (env override > flag value > this ' +
      'default). Boolean gate; ships dark.',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AGT_CONNECTIVITY_PROBE_ENABLED',
  },
  {
    key: 'bind-remediation',
    description:
      'Host-side bind-remediation self-heal (ENG-6203): when ON, after a hot-reload ' +
      'MCP restart fails to bind its tools across the bounded verify attempts, the ' +
      'manager forces ONE more gated re-respawn (breaker-capped) so the ensure-pass ' +
      're-reads .mcp.json and binds the tools, instead of leaving the live session on ' +
      'its pre-restart tool set. ENG-7575 layers a first-connect backoff on top ' +
      '(defer the re-respawn while a freshly-added integration is still transient_error). ' +
      'Migrated from the raw AGT_BIND_REMEDIATION_ENABLED env gate to this registry flag ' +
      '(ENG-7586, ADR-0022) so it can be flipped centrally/staged like connectivity-probe; ' +
      'the env var is retained as the highest-precedence operator override (env override > ' +
      'flag value > this default). Boolean gate; ships dark.',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AGT_BIND_REMEDIATION_ENABLED',
  },
  {
    key: 'session-tool-probe',
    description:
      'Host-side live-session tool-bind probe (ENG-7053 / ENG-7220): when ON, the ' +
      'manager periodically checks whether each integration\'s MCP tools are actually ' +
      'bound in the running agent session (stdio child alive / remote MCP answers ' +
      'tools/list) and reports a bound|missing|unreachable|unknown verdict to ' +
      '/host/session-tool-bind. This is the signal the Add Integration modal polls to ' +
      'confirm a newly-added integration came online after the agent\'s next restart, ' +
      'rather than resolving at "credentials verified". Observational only (no auto ' +
      'rebind). Boolean gate; ships dark. AGT_SESSION_TOOL_PROBE_ENABLED is the ' +
      'highest-precedence operator override; precedence is env override > flag value > ' +
      'this default.',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AGT_SESSION_TOOL_PROBE_ENABLED',
  },
  {
    key: 'session-tool-rebind',
    description:
      'Auto-rebind MCP tools that fell out of a running session (ENG-7318, ' +
      'ENG-7053 Slice A). When ON, the session-tool-bind probe does not just ' +
      'report a hard-negative verdict - the manager SIGTERMs just that ' +
      "integration's MCP child so Claude Code's transport respawns it (no full " +
      'agent restart, conversation context preserved). Fires on `missing` (a dead ' +
      'stdio/proxy child - the ENG-7301/Phil case) and on `unreachable` (an alive ' +
      'ENG-6859 OAuth-proxy child whose upstream stopped answering tools/list after ' +
      'a token rotated under the live session - the ENG-7318/Pepper case). Requires ' +
      'the `session-tool-probe` flag (it consumes that probe\'s verdict). The reap ' +
      "is debounced (the probe's ~1h cadence plus a per-integration cooldown) so a " +
      'persistently-broken integration is rebound at most once per window, never ' +
      'every cycle. Boolean gate; ships dark - canary per host (agt-aws-1) before ' +
      'any fleet flip. AGT_SESSION_TOOL_REBIND_ENABLED is the highest-precedence ' +
      'operator override; precedence is env override > flag value > this default.',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AGT_SESSION_TOOL_REBIND_ENABLED',
    // Reaps MCP children on a live customer agent (targeted, not a full restart,
    // but still a runtime mutation on customer infra); flipping it is worth an
    // explicit confirm.
    sensitive: true,
  },
  {
    key: 'channel-skip-reaction',
    description:
      'Seen-but-skipping reaction (ENG-6464): the agent adds a configured emoji ' +
      '(e.g. ➖ / 🫡) to a DM or thread message it saw but deliberately chose not ' +
      'to reply to, so the sender can tell "seen and skipped" from "never ' +
      'received". Explicit-agent-verb model (no Stop-hook inference). Boolean ' +
      'gate; ships dark — inert until flipped on per host/org.',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AGT_CHANNEL_SKIP_REACTION_ENABLED',
  },
  {
    key: 'channel-block-turn-end',
    description:
      'Block-turn-end / composed-but-unsent detector (ENG-6467, ADR-0024 Slice 2.5): ' +
      'closes the D1 silent-loss class where the agent composes a channel reply as ' +
      'plain turn text and ends the turn without calling the reply tool. When ON, the ' +
      'ghost-reply Stop hook returns {"decision":"block"} for an inbound it owes a reply ' +
      'to (a non-discretionary, non-undeliverable pending marker for the last channel ' +
      'tag, with no matching reply tool_use this turn) so the MODEL sends the reply itself ' +
      '— right thread, right content, no recovery mis-correlation (D2). Capped to one ' +
      'block per inbound via a per-marker ledger (NOT stop_hook_active, which is ' +
      'unverified across --resume); after one block it falls through to the existing ' +
      'recovery-outbox. Boolean gate; ships dark — canary per host (agt-aws-1) before any ' +
      'fleet flip. When OFF the hook behaves exactly as today (recovery only).',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AGT_CHANNEL_BLOCK_TURN_END_ENABLED',
    // Gating turn-completion fleet-wide is a high-blast-radius capability change;
    // flipping it (esp. beyond the canary host) is worth an explicit confirm.
    sensitive: true,
  },
  {
    key: 'block-turn-end-all-markers',
    description:
      'Multi-marker block-turn-end (ENG-7397 / WS3, cross-thread reply routing). ' +
      'Extends channel-block-turn-end from the LAST channel tag to EVERY pending ' +
      'marker across all sources: when ON, the ghost-reply Stop hook enumerates all ' +
      'obligated-and-unanswered inbounds and returns a single {"decision":"block"} ' +
      'listing each owed conversation by inbound_id + source + safe key, so a ' +
      'multi-thread agent is blocked until it answers ALL of them (with WS2 it ' +
      'answers each by inbound_id, no coordinate risk). Requires channel-block-turn-end ' +
      'also ON (the hook self-gates on both). Marker-semantics-neutral: it changes WHEN ' +
      'the hook blocks, arms/clears no markers. The env var ' +
      'AGT_BLOCK_TURN_END_ALL_MARKERS_ENABLED is the operator/canary override the bash ' +
      'hook reads directly (materialized by the manager). Ships dark; canary per host ' +
      '(agt-aws-1, where the multi-thread symptom is chronic) before any fleet flip. ' +
      'OFF (default) = the single-marker last-tag behavior, unchanged.',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AGT_BLOCK_TURN_END_ALL_MARKERS_ENABLED',
    // Broadens a turn-completion gate across every thread; flipping it beyond the
    // canary is a high-blast-radius change worth an explicit confirm.
    sensitive: true,
  },
  {
    key: 'direct-chat-recovery',
    description:
      'Always-on direct-chat ghost-reply recovery (ENG-7814, ENG-6722 slice E). ' +
      'direct-chat has no recovery-outbox by default: block-turn-end (channel-block-turn-end) ' +
      'only makes the MODEL re-send and is dark/canary, so a direct-chat turn answered as plain ' +
      'text with no direct_chat.reply is lost. When ON, the ghost-reply Stop hook, for an owed and ' +
      'unanswered direct-chat inbound where block-turn-end did not fire, writes the last assistant ' +
      'text to a direct-chat-recovery-outbox that the direct-chat MCP consumes and POSTs to ' +
      '/host/direct-chat/reply for the session, re-sending the reply the agent produced but never ' +
      'delivered. session_id is unambiguous (one session per marker), so there is no cross-thread ' +
      'mis-correlation risk (why direct-chat can safely re-send where Slack needs the ENG-7806 ledger). ' +
      'Confirm-before-clear: the pending marker is cleared only after the POST confirms; a per-marker ' +
      'recovery ledger caps one in-flight recovery per inbound and re-arms on failure. Independent of ' +
      'channel-block-turn-end (either can be on). The env var AGT_DIRECT_CHAT_RECOVERY_ENABLED is the ' +
      'operator/host override the bash hook reads directly (materialized by the manager) and the MCP ' +
      'resolves via the flags cache. Boolean gate; ships dark, flip per host/org to canary before fleet ' +
      'rollout.',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AGT_DIRECT_CHAT_RECOVERY_ENABLED',
    // Re-sends a reply on the agent behalf (a customer-visible send); flipping it
    // beyond a canary is a blast-radius change worth an explicit confirm.
    sensitive: true,
  },
  {
    key: 'ghost-reply-intent-classifier',
    description:
      'Intentional-non-reply suppression on the ghost-reply RECOVERY path (ENG-7096, ENG-7478). The ' +
      'recovery-outbox consumer in the channel MCP only ever fires for turns where the agent ended ' +
      'with text but did NOT call the matching reply tool for that conversation - a set that includes ' +
      'DELIBERATE non-replies and internal third-person self-status narration ("Filed CS-1443... ' +
      'standing by"). A cheap model (the conversation-eval backend, AGT_CONV_EVAL_*) classifies the ' +
      'recovered text as a real message for the user (deliver) or internal not-replying / self-status ' +
      'narration (suppress), keyed on grammatical person, biased to DELIVER and fail-open (a classifier ' +
      'outage degrades to today\'s behaviour, never a dropped real reply). off = classifier never runs, ' +
      'every recovered reply is posted exactly as today (ships dark, == the old boolean-false). shadow = ' +
      'classify + log the would-suppress verdict for operator adjudication but STILL DELIVER (measure the ' +
      'suppression rate before acting). enforce = actually suppress on a clean high-confidence suppress ' +
      'verdict. The channel server reads this live from the heartbeat flags-cache (or the env override); ' +
      'enforce is a deliberate, audited per-org flip after a shadow soak.',
    flagType: 'enum',
    allowedValues: ['off', 'shadow', 'enforce'],
    // Declared safe value is `off`, NOT `shadow` (deliberately diverging from
    // channel-quarantine-mode). quarantine's shadow is a free local log line;
    // THIS shadow spends a live Haiku classify per recovery candidate, so
    // defaulting to shadow would newly bill every host that never opted in. off
    // preserves the old boolean-false behaviour exactly (classifier never called).
    // Shadow is opt-in per canary; enforce is the audited flip after soak.
    defaultValue: 'off',
    // Enum override AGT_GHOST_REPLY_INTENT_CLASSIFIER_MODE (off|shadow|enforce);
    // resolveGhostReplyMode also grandfathers the old boolean
    // AGT_GHOST_REPLY_INTENT_CLASSIFIER_ENABLED (true->enforce, false->off).
    envVar: 'AGT_GHOST_REPLY_INTENT_CLASSIFIER_MODE',
    // enforce suppresses channel egress, and a false-suppress is silent,
    // unrecoverable loss - so flipping toward enforce is a deliberate, audited
    // change (ADR-0022 sensitive-flag confirm).
    sensitive: true,
  },
  {
    key: 'slack-scheduled-channel-guard',
    description:
      'Guard on slack.reply that refuses to let an active scheduled-task destination silently move a ' +
      'reply to a DIFFERENT channel than the agent named (ENG-8137). CS-1505 gave an explicit inbound_id ' +
      'priority over the task destination but left the no-inbound_id case treating "no inbound reference" ' +
      'as "no destination specified" - and `channel` is a REQUIRED argument, so every call names one. The ' +
      'symptom: a question asked in one channel answered in another, and a reply meant as a DM landing in ' +
      'a public channel. Fires only when the marker carries an EXPLICIT target, the named channel differs ' +
      'from it, and neither an inbound_id, an enforce binding, nor an ENG-7542 channel correction is ' +
      'available to arbitrate. off = never runs (today behaviour: silent retarget). shadow = count + log ' +
      'the would-block but STILL route as today, so the false-positive rate is measurable before anyone ' +
      'refuses a send. enforce = block the reply and tell the agent to retry. Read live from the ' +
      'heartbeat flags-cache (or the env override). NOTE: enforce is currently CLAMPED to shadow ' +
      'host-side and setting it here will not take effect - it stays blocked until ENG-8142 teaches the ' +
      'Stop hook to tell a refused reply from a delivered one, because until then a blocked send fails ' +
      'silently (no recovery, no re-prompt, nothing in the channel). Lifting the clamp is a code change, ' +
      'not a flag flip.',
    flagType: 'enum',
    allowedValues: ['off', 'shadow', 'enforce'],
    // Ships in SHADOW, not off: the counter is the whole point - the fleet metric
    // is currently blind to this misroute, which is why it went unnoticed. shadow
    // is a free local log line + counter (no model spend, no behaviour change), so
    // it measures the fire rate immediately. enforce turns a misroute into a
    // REFUSED send, which trades a confidentiality risk for an availability one -
    // that is the audited per-org flip, taken after reading the shadow numbers and
    // after ENG-8143 shrinks the stale-marker window that dominates the false
    // positives.
    defaultValue: 'shadow',
    // Enum override AGT_SLACK_SCHEDULED_CHANNEL_GUARD_MODE (off|shadow|enforce),
    // resolved by resolveSlackScheduledChannelGuardMode in the channel-server bundle.
    envVar: 'AGT_SLACK_SCHEDULED_CHANNEL_GUARD_MODE',
    // enforce REFUSES a send the agent asked to make - a visible availability
    // change, so flipping toward it is deliberate (ADR-0022 sensitive-flag confirm).
    sensitive: true,
    // ENG-8149: the agt-cli that first carries resolveSlackScheduledChannelGuardMode
    // (published from the ENG-8137 merge c6ff71cd). Without this the flip-reach modal
    // reports FULL reach, because an undefined `since` means "every host can honour
    // it" - true for flags that predate the reach work, wrong for a NEW host-read
    // flag whose reader older hosts simply do not have. Excluded from
    // projectDefinition, so setting it does not roll FLAGS_SCHEMA_VERSION.
    since: '0.28.421',
  },
  {
    key: 'slack-hot-thread-guard',
    description:
      'Server-side hot-thread guard on the slack.reply surface (ENG-7462). Prevents an agent posting a ' +
      'NEW top-level Slack message when it meant to reply inside the thread it is already working in - a ' +
      'prompt/memory rule proved insufficient (the agent had the rule and still slipped). When a reply ' +
      'would otherwise post to channel ROOT (no thread_ts / message_ts / inbound_id, no active kanban ' +
      'card) and the agent has a recent active thread in that channel (its last bot-posted thread, from ' +
      'the persisted trackedThreads cache, within a freshness window), the reply is redirected into that ' +
      'thread. proactive:true no longer implies channel root; posting at root becomes a deliberate action ' +
      '(the to_channel_root flag, or the thread_ts:null sentinel). off = guard never runs, replies with ' +
      'no coords root exactly as today (ships dark). shadow = compute + log the would-redirect but STILL ' +
      'post to root (measure the fire rate before acting). enforce = apply the redirect (a soft-block: ' +
      'redirect + inform, never a hard rejection). Read live from the heartbeat flags-cache (or the env ' +
      'override); enforce is a deliberate per-org flip after a shadow soak.',
    flagType: 'enum',
    allowedValues: ['off', 'shadow', 'enforce'],
    // Ships dark: off preserves today's behaviour exactly (a coordless reply
    // roots). shadow is a free local log line (no model spend, unlike ghost-reply)
    // so it is a cheap opt-in soak; enforce changes where a reply lands, so it is
    // the audited flip.
    defaultValue: 'off',
    // Enum override AGT_SLACK_HOT_THREAD_GUARD_MODE (off|shadow|enforce),
    // resolved by resolveSlackHotThreadMode in the channel-server bundle.
    envVar: 'AGT_SLACK_HOT_THREAD_GUARD_MODE',
    // enforce reroutes a message the agent asked to send to channel root into a
    // thread - a visible destination change, so flipping toward it is deliberate
    // (ADR-0022 sensitive-flag confirm).
    sensitive: true,
    // ENG-8235: the agt-cli whose bundle first carries resolveSlackHotThreadMode.
    // Without this the flip-reach modal reports FULL reach (an undefined `since`
    // means "every host can honour it"), which is wrong for a host-read flag that
    // older hosts have no reader for - and this flag's pending action IS a flip,
    // once the ENG-7855 soak has data.
    //
    // BISECTED from the published npm artifacts rather than inferred from the
    // merge commit: 0.28.338 lacks the reader; 0.28.339 (2026-07-18T02:48Z) carries
    // both AGT_SLACK_HOT_THREAD_GUARD_MODE and to_channel_root in
    // dist/mcp/slack-channel.js. ENG-7462 is usually cited as 0.28.340, but two
    // publishes fired 8 minutes apart and .339 is the one that shipped the reader;
    // `since` is a reach THRESHOLD, so .340 would wrongly mark hosts on exactly
    // .339 as un-reached. Excluded from projectDefinition, so setting it does not
    // roll FLAGS_SCHEMA_VERSION.
    since: '0.28.339',
  },
  {
    key: 'compaction-notice',
    description:
      'Compaction courtesy notice (ENG-7339): when a managed Claude Code session compacts its ' +
      'context, the persistent session pauses and stops replying for a stretch, which looks ' +
      'identical to a dead agent from the channel. When ON, the generated PreCompact hook finds ' +
      'the conversation the user is actively on (the last <channel ...> tag in the transcript) ' +
      'and drops a notice into the matching <channel>-notice-outbox; the channel MCP server ' +
      '(alive while the Claude process compacts) posts a short "reorganizing my memory, back ' +
      'shortly" line. The notice path never clears the pending-inbound marker, so the genuine ' +
      'reply the agent still owes after compaction is unaffected. No active channel tag ⇒ silent ' +
      '(idle agents never broadcast). ' +
      'Default ON (ENG-8040: introduced dark on 2026-07-23 via ENG-7339, never activated in prod; ' +
      'Brad Bond reported Sherlock went silent mid-compaction with no notice). ' +
      'Set AGT_COMPACTION_NOTICE_ENABLED=false or override via the Feature Flags admin page to disable per-host. ' +
      'When OFF the hook writes nothing and the consumer drops any stray notice unsent.',
    flagType: 'boolean',
    defaultValue: true,
    envVar: 'AGT_COMPACTION_NOTICE_ENABLED',
  },
  {
    key: 'channel-live-progress',
    description:
      'Live in-channel progress indicator (ENG-6567 Phase 2): while an agent is mid-task on a ' +
      'pending channel inbound, the channel server maintains a slimline Block Kit "⏳ working… ' +
      '(last: <step>)" context message on the thread, driven by a throttled PostToolUse ' +
      'heartbeat, and clears it the moment the final reply lands. Answers "is it still working ' +
      'or done?" without the agent having to narrate. Boolean gate; ships dark — canary per host ' +
      'before any fleet flip. When OFF the heartbeat is still written (cheap, local) but nothing ' +
      'is ever posted, so behaviour is exactly as today.',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AGT_CHANNEL_PROGRESS_ENABLED',
  },
  {
    key: 'agent-restart-approval',
    description:
      'Route an agent\'s self-restart request (ENG-6373) through HITL approval to its Manager ' +
      'instead of the local-confirm + flag fast path. Idle requests auto-approve + notify; ' +
      'requests with active in-flight work page the Manager. Boolean gate; ships dark. When OFF ' +
      'the request_restart tool falls back to the legacy local-flag behaviour.',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AGT_AGENT_RESTART_APPROVAL_ENABLED',
    // Enabling lets a (possibly prompt-injected) agent file Manager-facing
    // approval requests; flipping it is a capability change worth confirming.
    sensitive: true,
  },
  {
    key: 'agent-self-pause',
    description:
      'CS-1632: let an agent stop ITSELF via the self_pause tool when it believes its channel is '
      + 'compromised or is being used to inject instructions. Writes agents.status=paused with '
      + 'paused_reason=agent_self_pause, and only a human can resume from the console. '
      + 'DEFAULTS ON, unlike most gates here: a safety control that has to be armed in advance is '
      + 'missing on every agent nobody armed, including the one being attacked. Turn it OFF per org '
      + 'if an agent misuses it — that is the reverse lever, and it is instant and central, which a '
      + 'host env var would not be. NO envVar: the consumer is the API route, and a bare '
      + 'AGT_*_ENABLED gate would trip check-new-env-gates.sh (same reasoning as v2-writes above).',
    flagType: 'boolean',
    // Declared safe value is TRUE, and that inverts the usual posture on purpose.
    // For most gates the safe default is "off, ships dark". Here OFF means an
    // agent under a suspected compromise cannot stop, so a flag-read error must
    // NOT disable the control — the route evaluates this fail-OPEN.
    defaultValue: true,
    // Turning it off removes an agent's ability to stop itself during an
    // injection attempt; turning it on grants a (possibly prompt-injected) agent
    // a denial-of-service primitive against itself. Both directions are a
    // capability change worth confirming.
    sensitive: true,
  },
  {
    key: 'approval-sod',
    description:
      'Separation-of-duties (SoD) guard for the HITL approval FSM (ENG-6459): ' +
      'off = disabled, shadow = resolve principals + audit conflicts only (no block), ' +
      'enforce = fail-closed refuse of a self-approval or an unresolvable approver. ' +
      'Compares approver vs requesting-human on a canonical organization_people.person_id. ' +
      'This is the org-wide DEFAULT; a per-(team, verb) approval_policies.sod_mode row overrides ' +
      'it (ENG-6678: relax low-risk verbs while keeping enforce on money verbs). ' +
      'Ships dark (off); soak in shadow before enforce.',
    flagType: 'enum',
    allowedValues: ['off', 'shadow', 'enforce'],
    defaultValue: 'off',
    envVar: 'AGT_APPROVAL_SOD_MODE',
    // Access-control gate: flipping to enforce can REFUSE approvals (incl. a
    // legitimate approver whose Slack id isn't linked to a person row), so it
    // must soak in shadow first and is worth confirming on mutation.
    sensitive: true,
  },
  {
    key: 'operator-instruction-rail',
    description:
      'Operator instruction rail (ENG-10343, ADR-0107 D2): lets a platform admin send an agent ' +
      'an instruction over a bearer-authenticated route, which the agent reads back under its ' +
      'own credentials and sees labelled with the verified operator. Boolean gate; ships dark.',
    flagType: 'boolean',
    // Declared safe value is `false`, and OFF is a real state rather than a
    // placeholder: with the rail closed, the only out-of-band route to an agent
    // is the tmux pane, which carries no provenance at all (ADR-0107).
    //
    // ARMING THIS IS WHAT ACCEPTANCE OF ADR-0107 AUTHORISES. That ADR is
    // `Proposed` and gated on acceptance except for its D1 guidance fix, so the
    // write route and the read-path label ship inert until someone accepts it.
    // The flag is where that decision is expressed — not a later deploy.
    //
    // Convergence is NOT a published CLI version (the ENG-9729 lesson): the
    // label ships in packages/mcp and reaches a host only on the next
    // publish -> /host/refresh. An older MCP build renders the instruction
    // fenced and unlabelled, which under-claims authority rather than
    // over-claiming it — so a mixed fleet is safe, but the rail is not yet
    // useful on the un-converged half.
    defaultValue: false,
    envVar: 'AGT_OPERATOR_INSTRUCTION_RAIL_ENABLED',
    // Writing something an agent is told to treat as authenticated is an
    // authority grant, so flipping it is worth confirming.
    sensitive: true,
  },
  {
    key: 'direct-chat-doorbell',
    description:
      'Direct-chat doorbell + pull-cursor delivery (ENG-5927, ADR-0020): the manager rings a ' +
      'content-free doorbell and the agent\'s in-session MCP pulls via the capped ' +
      '/host/direct-chat/poll claim, replacing send-keys/one-shot delivery. Boolean gate; ships dark.',
    flagType: 'boolean',
    // Declared safe value is `false` — keeps today's send-keys/one-shot rail.
    // The doorbell rail is wired but not yet resume-aware (PR-4) nor carrying the
    // consume signal (PR-3), so flip on per-host only during the PR-5 canary.
    defaultValue: false,
    envVar: 'AGT_DIRECT_CHAT_DOORBELL_ENABLED',
  },
  // ENG-7967: the 'kanban-doorbell' flag was RETIRED. The durable direct-chat
  // notice rail is now the SOLE kanban-nudge delivery path (the tmux send-keys
  // fallback was removed with it), so there is nothing left to gate. Archived by
  // migration 20260722000003 (deletes any feature_flags / feature_flag_overrides
  // rows for the key). Do not re-add without re-introducing a second delivery path.
  {
    key: 'direct-chat-stream-reply',
    description:
      'Server-side type-out for direct-chat replies (direct-chat UI redesign Phase 2, ' +
      'docs/design/direct-chat-ui-chat-sdk.md): the channel MCP posts a small anchor via ' +
      '/host/direct-chat/reply, then reveals the rest of the (already-complete) reply ' +
      'progressively through /host/direct-chat/update so the webapp bubble grows instead ' +
      'of appearing as a wall of text. Purely cosmetic (the persisted reply is identical ' +
      'either way). Boolean gate; ships dark.',
    flagType: 'boolean',
    // Declared safe value is `false`, keeping today's single-shot reply (the agent
    // posts the full reply once). The type-out is a no-restart-flippable polish
    // gate; flip on per-host to dogfood before fleet rollout.
    defaultValue: false,
    envVar: 'AGT_DIRECT_CHAT_STREAM_REPLY_ENABLED',
  },
  {
    key: 'direct-chat-drawer',
    description:
      'Direct Chat drawer with the vendored Vercel Chat SDK UI (direct-chat UI redesign ' +
      'Slice 3, docs/design/direct-chat-ui-chat-sdk.md, ENG-6704): a flag-gated right-side ' +
      'drawer with markdown rendering, an auto-growing composer, scroll anchoring and a ' +
      'streamed-reply cursor, wired to the existing relay + Realtime. When on, the agent ' +
      'detail page shows the drawer instead of the plain Direct Chat tab; when off the tab ' +
      'is unchanged. Ships dark.',
    flagType: 'boolean',
    // PUBLIC (ADR-0022 §2): a UI-rollout flag the browser reads via GET /flags to
    // decide whether to render the drawer entry point (mirrors projects-menu).
    // Declared safe value is `false`: the existing Direct Chat tab stays the
    // surface until the drawer is rolled out per org from the admin Feature Flags
    // page. No envVar: a browser-only UI gate has no host-side override.
    defaultValue: false,
    public: true,
  },
  {
    key: 'direct-chat-per-user',
    description:
      'Scope a STANDARD agent\'s Direct Chat to the user who had the conversation (ENG-8712). ' +
      'Today two teammates who open the same agent land in the same thread and read each ' +
      'other\'s messages: the /recent and per-session reads carry no user predicate unless ' +
      'the agent is the per-org system_support concierge. When on, both reads return only ' +
      'sessions the requester authored in PLUS sessions no user has authored in — the second ' +
      'arm is load-bearing, since every server-originated row (kanban and scheduled-task ' +
      'nudges, integration failures, HITL approvals, degradation alerts) is written as ' +
      'role=user with no user_id and would otherwise vanish from every console (ENG-8431). ' +
      'Does NOT by itself make the thread private in the live direction: assistant replies ' +
      'fan out on the per-TEAM realtime topic direct-chat-agent:{agentId}, so a teammate ' +
      'holding the drawer open still receives them. That half is direct-chat-live-per-user ' +
      '(ENG-8760), which is a SEPARATE flag on purpose — turn both on for a private thread.',
    flagType: 'boolean',
    // Declared safe value is `false` — today's team-wide thread. Server-side only
    // (the two reads in routes/agents.ts); the browser needs no knowledge of it,
    // so NOT public. No envVar either: this is an org/team-scoped rollout gate
    // with no host-side consumer, and a declared envVar pinned in sst.config.ts
    // would make every admin-UI override unreachable (ENG-8303).
    //
    // A rollout device, not an undo. Flipping it back off restores the wider read,
    // but any message written while it was on was still written — and the legacy
    // backfill in migration 20260812000006 is not reversed by the flag.
    defaultValue: false,
  },
  {
    key: 'direct-chat-live-per-user',
    description:
      'The LIVE half of per-user Direct Chat (ENG-8760). direct-chat-per-user scoped the two ' +
      'REST reads; assistant replies kept fanning out on the per-TEAM realtime topic ' +
      'direct-chat-agent:{agentId}, whose RLS (20260804000003) has no session and no user ' +
      'predicate — so a teammate holding the drawer open still received another member\'s ' +
      'replies live, and only a reload hid them. When on, a reply in a session a human has ' +
      'authored in is broadcast to direct-chat-user:{agentId}:{userId} instead, whose policy ' +
      '(20260812000009) pins the third segment to auth.uid(). Sessions NO human has authored ' +
      'in — the agent\'s inbound work queue: kanban and scheduled-task nudges, integration ' +
      'failures, HITL approvals, degradation alerts — keep going to the per-TEAM topic, ' +
      'matching arm 2 of the REST read, or this would repeat ENG-8431 in the live lane. ' +
      'Also admits the system_support concierge to the per-user topic, which the per-TEAM ' +
      'topic denies outright (its live delivery is additive here, never narrowed).',
    flagType: 'boolean',
    // Declared safe value is `false` — today's per-TEAM fan-out, byte for byte,
    // for every agent kind including the concierge. Server-side only: the browser
    // subscribes to BOTH topics unconditionally and needs no knowledge of the
    // flag, so NOT public — which is also what keeps client and server from
    // disagreeing about it. No envVar: an org/team-scoped rollout gate with no
    // host-side consumer, and a declared envVar pinned in sst.config.ts would
    // make every admin-UI override unreachable (ENG-8303).
    //
    // DELIBERATELY INDEPENDENT of direct-chat-per-user. The two are separable in
    // both directions and neither ordering is unsafe: this one alone narrows live
    // delivery below what the REST read still serves (a teammate can load the
    // thread but stops receiving it live), and that one alone is the state
    // ENG-8712 shipped. Coupling them into one switch would have removed the
    // ability to soak the live change on an org that is already comfortable with
    // the read change.
    defaultValue: false,
  },
  {
    key: 'onboarding-auto-deploy',
    description:
      'Auto-deploy the first agent during onboarding (ENG-7378): the agent edit page\'s ' +
      'Deploy & Test tab fires the existing deploy (draft -> active) automatically once the ' +
      'agent\'s host is fully deployed (active + manager heartbeating, not provisioning) AND ' +
      'Claude-authenticated (claude_auth_status=valid), replacing the manual "Deploy Agent" ' +
      'click in the recruit funnel. Only draft, non-system_support agents; a failed or ' +
      'timed-out attempt falls back to the manual button (no auto-retry loop).',
    flagType: 'boolean',
    // PUBLIC (ADR-0022 §2): a browser-read UI-behaviour gate (mirrors
    // direct-chat-drawer). Declared safe value is `false`: auto-deploy flips an
    // agent's status by itself, so a flag-read error must never switch it on.
    // Ships dark; enable per-org (or fleet-wide) from the admin Feature Flags
    // page. When off, the recruit funnel keeps the manual Deploy button. No
    // envVar: a browser-only gate has no host-side override.
    defaultValue: false,
    public: true,
  },
  {
    key: 'manager-failure-notify',
    description:
      'Route terminal integration-failure notifications to the agent\'s human manager ' +
      '(reports_to → preferred channel) instead of only the agent\'s own direct-chat ' +
      '(ENG-6334). Boolean gate; ships dark.',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AGT_MANAGER_FAILURE_NOTIFY_ENABLED',
  },
  {
    key: 'agent-request-reconnect',
    description:
      'Agent-facing `request_reconnect` MCP tool (ENG-7495). When ON, POST /host/request-reconnect ' +
      'accepts an agent\'s report that one of its integrations is failing auth, validates it against ' +
      'the shared needsReconnect predicate, and notifies the agent\'s human manager with a reconnect ' +
      'deep link (preferred channel; falls back to the team alert channel, then owner/admin email). ' +
      'OFF returns a soft "not enabled" result to the agent. Boolean gate; ships dark.',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AGT_AGENT_REQUEST_RECONNECT_ENABLED',
  },
  {
    key: 'model-api-error-reporting',
    description:
      'Manager reports model-API failures (529 overloaded, 429 rate-limit, 5xx, timeouts) ' +
      'from spawned-agent output and its own eval/memory backend calls to the AGT API, which ' +
      'persists them and opens a threshold alert for early warning of provider incidents ' +
      '(ENG-7363). Boolean gate; ships dark. Materialized to the host flags-cache.',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AGT_MODEL_API_ERROR_REPORTING_ENABLED',
  },
  {
    key: 'model-policy-failover-trigger',
    description:
      'Automatic model-policy failover (ENG-10156, ADR-0074). off = the trigger does not run. ' +
      'shadow = it evaluates every ingested model-API error against the policy\'s failover_trigger ' +
      'config and LOGS the decision with its inputs, acting on nothing. Enum gate; ships OFF.',
    flagType: 'enum',
    // `armed` is DELIBERATELY not an allowed value yet.
    //
    // Nothing writes model_policy_failover_state with source='auto' — that is
    // the actuation half, and it is gated behind a recorded shadow soak
    // (ENG-10156 AC8). Offering `armed` now would be a setting an operator can
    // select, that reports success, and that does nothing: the failure mode is
    // worse than the missing feature, because it looks like a fleet running
    // with automatic failover enabled. Add it in the same change that makes it
    // act, not before.
    allowedValues: ['off', 'shadow'],
    // OFF, not `shadow`, and the contrast with `channel-quarantine-mode` is the
    // reason: that flag defaults to `shadow` because shadow WAS the live
    // manager's compiled behaviour, so anything else would have changed the
    // fleet. Here there is no incumbent behaviour to preserve — nothing has ever
    // evaluated this — so `off` is the honest default and turning shadow on is a
    // deliberate act with a soak attached to it.
    defaultValue: 'off',
    envVar: 'AGT_MODEL_POLICY_FAILOVER_TRIGGER_MODE',
    // Marked sensitive ahead of `armed` existing: the value this flag will
    // eventually carry moves a host onto a secondary binding whose credential
    // may be ours (credential_owner: 'platform'), i.e. it spends money with no
    // human in the loop. Declaring that now costs nothing and means the audited
    // -flip behaviour is already in place on the day the value is added.
    sensitive: true,
  },
  {
    key: 'claude-md-skills-index',
    description:
      'Manager injects the "## Available Skills" bullet list (one line per installed skill, ' +
      'name + frontmatter description) into the agent\'s project CLAUDE.md. Claude Code already ' +
      'surfaces installed skills to the model natively from .claude/skills/*/SKILL.md, so on ' +
      'current models the list is duplicated context - and an expensive one: it measured 12,045 ' +
      'chars across 29 skills on a prod agent, pushing project/CLAUDE.md to 51k against Claude ' +
      "Code's 40,000-char ceiling, past which the tail of the agent's own system prompt is " +
      'silently truncated. OFF suppresses ONLY the skill bullets; the "Updating Integrations" ' +
      'guidance in the same managed block is real instruction and is always kept. ' +
      'Defaults ON (today\'s behaviour) - flip OFF per org to reclaim the headroom.',
    flagType: 'boolean',
    defaultValue: true,
    envVar: 'AGT_CLAUDE_MD_SKILLS_INDEX_ENABLED',
  },
  {
    key: 'claude-md-integrations-section',
    description:
      'Manager injects the "## Integrations" bullet list (one line per installed integration, ' +
      'name + CLI binary + description) into the agent\'s project CLAUDE.md. Sibling of ' +
      'claude-md-skills-index and redundant for the same reason: every integration already ' +
      'reaches the model through a surface Claude Code presents natively - an ' +
      '`integration-*` skill for those that ship a skill bundle, and a dynamically-discovered ' +
      'MCP tool (`mcp__augmented__*`, fanned out from integration_definitions.metadata.tools ' +
      'via /host/mcp/tools/list) for those that do not. The list measured 2,302-3,466 chars ' +
      'per agent on agt-aws-1 (2026-07-27), against Claude Code\'s 40,000-char CLAUDE.md ' +
      'ceiling. Defaults OFF (ENG-8174), which also codifies the observed steady state: the ' +
      'section survives only ~3 minutes after each integration sync before the next ' +
      'provisioning pass strips it (ENG-8170), so the fleet has effectively been running ' +
      'without it - flip ON per org only to restore the list.',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AGT_CLAUDE_MD_INTEGRATIONS_SECTION_ENABLED',
  },
  {
    key: 'wedge-transient-notice',
    description:
      'Tell the person waiting when their turn dies on a transient LLM-API failure ' +
      '(529 overloaded / 5xx). TWO consumers now share this gate. (1) ENG-7360: a ' +
      'wedge-respawn preceded by such an error writes the ENG-6058 give-up signal tagged ' +
      'reason=transient_overload so the channel sweeps post a "please resend" notice. ' +
      '(2) ENG-8269: the channel MCPs watch the dispatched turn in Claude Code\'s own ' +
      'transcript and notify the conversation that was actually waiting — Slack, Telegram ' +
      'AND direct chat — plus a "still working on this" notice after ~3min on Slack/Telegram ' +
      'only (direct chat already shows a client-side one at 90s). NOTE: (2) fires on a MUCH ' +
      'larger population than (1) — any dispatched turn that dies, not only one that also ' +
      'wedged the session — and (1) has never actually been able to fire, because its ' +
      'pane.log detector cannot match the banner Claude Code renders today. So flipping this ' +
      'on is in practice enabling (2) for the first time. Boolean gate; ships dark — ' +
      'channel-visible copy soaks per host before going wide. Materialized into the ' +
      'channel-MCP spawn env: a Docker-isolated agent never mounts the host flags-cache, so a ' +
      'central flip reaches it only that way.',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AGT_WEDGE_TRANSIENT_NOTICE_ENABLED',
  },
  {
    key: 'manager-review-notify',
    description:
      'Deliver the agent\'s weekly performance-review check-in to its human manager ' +
      '(reports_to, chain-walked → preferred channel) via the submit_performance_review ' +
      'tool (ENG-6513). Boolean gate; ships dark.',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AGT_MANAGER_REVIEW_NOTIFY_ENABLED',
  },
  {
    key: 'human-task-assignment',
    description:
      'Allow an agent to assign a kanban task to a human teammate via the ' +
      'assign_kanban_to_human MCP tool / POST /host/kanban/assign-human (ENG-6665). ' +
      'Off = the endpoint soft-refuses, so no agent can create human-assigned cards. ' +
      'This is the per-org activation gate for the whole human-assignment feature; ' +
      'the separate human-task-assignment-notify flag controls whether the recipient ' +
      'is also notified. Boolean gate; ships dark.',
    flagType: 'boolean',
    defaultValue: false,
  },
  {
    key: 'human-task-assignment-notify',
    description:
      'Notify a human teammate, via their preferred channel (Slack DM, else email ' +
      'fallback), when an agent assigns them a kanban task (ENG-6665, ' +
      '/host/kanban/assign-human). Off = the card is created silently (it still ' +
      'appears on the recipient\'s My Tasks page). Boolean gate; ships dark.',
    flagType: 'boolean',
    defaultValue: false,
  },
  {
    key: 'resume-reconciler',
    description:
      'Health-gated safe auto-resume for circuit-breaker pauses (ENG-6383, epic ENG-6333). ' +
      'When on, the manager clears a trip only when the dependency has genuinely recovered ' +
      '(status active + MCP present + connectivity ok ≥ hysteresis), replacing the blind ' +
      'ENG-6088 quiet-timer; when off, the blind timer still governs. Boolean gate; ships dark.',
    flagType: 'boolean',
    // Declared safe value is `false` — the blind ENG-6088 timer (already bounded
    // to ≤1 auto-resume/window) stays the behaviour when the API can't be
    // reached or no flag map arrives. Flip on per-host for the canary bake.
    defaultValue: false,
    // Migration override (ADR-0022): the pre-flags env gate stays the
    // highest-precedence operator override so a host already running with
    // AGT_RESUME_RECONCILER_ENABLED set keeps that value until the env is retired.
    envVar: 'AGT_RESUME_RECONCILER_ENABLED',
    // ENG-10036: bisected from the PUBLISHED agt-cli dists, same method as its
    // sibling `auto-resume-enabled` (ENG-10034). Taken out of that ticket's
    // grandfather list rather than deferred with the other 20, because the
    // arming decision for this flag is live this week and a `since` that is
    // merely RECORDED as missing is still missing at the moment somebody reads
    // the flip-reach modal.
    //
    // AND THIS ONE IS WHY THE PROBE MATTERS. Bisecting on the module symbols
    // gives the WRONG release, and plausibly so:
    //
    //   0.28.19  2026-06-12T05:35:32Z  decideResumeReconcile ✓  readResumeReconcilerConfig ✓  AGT_RESUME_RECONCILER_ENABLED ✓  'resume-reconciler' ✗
    //   0.28.20  2026-06-12T06:17:28Z  decideResumeReconcile ✓  readResumeReconcilerConfig ✓  AGT_RESUME_RECONCILER_ENABLED ✓  'resume-reconciler' ✓
    //
    // ENG-6383 shipped the decision and the env gate; ENG-6395 promoted it to a
    // registry key 42 minutes later. A host on 0.28.19 honours the ENV VAR and
    // cannot read the FLAG, so `since: '0.28.19'` would report reach onto hosts
    // a registry flip cannot touch — the precise defect `since` exists to
    // prevent. The flag KEY is the only honest discriminator.
    since: '0.28.20',
  },
  {
    key: 'auto-resume-enabled',
    description:
      'Bounded BLIND auto-resume for circuit-breaker pauses (ENG-6088): at most one unattended ' +
      'resume per trip cycle, fired once the trip driver has been quiet for the configured window ' +
      'and only for provisioning-reload trips (the ENG-7577 class gate — a crash-class trip still ' +
      'needs an operator). Superseded wherever `resume-reconciler` is on: that path proves the ' +
      'dependency recovered instead of assuming it, and the blind timer is its flag-off fallback. ' +
      'Boolean gate; ships dark. ENG-8395 promoted this from a raw host env var because ' +
      'AGT_AUTO_RESUME_ENABLED lives in the host env file, so its grain is per-HOST — one flip arms ' +
      'every agent that host runs (agt-aws-1 carries 14), which made the canary-then-fleet rollout ' +
      'its own documentation prescribes impossible. As a registry flag it resolves per ' +
      'org/team/host/AGENT, so one agent can genuinely be cut over first. Read host-side by the ' +
      'manager (agt-cli), not centrally.',
    flagType: 'boolean',
    // Declared safe value is `false` — identical to today's behaviour on a host
    // that has never set the env var. Fail-safe direction on a flag-DB read
    // error is "no unattended resume of a paused agent".
    defaultValue: false,
    // Migration override (ADR-0022) — KEPT, not swapped (ENG-8395 is an addition).
    // The pre-flags env gate stays the highest-precedence operator override so a
    // host already running with AGT_AUTO_RESUME_ENABLED set keeps that value, and
    // so an operator retains a host-local escape hatch that needs no control-plane
    // round-trip. Note the host reader accepts a slightly WIDER truthy vocabulary
    // than coerceEnvValue (it also honours the legacy yes/on/no/off spellings);
    // both agree on true/1/false/0, and the reader is the authority for this gate.
    envVar: 'AGT_AUTO_RESUME_ENABLED',
    // An unattended resume is a real availability change — the agent starts
    // spawning sessions again with no human in the loop — so arming it is a
    // deliberate, confirmed write (ADR-0022 sensitive-flag confirm).
    sensitive: true,
    // ENG-10034: bisected from the PUBLISHED agt-cli dists, not from git history —
    // auto-resume.ts is host-side, so what matters is the first ARTIFACT a host
    // could be running that contains a consumer for this flag.
    //
    // METHOD, so it can be re-run rather than trusted: download each candidate
    // tarball from the npm registry and grep its contents for the flag-specific
    // wiring. Binary search over the 22 versions published either side of
    // ENG-8395's merge gave a clean one-release boundary, eight minutes wide:
    //
    //   0.28.506  2026-08-03T22:29:22Z  readAutoResumeEnvOverride ✗  AUTO_RESUME_FLAG_KEY ✗  'auto-resume-enabled' ✗
    //   0.28.507  2026-08-03T22:37:00Z  readAutoResumeEnvOverride ✓  AUTO_RESUME_FLAG_KEY ✓  'auto-resume-enabled' ✓
    //
    // All three symbols are checked rather than one. `getBooleanForAgent` alone
    // would have been the wrong probe — it is present in BOTH builds, because the
    // generic resolver long predates this flag. A `since` bisected on a symbol the
    // flag does not depend on is a number that looks rigorous and is not.
    //
    // ENG-8395 AC5 left this unset on purpose and said why: an unset `since` makes
    // the flip-reach estimate report FULL reach for a host-side reader, promising
    // reach it does not have (the defect caught on ENG-8344 / PR #4014). The
    // follow-up it was waiting on was never filed, so the flag sat un-armable for
    // a month and ENG-6088's bounded auto-resume stayed dark fleet-wide — a
    // customer agent sat breaker-paused 2h08m on 2026-09-05 with the recovery path
    // silently disabled. That is what an administrative blocker recorded only in a
    // code comment costs.
    //
    // Same method as its precedents: slack-hot-thread-guard = 0.28.339,
    // slack-scheduled-channel-guard = 0.28.421 (ENG-8149). Setting it now is safe:
    // `since` is excluded from projectDefinition, so it does not roll
    // FLAGS_SCHEMA_VERSION.
    //
    // This does NOT arm the flag. defaultValue stays false and `sensitive` stays
    // true; arming is a separate, deliberate decision that belongs to ENG-6066.
    since: '0.28.507',
  },
  {
    key: 'host-poll-rate-alarm',
    description:
      'Arms the ENG-9637 per-agent poll-rate alert. The HostPollRateMonitor cron counts ' +
      '/host/refresh requests per agent from CloudWatch Logs Insights each hour and opens an ' +
      'agent_poll_rate_runaway alert when an agent is far above its OWN trailing baseline, or far ' +
      'above the fleet median. OBSERVE-FIRST rollout gate, on the agt-version-drift-alarm ' +
      'precedent: the cron measures, PERSISTS and LOGS every agent on every run regardless of this ' +
      'flag, so the real fleet spread is measurable before anyone is paged. Off = measure, persist ' +
      'and log, open nothing. It gates OPENING only - the recovery pass runs regardless, because ' +
      'arming decides whether we may assert a condition and has no bearing on whether an assertion ' +
      'already made is still true; gating the close would make disarming this flag freeze every open ' +
      'alert permanently. Boolean gate; ships dark. Evaluated API-side, in the cron.',
    flagType: 'boolean',
    // Declared safe value is `false`, and the reason is specific rather than
    // habitual. The self-baseline rule needs several hours of persisted history
    // per agent before it can say anything, so arming this on the same deploy
    // that creates the table would run the fleet rule alone against a fleet
    // that is still mid-rollout of the ENG-9637 poll ceiling - i.e. against the
    // widest poll-rate spread the fleet will ever have. That is a page for
    // every host still carrying the old CLI.
    defaultValue: false,
    // Arming it makes a previously-invisible condition visible all at once,
    // which is the ADR-0022 sensitive-flag shape.
    sensitive: true,
  },
  {
    key: 'host-alarm-reconciler-repair',
    description:
      'Arms the CREATE and DRIFT-REPAIR halves of the ENG-8325 host-alarm reconciler. The cron ' +
      'computes and LOGS the full diff for every host on every run regardless of this flag, on the ' +
      'host-poll-rate-alarm precedent, so the fleet diff is measurable before anything mutates. ' +
      'Off = measure and log, create nothing and re-put nothing. Evaluated PER ORGANIZATION from ' +
      'the host row, so arming can be staged one org at a time and watched. It does NOT gate the ' +
      'reap - see host-alarm-reconciler-reap, which is deliberately fleet-wide because an orphan ' +
      'alarm whose host row is gone has no organization to scope by. ' +
      'AGT_HOST_ALARM_RECONCILER_APPLY outranks this flag and forces BOTH gates on fleet-wide; it ' +
      'is deliberately not declared as this flag\'s envVar, because it is a master override of two ' +
      'flags rather than the operator override of this one. Boolean gate; ships dark. ' +
      'Evaluated API-side, in the cron.',
    flagType: 'boolean',
    // Safe value is `false` for a specific reason, not a habitual one: the
    // first applying run creates one alarm per host that lacks one, and a
    // PutMetricAlarm on a fleet whose expected set is wrong is not a no-op you
    // can shrug off - it is monitoring that now exists and will page.
    defaultValue: false,
    // It mutates CloudWatch for every host in the armed org, which is the
    // ADR-0022 sensitive-flag shape.
    sensitive: true,
  },
  {
    key: 'host-alarm-reconciler-reap',
    description:
      'Arms the REAP half of the ENG-8325 host-alarm reconciler: deleting alarms under ' +
      'augmented/{env}/host/ whose parsed host id is not in the live set. Off = count and name the ' +
      'orphan candidates, delete nothing. ' +
      'DELIBERATELY EVALUATED GLOBALLY, with no organization scope, and that is not an oversight ' +
      'to be tidied up later. An orphan is defined as an alarm whose host id resolves to no live ' +
      'host; where the host row is gone entirely there is no organization to scope by, so the ' +
      'property that makes an alarm an orphan is the same one that makes it unattributable. A ' +
      'per-org gate here would silently reap other orgs\' orphans the moment any one org was ' +
      'armed, which is staged creates with an unstaged reap - the more dangerous half. ' +
      'AGT_HOST_ALARM_RECONCILER_APPLY outranks this flag and forces both gates on. ' +
      'Boolean gate; ships dark. Evaluated API-side, in the cron.',
    flagType: 'boolean',
    // The destructive half. Measured on prod 2026-09-03: a first applying run
    // deletes 84 alarms across 33 dead instance ids. Nothing about that is
    // recoverable by re-running, so the safe value is not a formality.
    defaultValue: false,
    sensitive: true,
  },
  {
    key: 'agt-version-drift-alarm',
    description:
      'Arms the ENG-8341 agt CLI version-drift alert. When a host has fallen behind its EFFECTIVE ' +
      'target (env pin > DB pin > release channel) across two or more of its OWN maintenance ' +
      'windows, the monitor opens a host_agt_version_drift alert. OBSERVE-FIRST rollout gate: the ' +
      'cron computes and LOGS the drift for every host on every run regardless of this flag, so the ' +
      'real fleet spread is measurable before anyone is paged - the ticket asks for the threshold to ' +
      'be set from observed data rather than a guess. Off = compute and log, open nothing. ' +
      'Boolean gate; ships dark. Evaluated API-side, in the cron.',
    flagType: 'boolean',
    // Declared safe value is `false`. Fail-safe direction for a NEW alerting
    // cron is silence: ~21 hosts with no prior drift signal means the first
    // armed pass could open an alert for every host that is behind at once.
    // The cron also carries a bootstrap floor (agent-stall-monitor.ts's
    // precedent) so arming it does not retroactively page for drift that
    // started before the monitor existed.
    defaultValue: false,
    // Arming this makes a previously-invisible backlog visible all at once, in
    // the same shape ENG-8204's never-spawned-session-metric warned about - so
    // the flip is a deliberate, confirmed one (ADR-0022 sensitive-flag confirm).
    sensitive: true,
  },
  {
    key: 'org-email-domain-derivation',
    description:
      "Auto-derive an organization's own email domain from its members' signup " +
      'addresses (ENG-9888) and add it to organization_email_domains already ' +
      "verified, so email.domain_restrict's internal_only mode has the allow set " +
      'it resolves against. Without a verified domain internal_only is a runtime ' +
      'no-op (an empty allow set would block every send), which was the live state ' +
      'for 22 of 23 orgs while the console showed the strictest egress mode. ' +
      'Server-side only — the API derives lazily when the guard finds no verified ' +
      'domain; there is no host-side reader, hence no envVar. Flipping this ON is ' +
      'the backfill: derivation runs per-org on first need rather than as a batch ' +
      'job, and the vendor-domain exclusion is what stops the six orgs whose ' +
      'earliest member is a staff account from being given integritylabs.dev.',
    flagType: 'boolean',
    // Ships dark. The safe value is `false` (derive nothing) because arming this
    // gives internal_only a real allow set, which CHANGES what the guard evaluates
    // for every org it touches — and not only the guard: getVerifiedOrgEmailDomains
    // also feeds the Linear customer-need linker and the support/host-runtime
    // prompts, so a row appearing is visible beyond the send path. A flag-read
    // error must never start writing verified domains on its own.
    defaultValue: false,
    // Arming it makes a previously-inert egress control start evaluating, so the
    // flip wants a deliberate confirm rather than a casual toggle. Before flipping,
    // check which orgs sit at the `enforce` / `require_approval` email-guard stage:
    // those are the ones where a newly-effective policy can reach a real block.
    //
    // Run that pre-flight per `docs/runbooks/org-email-domain-derivation-rollout.md`,
    // and read it before acting on the result: the dry run reports `would-derive`
    // for orgs where arming is provably inert (an unverified row for the candidate
    // domain makes the insert collide and write nothing), so the report reads as
    // riskier than it is and invites per-org overrides that are not needed.
    sensitive: true,
  },
  {
    key: 'memory-extraction',
    description:
      'Host-side durable-memory extraction (ENG-6200): the manager extracts ' +
      'candidate memories from completed conversations and POSTs them to ' +
      '/host/memories/candidates, which writes dream_log via promoteCandidates. ' +
      'Post persistent-session cutover this is the ONLY live consolidation path. ' +
      'Boolean gate; ships dark — when off the manager runs no extraction, so 0 ' +
      'dream_log rows fleet-wide is the expected steady state until it is armed.',
    flagType: 'boolean',
    // Declared safe value is `false` (no extraction). Fail-safe direction: a
    // flag-DB read error must never start sending candidate memories across the
    // host→control-plane boundary on its own. Flip on per-host for the canary,
    // then fleet-wide from the admin Feature Flags page.
    defaultValue: false,
    // Migration override (ADR-0022): the pre-flags env gate stays the
    // highest-precedence operator override so a host already running with
    // AGT_MEMORY_EXTRACTION_ENABLED set keeps that value until the env is retired.
    envVar: 'AGT_MEMORY_EXTRACTION_ENABLED',
  },
  {
    key: 'tool-call-audit',
    description:
      'Host-side tool-call audit extraction (ENG-8575, slice 3 of ENG-8427): the ' +
      'manager scans agent transcripts for tool calls, redacts each to identity-' +
      'without-content, and POSTs them to /host/tool-calls. Boolean gate; when ' +
      'off the manager reads no transcripts and sends nothing. ARMED STAGE-WIDE ' +
      'SINCE 2026-08-17 — the compiled default below is what a host resolves when ' +
      'nothing else says otherwise, NOT a description of the fleet. This text once ' +
      'asserted the opposite and outlived its truth by nine days, which is what got ' +
      'ENG-9472 filed to arm an already-armed flag. Query the table, do not infer it ' +
      'from this line. NOT the commercial gate: /host/tool-calls independently re-checks ' +
      'the advanced_governance entitlement and refuses regardless of this flag, ' +
      'because the host is customer-owned infrastructure. This flag is rollout ' +
      'control only.',
    flagType: 'boolean',
    // Declared safe value is `false` (no extraction). Fail-safe direction: a
    // flag-DB read error must never start shipping tool-call metadata across the
    // host→control-plane boundary on its own. Same posture as memory-extraction
    // above, and for the same reason — both read agent transcripts.
    defaultValue: false,
    // ADR-0022 operator override. Read by the host flag store's env layer, not
    // by a hand-written process.env gate — so check-new-env-gates.sh stays quiet
    // and the escape hatch still exists for a per-host canary.
    envVar: 'AGT_TOOL_CALL_AUDIT_ENABLED',
    // The real published version, replacing the deliberately-unreachable
    // placeholder this flag shipped with. `since` is advisory: it is read only
    // by computeFlagReach behind GET /flags/admin/reach?key=…, so it changes what an
    // operator is TOLD and does not itself stop a flip. Omitting it is NOT the
    // safe default — it resolves to `honors`, reporting every host as honouring
    // the flip, including hosts whose agt-cli has no reader at all.
    //
    // WHAT THIS VERSION HAS TO MEAN. Not "the first build with a reader" — the
    // first build where flipping the flag does what the flag is documented to
    // do. Those diverged twice already, in the same direction:
    //
    //   0.28.566 (ENG-8575) — the first build with a reader. Pinned here and
    //     correct on the day. It has the unbounded scan window and the coverage
    //     rows that overstate (both ENG-8740), and no `logging_mode` enforcement
    //     at all (ENG-8726) — so a hash-only agent on it emits plaintext
    //     `argv0` / `url_host` / `mcp_tool`, which is precisely the governance
    //     breach this epic exists to prevent.
    //   0.28.569 (ENG-8740) — bounded window, honest coverage intervals. Still
    //     no hash-only enforcement. The pin was NOT moved at that merge and went
    //     stale for a day; reach reported those hosts `honors`.
    //   0.28.570 (ENG-8726) — hash-only honoured at runtime. Current pin.
    //
    // MOVE THIS PIN whenever a change alters what arming the flag DOES on a
    // host, and verify it against the published tarball rather than inferring it
    // from the merge — `npm pack @integrity-labs/agt-cli@<v>` and grep
    // package/dist/lib/manager-worker.js (the manager is the actual reader; the
    // dist/mcp/* occurrences are bundling artifacts). For 0.28.570 the markers
    // are `readAgentLoggingMode` and `loggingModeWithholdsTargets`. Auto-publish
    // patch-bumps from whatever is on main, so if another CLI-touching PR lands
    // first the real cut is higher than this and the pin under-reports.
    since: '0.28.570',
  },
  // ENG-9348 RETIRED (2026-08-24): the `secret-files-tmpfs` flag and its whole
  // tmpfs-backing implementation are removed after TWO prod outages. Symlinking
  // `.env.integrations` into /dev/shm raced the agent wrapper's `set -e; source
  // .env.integrations` (a symlink observed before its target existed → crashloop);
  // the seed-before-symlink fix (PR #4960) proved insufficient (a flag-read flap
  // let the destructive disable-revert re-open the window). Approach abandoned in
  // favour of the spawn-env path (ENG-9350, which never writes the file at all)
  // plus disk shred (ENG-9349). Removing the flag makes a third outage impossible.
  // Any lingering feature_flag_overrides rows for this key are inert (no reader);
  // clear them out-of-band if desired.
  // flag-archive-allow: secret-files-tmpfs retired after two prod outages — feature removed, no manager reader remains
  {
    key: 'memory-file-tombstones',
    description:
      'Host-side removal of RETIRED agent memory files (ENG-9257). The control plane sends ' +
      'an explicit tombstone list on /host/memories and the manager moves the matching .md ' +
      'out of the agent\'s memory directory — but only when its write-time manifest says the ' +
      'manager itself wrote that exact file and the sha256 still matches, so an agent-edited ' +
      'or hand-authored file is retained and reported instead. Boolean gate; ships dark. ' +
      'Scope is retirement only, never expiry: /host/memories has always withheld expired ' +
      'rows, so an expiry-driven list would make months of accumulated backlog deletable on ' +
      'the first armed tick. This is the ONLY thing that makes retirement visible to the ' +
      'agent, which reads its memory directory straight off disk — every server-side reader ' +
      'already excludes retired rows, so with this off a retired memory is gone everywhere ' +
      'except the one surface that changes the agent\'s behaviour.',
    flagType: 'boolean',
    // Declared safe value is `false` (remove nothing). Fail-safe direction: a
    // flag-DB read error must never start deleting files on customer-owned
    // hosts on its own. Note the asymmetry with a read-only gate — the blast
    // radius here is data on a machine we frequently cannot re-enter, which is
    // also why the first release MOVES files rather than unlinking them.
    defaultValue: false,
    // ADR-0022 §4: mutating this requires an explicit caller confirmation.
    //
    // Arming it DELETES FILES on customer-owned hosts. The registry already
    // marks `id-keyed-layout-migration` sensitive on the narrower basis that it
    // moves data, and `admin-send-keys` because it writes to a customer host;
    // this is the stronger case of both at once. Without it the flip is a single
    // unguarded write, which is the wrong ceremony for an irreversible action on
    // a machine we frequently cannot re-enter — the same fact the defaultValue
    // comment above already leans on.
    sensitive: true,
    // ADR-0022 operator override, read by the host flag store's env layer.
    //
    // ROLLOUT WARNING: an env override needs a manager RESTART to set or clear,
    // and masks the central flag while set (ENG-7409). So a per-host canary
    // pinned via env becomes a host the central kill switch cannot reach during
    // an incident. Canary centrally (per-agent overrides are delivered on the
    // heartbeat for every boolean flag) rather than with this.
    //
    // Note `sensitive` does NOT gate this env var — it guards the control-plane
    // write path, not a host's local environment file. One more reason not to
    // canary with it.
    envVar: 'AGT_MEMORY_TOMBSTONES_ENABLED',
    // Pinned to the first PUBLISHED agt-cli whose manager carries a WORKING
    // reader, verified against the tarball rather than inferred from the merge.
    // ENG-9229 + ENG-9257 merged as 91a67cb08, but auto-publish patch-bumps
    // from whatever is on main, so the merge does not name a version: a second
    // CLI-touching merge landed eight minutes later, and `latest` was already
    // past our cut by the time anyone looked. What settles it is
    // `npm pack @integrity-labs/agt-cli@<v>` + grep of
    // package/dist/lib/manager-worker.js (the manager is the actual reader; the
    // dist/mcp/* occurrences are bundling artifacts). 0.28.666 carries NONE of
    // `_memory-retirement`, `memory-file-tombstones`, `applyMemoryTombstones`,
    // `memory-retirement-report`; 0.28.667 carries all four.
    //
    // ALL FOUR, because this has to name the first build where flipping the
    // flag does what the flag SAYS, not merely the first build with a reader:
    // the write-time manifest gate, the tombstone pass sitting above the
    // download short-circuit, the response hash covering the tombstone list,
    // and the report-back. Ship a build missing any of those and reach reports
    // `honors` for hosts that silently never act. Here they shipped in one
    // merge so they could not be split across versions - that is a property of
    // this cut, not a guarantee about the next one.
    //
    // MOVE THIS PIN whenever a change alters what arming the flag DOES on a
    // host, to the first version carrying the CHANGED behaviour, and verify
    // that one against the tarball too (apps/cli/package.json is not the
    // published number). See `tool-call-audit` above, where the pin was NOT
    // moved at such a merge and went stale for a day.
    //
    // WHY THIS FIELD IS NEVER OMITTED, whatever it is set to. classifyHostReach
    // (packages/core/src/feature-flags/reach.ts) maps an empty `since` to
    // `honors` for EVERY host including ones with no reader at all, and maps an
    // unparseable one to `unknown` - which collides with the "host reports no
    // agt_version" class, so an operator cannot tell "not shipped yet" from
    // "these hosts are unreadable". Both outlive this particular version.
    since: '0.28.667',
  },
  {
    // ADR-0110 Decision 5 (ENG-10854). Gates PLATFORM-INTERNAL consumers of the
    // typed evaluation capability - inbound triage, kanban classification,
    // memory-extraction sensitivity gating, and later the conversation
    // evaluator. Nobody "installs" those; they are ours, so they are gated
    // centrally.
    //
    // IT DOES NOT GATE THE AGENT TOOL, and that separation is the point. The
    // agent tool's gate is the customer's own premium opt-in plus a per-agent
    // install (Decision 4). Pointing this flag at the tool as well would give
    // it two masters and let a staff flag silently override a decision the
    // customer made about their own data reaching a sub-processor.
    //
    // Ships with no readers: the consumers land in later slices, and the flag
    // exists now so the first of them has a gate to be born behind rather than
    // acquiring one afterwards.
    key: 'typed-evaluation',
    description:
      'ADR-0110: platform-internal consumers of the typed evaluation model ' +
      '(Jev, via our AI Gateway). OFF (default) = no internal consumer calls it; ' +
      'ON = consumers that have shipped may call, each still subject to the ' +
      'per-org quota, per-agent rate limit and audit row enforced in the broker. ' +
      'Does NOT gate the agent-facing tool, which is governed by the premium ' +
      'opt-in and a per-agent install instead. Customer orgs stay off until the ' +
      'Decision 9 vendor gates close (sub-processor listing with DPA coverage, ' +
      'the legal read on emotion inference, and a recorded disclosure).',
    flagType: 'boolean',
    // Ships dark. Every consumer degrades to its current behaviour when off,
    // so the safe value and the no-op value are the same one.
    defaultValue: false,
    // Turning it on starts sending customer text to an external sub-processor
    // on paths the customer did not individually opt into, which is exactly the
    // class ADR-0022 section 4 reserves the confirmation for.
    sensitive: true,
  },
  {
    key: 'conversation-eval-backend',
    description:
      'Backend for host-side conversation-success scoring AND memory extraction (ENG-6581), ' +
      'which share one scorer: anthropic-api = Haiku via a direct Anthropic Messages API ' +
      'fetch (no subprocess); claude-p = Haiku via the `claude -p` subprocess (reuses the ' +
      'agent auth, effectively free under Max but a heavy spawn); local = an ' +
      'OpenAI-compatible loopback endpoint (transcript never leaves the host). Auth for the ' +
      'anthropic-api path comes from AGT_CONV_EVAL_ANTHROPIC_API_KEY (falls back to ' +
      'ANTHROPIC_API_KEY); with no key that path fails closed (eval disabled) rather than ' +
      'reverting to claude-p. Enum.',
    flagType: 'enum',
    allowedValues: ['anthropic-api', 'claude-p', 'local'],
    // Declared safe value is `claude-p` - it matches the manager's compiled
    // default before this flag existed, so migrating the host reader onto the
    // flag (ADR-0022) preserves fleet behaviour rather than silently moving eval
    // onto the metered direct API (which would also fail closed on any host that
    // lacks an Anthropic API key). The flip to `anthropic-api` is a deliberate,
    // staged cost decision from the admin Feature Flags page (claude-p is
    // deprecating under Max, ENG-5576).
    defaultValue: 'claude-p',
    // Migration override (ADR-0022): the pre-flags env var stays the
    // highest-precedence operator override so a host already setting
    // AGT_CONV_EVAL_BACKEND keeps that value until the env var is retired.
    envVar: 'AGT_CONV_EVAL_BACKEND',
  },
  {
    key: 'synthetic-probe-enabled',
    description:
      'Global gate for the agent synthetic-liveness cron (ENG-8074). ' +
      'When OFF, the cron exits after reading the flag — no probes sent, no metrics emitted, ' +
      'no CloudWatch alarms fired, near-zero DB footprint. This is the fleet-wide kill switch ' +
      'for synthetic probing, flippable per-environment from the admin Feature Flags page. ' +
      'The AUGMENTED_SYNTHETIC_PROBE_ENABLED env var is the declared envVar for this flag ' +
      '(ADR-0022): set it in the deploy environment to "true"/"1" to force-enable or ' +
      '"false"/"0" to force-disable without a DB lookup. Leave it UNSET (the default on every ' +
      'stage since ENG-8303) so this flag governs. ' +
      'Evaluated as a global stage value by the central cron (no per-org targeting). Boolean gate.',
    flagType: 'boolean',
    // ON by default — this deliberately matches the fleet's live behaviour rather than the
    // dark default ENG-8074 declared, and the history is worth keeping straight:
    //
    // ENG-8074 set `false` to hold the probe dark until its reliability issues were fixed
    // (probe_timeout false positives, ENG-8048; open cron_invocation_errors alarms). That
    // default was never reachable: sst.config.ts pinned the declared envVar to the literal
    // 'true' on every deployed stage, and a declared envVar outranks the DB value, so the
    // probe ran throughout (ENG-8303). ENG-8303 un-pinned the env var, which hands this flag
    // real authority for the first time — so its value must now be chosen for the fleet it
    // actually controls, not inherited from an intent that never took effect.
    //
    // `true` keeps prod exactly as it has been running. Flipping to `false` here instead
    // would have silently switched agent liveness probing off at the next deploy — including
    // the prod verification of ENG-8302's PutMetricAlarm quota fix, which needs the cron
    // alive to observe. The reliability concern behind ENG-8074 is addressed by ENG-8302;
    // if it recurs, the kill switch is now a genuine admin-UI flip rather than a redeploy.
    defaultValue: true,
    envVar: 'AUGMENTED_SYNTHETIC_PROBE_ENABLED',
  },
  {
    key: 'synthetic-probe-on-metered-hosts',
    description:
      'Synthetic liveness probing of OpenRouter-metered agents (ENG-7235). On a host whose ' +
      "claude_auth_mode='openrouter' every model call is per-token cost, so the hourly synthetic " +
      'probe (a full inbound→session→model→outbound round-trip whose only output is a probe_ack) ' +
      'is real spend for zero deliverable - unlike a Max-subscription host where it is effectively ' +
      'free. When OFF (default) the synthetic-probe cron skips OpenRouter-mode agents entirely; ' +
      'their liveness falls back to the zero-token signals (pane-activity ENG-5399 + manager ' +
      'heartbeat + inbound-loop liveness ENG-5614). When ON, metered agents are probed like every ' +
      'other agent (the pre-ENG-7235 behaviour) - the explicit opt-in for an operator who wants ' +
      'the end-to-end check and accepts the cost. Subscription / api_key hosts are unaffected ' +
      'either way. Evaluated as a global stage value by the central cron (no per-org targeting in ' +
      'v1). Boolean gate.',
    flagType: 'boolean',
    // Declared safe value is `false` = do NOT probe metered agents. This both
    // encodes the ENG-7235 intent (no per-token cost for a no-deliverable probe)
    // and is the fail-safe direction for the cron's flag read: a flag-DB error
    // degrades to the cost-avoiding default rather than silently re-arming paid
    // probes fleet-wide. No envVar - net-new control with no pre-flags env gate
    // to migrate (ADR-0022).
    defaultValue: false,
  },
  {
    key: 'kanban-nudge-before-reap',
    description:
      'Ask a quiet agent before reaping its card (ENG-8906). The kanban stale-reaper decides an ' +
      "in_progress card is dead from silence on a clock, and silence is identical for a BUSY " +
      'agent and a WEDGED one - ENG-8669 was parked as stalled while its agent was healthily ' +
      'blocked on CI polls and a 15-20 minute SST deploy. When ON, a card that passes the quiet ' +
      'threshold gets up to MAX_KANBAN_NUDGES (2) targeted direct-chat nudges over the existing ' +
      'ENG-4892 probe rail before any verdict is taken; the card is only reaped if it is still ' +
      'not renewed. Renewal must come from a kanban_progress call that moves lease_expires_at - ' +
      'a chat reply proves the session is responsive, not that the agent still holds the card, ' +
      'so answering in chat alone does NOT save it. Metered / OpenRouter agents are never nudged ' +
      'unless synthetic-probe-on-metered-hosts is also ON (ENG-7235: a nudge there is real ' +
      'customer spend), and keep the passive clock path. When OFF (default) the sweep is ' +
      'byte-identical to its pre-ENG-8906 behaviour. Evaluated as a global stage value by the ' +
      'kanban-stale-item-reaper cron. Boolean gate; ships dark.',
    flagType: 'boolean',
    // Declared safe value is `false` = do not nudge. Two independent reasons,
    // and both point the same way:
    //
    //   COST - a nudge is a model round-trip. It is a cheap one (AC1 measured
    //   a mid-turn nudge at ~43-46K input-equivalent tokens against ~294-469K
    //   to wake an idle agent, 6.8x-10.2x cheaper), but "cheap" is not "free"
    //   and a flag-DB error must not silently arm fleet-wide message volume.
    //
    //   CORRECTNESS - `nudge` is a third StaleReapAction, and any consumer that
    //   has not been taught about it branches `action === 'auto_return' ? todo
    //   : needs_attention` and would DEAD-LETTER the card. Off is the state in
    //   which no un-taught caller can be handed the new action.
    //
    // So the cron's flag read fails closed: an error degrades to today's
    // behaviour rather than to a live experiment. No envVar - net-new control
    // with no pre-flags env gate to migrate (ADR-0022).
    defaultValue: false,
  },
  {
    key: 'skill-dreaming',
    description:
      'Memory-driven skill improvement (ENG-6500): the nightly dreaming cron analyzes each ' +
      "agent's promoted agent_memories against its in-scope skills (agent/team/org/global) and " +
      'auto-drafts concrete skill improvements as status=draft skill_definitions. Drafts carry ' +
      'their evidence memories + rationale + confidence as provenance and flow through the ' +
      'existing scan → Pending-Skills review → publish funnel — never auto-published. Boolean ' +
      'gate; ships dark — when off the cron drafts nothing, so 0 skill-dreaming drafts is the ' +
      'expected steady state until an org is armed from the admin Feature Flags page.',
    flagType: 'boolean',
    // Declared safe value is `false` (no drafting). Fail-safe direction: a
    // flag-DB read error must never start writing draft skills on its own. No
    // envVar — this is a net-new control with no pre-flags env gate to migrate.
    defaultValue: false,
  },
  {
    key: 'skill-dreaming-support-intake',
    description:
      "Route skill dreaming's GLOBAL-scope proposals into Integrity Labs' own support-intake " +
      'Linear workspace (ENG-9926). 42% of dreaming output targets a global skill no product ' +
      'surface can change, so those drafts can only ever become an agent-scoped fork that never ' +
      'reaches the thing it improves — the improvement is therefore ALSO filed as a ' +
      'support-intake issue, deduped per target skill with one Linear customer need per ' +
      'requesting org. Boolean gate; ships dark — when off nothing is filed and the fork ' +
      "behaviour is exactly today's.",
    flagType: 'boolean',
    // Arming this starts exporting customer-derived text to a third-party tracker, so it
    // takes the ADR-0022 §4 explicit-confirmation step rather than a single unguarded
    // write — matching every comparable export gate in this file
    // (`org-email-domain-derivation`, `kanban-push-status`,
    // `augmented-support-auto-provision`, `email-guard-enforce-enabled`).
    // (CodeRabbit, PR #5519: the first cut documented the risk in the comment below and
    // then omitted the marker that acts on it.)
    sensitive: true,
    // THIS GATES A DATA CROSSING, NOT A COST. Read that before arming it.
    //
    // Everything skill dreaming does today stays inside the control plane: it reads
    // `agent_memories` and writes a `skill_definitions` row in the same Postgres, same
    // org, same RLS. This flag is what sends LLM-written rationale, confidence, and the
    // verbatim NAMES of the memories that motivated a proposal OUT to Linear — and
    // `feature-request-intake.ts` already calls Linear "the third-party tracker" where it
    // refuses to export a requester's contact details there. Via
    // linkFeatureRequestCustomerNeed it also sends the org's verified email domains.
    //
    // That is strictly more than ADR-0052 §6 gated for PostHog, and ENG-8247 is the
    // precedent for why a credential must not BE the gate: holding a support-intake key
    // is not the same as being cleared to send customer-derived text to a vendor's
    // tracker. Hence a separate flag rather than leaning on isFeatureIntakeConfigured.
    //
    // Declared safe value is `false` (nothing filed). Fail-safe direction: a flag-DB read
    // error must never start exporting on its own. No envVar — net-new control with no
    // pre-flags env gate to migrate (ADR-0022).
    defaultValue: false,
  },
  // ENG-9849 (ADR-0081 decision 5): `skill-draft-review-notify` was archived
  // here. Its rail — a plain-text Slack DM nudge with no channel concept, no
  // platform discriminator and no approval lifecycle — is replaced by the
  // `skill.publish` approval verb, so skill review now has one rail rather than
  // two. Archived rather than deleted: migration 20260908000001 sets
  // `archived_at` on the prod row, which was globally `true` from 2026-06-21, so
  // the row stops resolving instead of silently continuing to evaluate against a
  // key no code reads. The reviewer resolution it owned survives in
  // `skill-review-recipients.ts` and is what the new rail falls back to when an
  // org has no `approval_routes` row.
  {
    key: 'skill-fragments',
    description:
      'Agent-contributed additive skill fragments (ENG-6811, epic ENG-6805 P2a): when on, ' +
      'an agent can propose an additive fragment for a shared (team/org) skill via the ' +
      'skill_contribute_fragment MCP tool; accepted fragments compose into the parent ' +
      "skill's delivered body in one delimited region at /host/refresh. This is the per-org " +
      'activation gate for the whole additive path - it gates BOTH the contribute tool ' +
      '(off = soft-refuse, no fragment is created) AND compose-at-refresh (off = agents ' +
      'receive the core body only), so flipping it off is a clean kill switch that reverts ' +
      'shared skills to operator-owned content. Boolean gate; ships dark.',
    flagType: 'boolean',
    // Declared safe value is `false` (no contribution, no compose). Fail-safe
    // direction: a flag-DB read error must never start composing agent-authored
    // content into a shared skill's delivered body on its own. No envVar - net-new
    // control with no pre-flags env gate to migrate (ADR-0022).
    defaultValue: false,
  },
  {
    key: 'skill-revision-proposals',
    description:
      'Agent-proposed corrective rewrites of shared skills (ENG-6824, epic ENG-6805 P2b): ' +
      'when on, an agent can propose a full-body rewrite of a shared (team/org) skill via the ' +
      'skill_propose_revision MCP tool, anchored on an immutable base_version_id; an operator ' +
      'reviews a machine-derived diff and approves (conflict-guarded apply) or rejects. This is ' +
      'the per-org activation gate for the corrective path - off = the propose tool soft-refuses, ' +
      'so no proposal is created. Distinct from skill-fragments (the additive path). Approved ' +
      'revisions land via the existing operator-owned skill body + delivery, so there is nothing ' +
      'to revert when flipped off. Boolean gate; ships dark.',
    flagType: 'boolean',
    // Declared safe value is `false` (no proposals). Fail-safe direction: a
    // flag-DB read error must never let an agent file rewrites of operator-owned
    // shared skills on its own. No envVar - net-new control (ADR-0022).
    defaultValue: false,
  },
  {
    key: 'skills-marketplace',
    description:
      'Cross-org skills marketplace: an org publishes a skill (agent proposes via ' +
      'skill_publish_to_marketplace, org owner/admin approves) so agents in OTHER orgs can ' +
      'browse the listing and import a copy into their own draft -> SkillSpector scan -> ' +
      'Pending-review -> publish funnel. Per-org activation gate for the whole feature: it ' +
      'gates the propose tool + the /marketplace publish/browse/import routes + the webapp ' +
      'marketplace UI. Off = propose/import soft-refuse and browse returns nothing, so no ' +
      'org content crosses an org boundary. Boolean gate; ships dark - flip on per org from ' +
      'the admin Feature Flags page.',
    flagType: 'boolean',
    // PUBLIC (ADR-0022 §2): the webapp reads it via GET /flags to decide whether to render
    // the Marketplace panels (mirrors projects-menu / direct-chat-drawer). Declared safe
    // value is `false`: publishing a skill to other orgs is outward-facing IP exposure, so
    // the fail-safe / absent-flag direction is "nothing is published or importable". No
    // envVar - net-new control with no pre-flags env gate to migrate.
    defaultValue: false,
    public: true,
  },
  {
    key: 'agent-offers',
    description:
      'Agent Routines and the Offers surface (ADR-0041 D4a/D8, ENG-7417): the ' +
      'capability-discovery surface where an agent earns the right to propose a Routine ' +
      '(an Offer) from observed repetition, elicitation, or its template. off = the surface ' +
      'is fully dark (no candidate intake, no Offer creation, no delivery). shadow = the ' +
      'observer runs and would-be Offers are written with a shadow marker, visible to ' +
      'platform admins only, never delivered - the D4a calibration mode that must hit the ' +
      'precision bar on real transcripts before any org goes live. on = the full surface ' +
      'for the org. Evaluated API-side only (the observed feeder additionally requires the ' +
      'memory-extraction flag, whose pipeline it rides), so no host materialisation seam is ' +
      'needed. Flipping off kills candidate intake and Offer delivery but NOT already ' +
      'accepted Routines - their runtimes are ordinary scheduled tasks / kanban templates ' +
      'the user consented to; disabling those is an explicit per-routine revoke (D5b). ' +
      'Ships dark; per-org rollout from the admin Feature Flags page.',
    flagType: 'enum',
    allowedValues: ['off', 'shadow', 'on'],
    // Declared safe value is `off` (fully dark). Fail-safe direction: a flag-DB
    // read error must never start accumulating routine candidates or delivering
    // proactive Offers on its own. Shadow is a deliberate per-org calibration
    // step, not the absent-flag fallback. No envVar - net-new API-side control
    // with no pre-flags env gate to migrate (ADR-0022 / check-new-env-gates.sh).
    defaultValue: 'off',
  },
  {
    key: 'mcp-quarantine-mode',
    description:
      'MCP bind-failure auto-quarantine (ENG-7916): off = disabled, shadow = log only (would-quarantine without acting), ' +
      'enforce = mark integration status=unhealthy after DEFAULT_BIND_FAILURE_QUARANTINE_THRESHOLD consecutive ' +
      'missing|unreachable session-tool-bind verdicts. Unhealthy integrations are excluded from .mcp.json by the ' +
      'existing ENG-5292 writer, breaking the storm → restart → probe → storm loop for permanently-failing MCPs. ' +
      'Declared safe value is shadow (observe-only, non-destructive, stays diagnosable). A flag-DB read error ' +
      'degrades here. Flip to enforce only after shadow metrics confirm the threshold is tuned correctly.',
    flagType: 'enum',
    allowedValues: ['off', 'shadow', 'enforce'],
    // shadow = observe-only safe default. See comment block above.
    defaultValue: 'shadow',
    // Enforcement-mode control: like the sibling off/shadow/enforce flags
    // (channel-quarantine-mode, composio-hitl-mode) it's masked on public
    // surfaces and mutations require explicit confirmation (CodeRabbit PR #3576).
    sensitive: true,
    // No envVar - net-new API-side control with no pre-flags env gate to migrate (ADR-0022).
  },
  {
    key: 'channel-quarantine-auto-resume',
    description:
      'Time-based auto-resume of an ENG-5932 channel quarantine (ENG-10510): off = disabled, ' +
      'shadow = log the would-resume without acting, enforce = clear the local quarantine marker ' +
      'and re-add the channel for a TRIAL run, letting the reaper re-quarantine it if it is still ' +
      'dead. Backoff 2h doubling to a 24h cap, giving up after 5 attempts and leaving it for an ' +
      'operator; the manual Re-enable is unaffected and clears the strike count. ' +
      'DELIBERATELY NOT health-based: a quarantined channel is dropped from .mcp.json and the launch ' +
      'flags, so its process never spawns and its liveness is unobservable - unlike an ENG-7916 ' +
      'integration, which stays probeable via a reconstructed inline_server_entry, which is why ' +
      'mcp-auto-resume-mode can wait for healthy cycles and this cannot. ' +
      'A SEPARATE FLAG FROM channel-quarantine-mode ON PURPOSE: that one defaults to shadow, so ' +
      'giving it a resume meaning would arm auto-resume fleet-wide on the existing default. ' +
      'Declared safe value is off (fully dark): a flag-DB read error must never re-add a channel on ' +
      'its own, because a trial costs restarts against the breaker budget.',
    flagType: 'enum',
    allowedValues: ['off', 'shadow', 'enforce'],
    // off = fully dark. See the description's fail-safe reasoning.
    defaultValue: 'off',
    // Enforcement-mode control like its siblings channel-quarantine-mode and
    // mcp-auto-resume-mode: masked on public surfaces, mutations confirmed.
    sensitive: true,
    // ENG-10036: the reader lives in apps/cli (manager-worker), so an older
    // host has no code to honour a flip. Without `since`, classifyHostReach
    // reports 'honors' - full reach - which is the most confident thing the
    // field can say and would be a lie here.
    //
    // DELIBERATE UNREACHABLE PLACEHOLDER - NOT A REAL THRESHOLD YET.
    // Obi-Wan on #6313: the previous value here was '0.28.990', written before
    // merge and stated as though it were the reader's shipping version. It is
    // not knowable pre-merge. `since` is documented (types.ts) as "the agt-cli
    // version its reader lands in", and the bisected siblings in this registry
    // were all read off published dists AFTER their merge. The published line
    // is around 0.28.916-0.28.918, so that guess sat ~70 patch releases above
    // the build that will actually carry this reader - every host running the
    // release that DOES honour the flag would have classified `stale` until the
    // fleet passed a version that does not exist yet.
    //
    // So this follows the convention this registry already established for
    // exactly this situation - see `github-broker-credentials` below, whose
    // identical DELIBERATE UNREACHABLE PLACEHOLDER block and `since: '0.29.0'`
    // are the precedent: pinned ABOVE every published release ON PURPOSE,
    // labelled as a placeholder, and gated on a post-publish bisect.
    //
    // (This cited `claudecode-agt-bin-credential-helpers` until obi-wan
    // `8f9d3c43076c` pointed out no such key exists anywhere in the repo - the
    // only occurrence in the tree was this citation. The precedent was real and
    // the reasoning held, but the pointer could not be followed, so a reader
    // looking it up found nothing and had no way to tell whether the convention
    // was inherited or invented here. That is the one question a citation
    // exists to answer, and it is asked at exactly the moment someone is about
    // to do the post-publish bisect.) It classifies every host
    // as `stale`, which reports no reach rather than reach there is no basis
    // for - and unlike a plausible-looking number, it says out loud that it
    // must be re-bisected.
    //
    // BEFORE THIS FLAG IS FLIPPED ANYWHERE: bisect the published dist for the
    // first agt-cli whose manager-worker carries the auto-resume poll block,
    // and replace this with that exact version. Leaving the placeholder is safe
    // (defaultValue 'off', sensitive: true, nothing flips); shipping a guessed
    // number is not.
    since: '0.29.0',
  },
  {
    key: 'mcp-auto-resume-mode',
    description:
      'MCP auto-resume of a bind-failure quarantine (ENG-8036, follow-up to ENG-7916): off = disabled, ' +
      'shadow = log only (would-resume without acting), enforce = flip an ENG-7916-quarantined integration ' +
      '(status=unhealthy) back to status=active after DEFAULT_AUTO_RESUME_OK_CYCLES consecutive healthy ' +
      'connectivity-probe cycles. Recovery is observed via the connectivity-probe path, not the ' +
      'session-tool-bind probe (a quarantined row is excluded from the bind probe by the ENG-5292 writer). ' +
      'Only rows this quarantine system marked are auto-resumed - the reaper/oauth-refresh error/unhealthy ' +
      'states are never stomped. Declared safe value is off (fully dark): a flag-DB read error must never ' +
      'auto-un-quarantine on its own. Flip to shadow to observe, then enforce once the cycle threshold is tuned.',
    flagType: 'enum',
    allowedValues: ['off', 'shadow', 'enforce'],
    // off = fully dark. See comment block above for the fail-safe direction.
    defaultValue: 'off',
    // Enforcement-mode control, like its ENG-7916 sibling mcp-quarantine-mode:
    // masked on public surfaces and mutations require explicit confirmation.
    sensitive: true,
    // No envVar - net-new API-side control with no pre-flags env gate to migrate (ADR-0022).
  },
  {
    key: 'channel-quarantine-mode',
    description:
      'Optional-channel quarantine (ENG-5932): off = disabled, shadow = log matches only, enforce = quarantine.',
    flagType: 'enum',
    allowedValues: ['off', 'shadow', 'enforce'],
    // Declared safe value is `shadow` (compute + log "would quarantine X",
    // takes no action) — NOT `off`. Two reasons: (1) shadow is observe-only, so
    // it's as non-destructive as off while staying diagnosable; (2) it matches
    // the live manager's compiled default since ENG-5932, so migrating the host
    // reader onto this flag (ENG-6252) preserves fleet behaviour rather than
    // silently disabling the quarantine logic. Enforcement (`enforce`) stays a
    // deliberate, audited flip via the admin surface.
    defaultValue: 'shadow',
    envVar: 'AGT_CHANNEL_QUARANTINE_MODE',
    // Enforcement gate: weakening it (enforce → shadow/off) drops a channel
    // safety control, so mutations require explicit confirmation.
    sensitive: true,
  },
  {
    key: 'docker-hygiene',
    description:
      'Manager-side docker hygiene on Docker-isolated hosts (ENG-8401): off = no-op, ' +
      'reap-superseded = after a successful agt-runtime retag, remove the prior untagged ' +
      'agt-runtime images that retag orphaned (Layer 1), full = reap-superseded plus a ' +
      'daily dangling-only image+builder prune (Layer 2). agt-runtime:latest and the ' +
      'just-pulled image are never removed and -a/--volumes are never used. Read host-side ' +
      'via hostFlagStore (getString); ships dark.',
    flagType: 'enum',
    allowedValues: ['off', 'reap-superseded', 'full'],
    // Declared safe value is `off`: this flag introduces NEW janitorial behaviour
    // (the pre-flag compiled behaviour is "never reap"), so off preserves fleet
    // behaviour on rollout. Not `sensitive` — weakening it (full → off) only stops
    // cleanup; it relaxes no safety/enforcement control. No envVar by design: this
    // gate is flag-managed only, with no operator env override.
    defaultValue: 'off',
    // New host-side reader (ENG-8401): its consumer lands in the agt-cli build
    // this PR publishes (auto-publish patch-bumps from npm's current 0.28.503 →
    // 0.28.504), so a flip only takes effect on hosts running >= that version.
    // Best-effort reach estimate only; not part of the schema fingerprint.
    since: '0.28.504',
  },
  {
    key: 'utilization-occupancy-qualifier',
    description:
      'Utilization busy-minutes gate (ENG-8465): shadow = report every measured ' +
      'bucket exactly as before while COUNTING how many a transcript qualifier ' +
      'would drop; enforce = drop measured buckets that have no qualifying assistant ' +
      'turn (>= 25 output tokens, non-synthetic) within ±120s. The qualifier is the ' +
      'merged @augmented/core primitive (extractQualifyingTurns / isBucketQualified); ' +
      'the host reads a bounded transcript tail incl. <sessionId>/subagents/ at drain ' +
      'time and FAILS OPEN (credits ungated) when a transcript cannot be read. Read ' +
      "host-side via hostFlagStore (getString) in the manager; consumer ships in this " +
      "PR's agt-cli auto-publish bump. Both modes emit the OccupancyQualificationClassifications " +
      'shadow metric (qualified positive-control + unqualified_dropped signal + unreadable), ' +
      'so per-agent impact is measured before the flip.',
    flagType: 'enum',
    allowedValues: ['shadow', 'enforce'],
    // Declared safe value is `shadow`: it reproduces pre-flag billing exactly, so
    // rollout changes no agent's minutes while the shadow metric accrues the
    // enforce-flip evidence. `enforce` lowers a billing-adjacent number, so it is a
    // deliberate, audited flip — never a flag-DB-read-error default.
    defaultValue: 'shadow',
    // Operator per-host override (ADR-0022 highest precedence). No _ENABLED/_DISABLED/
    // _MODE suffix, and read via hostFlagStore not a raw process.env gate, so not a
    // new env gate under ENG-6252. Never pinned in sst.config.ts (ENG-8303).
    envVar: 'AGT_UTILIZATION_OCCUPANCY_QUALIFIER',
    // enforce lowers a billing-adjacent number, so flipping/weakening it is guarded
    // like the other enforcement-mode flags.
    sensitive: true,
    // Reach threshold (ENG-8465): the host reader (occupancyQualifierMode in
    // manager-worker.ts) lands in this PR's agt-cli auto-publish patch bump, which
    // is the first published version to contain it (through 0.28.528 does not). An
    // UNSET `since` makes classifyHostReach report `honors` (full coverage), which
    // would greenlight an `enforce` flip while older hosts silently stay `shadow`.
    // Best-effort estimate (like docker-hygiene's), in the safe direction — a
    // not-yet-published version reports 0% honor until hosts update. (CodeRabbit)
    since: '0.28.529',
  },
  {
    key: 'composio-hitl-mode',
    description:
      'Composio managed-toolkits HITL gate (ENG-6027): off = kill switch, shadow = ' +
      'resolve + audit the tier decision but never block, enforce = block ' +
      'write_high_risk+ calls and route them to Human Approval (an Approve/Deny card). ' +
      'Since ENG-7828 enforce ALWAYS routes to a Human Approval card by default; set ' +
      'hitl-approval-routing=off to make enforce a hard block with no approval instead. ' +
      'Per-org rollout from the admin Feature Flags page; ENG-7672 migrated this off ' +
      'the fleet-wide COMPOSIO_HITL_MODE env var.',
    flagType: 'enum',
    allowedValues: ['off', 'shadow', 'enforce'],
    // Declared safe value is `shadow` (resolve + audit, never block) — NOT `off`.
    // shadow keeps the control evaluating + producing enforce-flip evidence while
    // staying non-blocking, and matches the pre-flag compiled default so migrating
    // onto the flag (ENG-7672) preserves fleet behaviour. A flag-DB read error
    // degrades here. `enforce` is the deliberate, audited flip.
    defaultValue: 'shadow',
    // Migration override (ADR-0022): the pre-flags env gate stays the
    // highest-precedence operator override. It is only injected onto the Lambdas
    // when an operator explicitly sets it (sst.config.ts) — otherwise this flag governs.
    envVar: 'COMPOSIO_HITL_MODE',
    // enforce blocks prod tool calls, so weakening it (enforce → shadow/off) drops a
    // safety control — mutations require explicit confirmation.
    sensitive: true,
  },
  {
    key: 'hitl-batch-approval',
    description:
      'ADR-0104: may an agent in this org ask for approval of a BATCH — one exact list of up to ' +
      '1,000 destructive calls on a single card — instead of one card per call? The incident ' +
      'behind it needed ~700 calendar deletes and produced ~700 cards, which is a queue nobody ' +
      'reads and therefore a gate nobody applies. ' +
      'This flag is only half the gate, and the other half is deliberate: a tool must ALSO ' +
      'declare a `batch` block in the catalog (its reconciliation rule and pacing), so arming an ' +
      'org can never batch a tool whose semantics nobody reviewed. With the flag on and no tool ' +
      'opted in, every batch request is refused — which is the state this ships in. ' +
      'It does not lower any tier: a batched `write_destructive` call still routes to a human, ' +
      'and above 20 items that human must open the full list before approving. What the flag ' +
      'changes is how many actions ONE decision can cover. Evaluated per-org. Ships dark.',
    flagType: 'boolean',
    // Off is the safe value in both directions. An org that never opted in cannot have an agent
    // request a batch, and a flag-read failure degrades to "not opted in" rather than to a
    // capability nobody asked for — an infrastructure blip is not an opt-in.
    defaultValue: false,
    // Turning it on lets one human tap authorise up to 1,000 irreversible calls, so it is a
    // deliberate, audited per-org flip (ADR-0022 §4).
    sensitive: true,
    // NO newOrgOverride. Nothing has run in production yet; arming every new org on deploy
    // would put a first-of-its-kind approval shape in front of real customers with nothing
    // observed. Arm one org, watch a batch drain, then revisit.
  },
  {
    key: 'hitl-approval-routing',
    description:
      'Routing for HITL-blocked integration calls (ENG-6028, ENG-7828): on (default) = ' +
      'route a blocked call into the Human Approval flow (an approval_requests row + an ' +
      'Approve/Deny card on Slack/Telegram, or a structured reply-to-approve prompt on ' +
      'channels with no buttons). off = kill switch: the blocked call hard-stops with an ' +
      'honest terminal block and NO approval is offered, for an org/agent that must never ' +
      'send even with approval. Needs a block first (composio-hitl-mode=enforce, or the ' +
      'always-on Direct-HTTP lane). ENG-7828 flipped the default from off to on so an org ' +
      'at enforce always produces a Human Approval card instead of a prose dead-end. Per-org ' +
      'rollout; ENG-7672 migrated this off the fleet-wide HITL_APPROVAL_ROUTING env var.',
    flagType: 'enum',
    allowedValues: ['off', 'on'],
    // Steady-state default is `on` (ENG-7828): an org at composio-hitl-mode=enforce routes
    // blocked calls to a Human Approval card rather than dead-ending in prose. `off` is the
    // explicit operator kill switch (honest hard block, nothing executes) for "never send,
    // even with approval". NOTE the split from the DB-READ-ERROR fallback:
    // resolveHitlApprovalRoutingMode still degrades to `off` when it cannot READ the flag
    // (the conservative direction when the value is unknown), which is distinct from this
    // absent-override default of `on`. Either way the call is blocked; only card-vs-terminal
    // differs, so degrading to `off` never leaks a send.
    defaultValue: 'on',
    // Migration override (ADR-0022): only injected onto the Lambdas when an operator
    // explicitly sets it (sst.config.ts) — otherwise this flag governs.
    envVar: 'HITL_APPROVAL_ROUTING',
    // Flipping this to `off` disables the Human Approval card for enforce-blocked calls
    // (hard block instead), a policy change worth an explicit confirm.
    sensitive: true,
  },
  {
    key: 'email-guard-per-scope-stage',
    description:
      'Per-agent / per-team email-domain guardrail rollout stage (ENG-7830 / ADR-0048). ' +
      'When ON for an org, the effective email_guard_stage is resolved most-specific-non-null ' +
      '(agent ?? team ?? org, with time-boxed exemption decay), so one agent can run shadow ' +
      'while the org is enforce (and vice-versa). When OFF (default) the stage is org-grain only ' +
      '(pre-ENG-7830 behaviour) - the per-agent/team email_guard_stage columns are ignored. ' +
      'Gates the resolver seam (getEffectiveEmailGuardStage) and the owner/admin write routes. ' +
      'Evaluated per-org. Ships dark.',
    flagType: 'boolean',
    // Declared safe value is `false` (org-only stage = today's behaviour). A flag-DB
    // read error degrades here, so the per-scope columns stay ignored and the org
    // policy governs - the fail-closed direction for a compliance control.
    defaultValue: false,
    // Enabling it lets a lower scope LOOSEN an email compliance control per-agent, so
    // flipping it on is worth an explicit confirm (ADR-0022 §4).
    sensitive: true,
  },
  {
    key: 'email-guard-enforce-enabled',
    description:
      'Platform enable-gate for the email-domain guardrail ENFORCE stage (ENG-5827 / A5, ' +
      'migrated to a flag under ENG-7899 / ADR-0022). When ON for an org, an org (or scope) at ' +
      'email_guard_stage=enforce actually BLOCKS sends to non-allowed domains: the rollout UI ' +
      'unlocks the Enforce option and PUT /email-guard accepts enforce. When OFF (default) a ' +
      'per-org enforce stage degrades to observe-only (would_block, never blocks) and the UI ' +
      'shows Enforce as not yet available. Ungates a control that blocks real customer email, so ' +
      'flip on per org only once block-spike alerting (ENG-5847) is firing. Evaluated per-org. ' +
      'Operator override: env EMAIL_DOMAIN_ENFORCE_ENABLED (an explicitly-set value wins over the ' +
      'flag; highest precedence per host). Ships dark.',
    flagType: 'boolean',
    // Declared safe value is `false` (enforce degrades to observe-only = today's behaviour).
    // A flag-DB read error degrades here, so a flags outage can never silently start blocking
    // real email - the fail-closed direction for a blocking compliance control.
    defaultValue: false,
    // Flipping it on ARMS a control that blocks real customer email, so it is a
    // deliberate, audited per-org flip (ADR-0022 §4).
    sensitive: true,
  },
  {
    key: 'email-require-approval-enforcement',
    description:
      'Enforcement gate for the `email.require_approval` guardrail (ENG-9611). That guardrail ' +
      'has been `default_for_new_orgs` since ~2026-05-03 and its display text promises "Require ' +
      'human approval before sending any email", but nothing ever read it: its only runtime path ' +
      'was toGuardrailForPrompt, i.e. a line in the agent CLAUDE.md. When ON for an org, a ' +
      'DISPATCHING email send (never a draft-create, ENG-7980) is held and routed to Human ' +
      'Approval regardless of recipient domain — which is what "any email" means, and is what ' +
      'distinguishes it from email-guard-require-approval-enabled, whose require_approval stage ' +
      'only intercepts a would-be DOMAIN block. ' +
      'Deliberately separate from the guardrail row: roughly four months of orgs already carry an ' +
      'inert `enforce`-level row, so reading the row alone would arm a blocking control across all ' +
      'of them on deploy. New orgs are armed by an org-scoped override written at org creation; ' +
      'existing orgs stay off until an operator flips them. Evaluated per-org. Ships dark.',
    flagType: 'boolean',
    // Declared safe value is `false` (no hold). A flag-DB read error degrades here, so a flags
    // outage can never silently start holding real customer email — the same fail-OPEN posture
    // enforceEmailDomainGuard already takes, and the opposite of the enforcement resolver's
    // fail-CLOSED, deliberately: a control that cannot verify itself must not block mail.
    // No envVar: net-new control with no pre-flags env gate to migrate (ADR-0022).
    defaultValue: false,
    // Turning it on holds real customer email pending a human tap, so it is a deliberate,
    // audited per-org flip (ADR-0022 §4).
    sensitive: true,
    // ENG-9629: NEW orgs are armed on. Declared here rather than hardcoded in the
    // seeding code, so `listNewOrgFlagOverrides()` is the one answer to "which flags
    // does a new org get". `defaultValue` stays false so the ~4 months of orgs already
    // carrying an inert enforce-level email.require_approval row are NOT armed on deploy.
    // ENG-9675: EVERY creation path, self-serve included.
    //
    // ENG-9630 had restricted this to API-created orgs on the grounds that a
    // self-serve org has no way of being TOLD about a held send — no Slack
    // alert channel is wired at signup. That reasoning was wrong, and the check
    // behind it was incomplete: it confirmed an in-console page exists at
    // /organization/approvals and stopped, without looking at the dispatch path.
    //
    // `routeBlockedCallToApproval` has three dispatchers, and the Direct Chat
    // one (lib/integration-broker-approval-direct-chat.ts, ENG-7680) is the
    // universal fallback — "every managed agent always has a Direct Chat session
    // its operator/manager watches in the Console ... usable out of the box with
    // no per-org approval-route setup and no Slack dependency". It posts into a
    // thread keyed on the approval_requests.id and correlates the manager's
    // reply back through tryCaptureDirectChatApproval, so a self-serve org has
    // both a zero-config delivery channel AND a reply path.
    //
    // With the stated blocker gone, the guardrail's own promise — "before
    // sending any email" — applies to the organizations with the LEAST operator
    // oversight exactly as it does to the rest.
    //
    // Existing self-serve orgs are deliberately NOT armed by this: newOrgOverride
    // is applied at creation, and nothing backfills. Extending it to them is a
    // separate decision with a comms obligation.
    newOrgOverride: true,
  },
  {
    key: 'mail-rule-control-enforcement',
    description:
      'Enforcement gate for the `email.mail_rules` guardrail (ENG-9640). A mail RULE is not a ' +
      'send: the agent installs a standing server-side instruction and the PROVIDER acts on it ' +
      'from then on — on mail that has not arrived yet, after the session ends, after the agent ' +
      'is paused or revoked, and after the integration is disconnected. So no fix to the send ' +
      'registry can reach it (ENG-9607 / ENG-9633 both closed the send surface), and before this ' +
      'guardrail NOTHING read the five rule-mutation tools at all. When ON for an org, a rule ' +
      'mutation whose classified disposition is held (egress / suppression / opaque) routes to ' +
      'Human Approval; a label-only filter passes untouched, which is what keeps the control ' +
      'from becoming a click-through. ' +
      'Deliberately separate from the guardrail row, for the ENG-9611 reason one step earlier: ' +
      '`email.mail_rules` is `default_for_new_orgs`, and org seeding writes those rows at ' +
      "`enforcement: 'enforce'`, so reading the row alone would arm a brand-new blocking control " +
      'on deploy for every org created after it lands. Evaluated per-org. Ships dark.',
    flagType: 'boolean',
    // Declared safe value is `false` (no hold). A flag-DB read error degrades here, matching
    // `email-require-approval-enforcement`: a control that cannot verify itself must not start
    // holding real customer traffic. Note this is the opposite of the CLASSIFIER's posture
    // inside mail-rule-tools.ts, which fails CLOSED on an unreadable rule — the two are
    // consistent, not contradictory. The classifier answers "is this rule dangerous?" and
    // resolves doubt towards held; the flag answers "is this org opted in at all?" and an
    // infrastructure blip is not an opt-in.
    defaultValue: false,
    // Turning it on holds real customer mail-rule changes pending a human tap, so it is a
    // deliberate, audited per-org flip (ADR-0022 §4).
    sensitive: true,
    // NO newOrgOverride, unlike email-require-approval-enforcement. That flag armed new orgs
    // because its guardrail row had already sat inert for ~4 months, so the behaviour was
    // known. This control is net-new with no soak: arming it for every new org on deploy would
    // put a first-of-its-kind classifier in front of real customer traffic with nothing
    // observed. Arm per-org, then revisit once there is evidence.
  },
  {
    key: 'email-guard-require-approval-enabled',
    description:
      'Platform enable-gate for the email-domain guardrail REQUIRE_APPROVAL stage (ENG-7939). ' +
      'When ON for an org, a scope at email_guard_stage=require_approval routes a send to a ' +
      'non-allowed domain into Human Approval (an Approve/Deny card) instead of a hard block: ' +
      'the rollout UI unlocks the option and the write routes accept require_approval. When OFF ' +
      '(default) the write routes reject require_approval and the UI shows it as not yet ' +
      'available, so no scope can select it. Structurally also depends on hitl-approval-routing ' +
      'being on (the approval broker fails closed to a hard block when routing is off). Evaluated ' +
      'per-org. Ships dark.',
    flagType: 'boolean',
    // Declared safe value is `false` (require_approval is unselectable / inert). A flag-DB read
    // error degrades here, so a flags outage can never start routing customer email through a
    // half-wired approval path - the fail-closed direction. No envVar: net-new control with no
    // pre-flags env gate to migrate (ADR-0022).
    defaultValue: false,
    // Ungates a stage that changes how real customer email is handled (block becomes approval),
    // so it is a deliberate, audited per-org flip (ADR-0022 §4).
    sensitive: true,
  },
  {
    key: 'approval-notify-auto-resolve',
    description:
      'Approval-notification "Auto" dispatch resolution (ENG-7985). When ON for an org, an ' +
      'approver whose approval_notify_channel is "Auto" (unset) has their approval card resolved ' +
      'to the best LINKED chat surface at dispatch time - Slack DM first, else Telegram DM - ' +
      'instead of silently falling through to the team channel / agent-watched direct-chat thread ' +
      '(where the approver never sees it). Explicit slack/telegram/email/msteams preferences are ' +
      'unchanged, and an explicit-but-unsatisfiable pref is never silently rerouted. Default OFF is ' +
      "behaviour-preserving, so existing orgs' approvers do not suddenly start receiving DMs " +
      'without opt-in. Evaluated per-org. Ships dark.',
    flagType: 'boolean',
    // Safe/default value is `false` (today's resolution: Auto falls to team/direct-chat). A flag-DB
    // read error degrades to this, so an outage can never flip approval routing for an org.
    defaultValue: false,
    // A routing/visibility change (which surface an approval card is DM'd to), not a control that
    // blocks customer email - so not `sensitive`, unlike the email-guard enable gates above.
    sensitive: false,
  },
  {
    key: 'slack-reply-binding',
    description:
      'Slack reply-target binding (ENG-7396 / WS2, cross-thread reply routing): ' +
      'the model names which inbound it answers (inbound_id) and the server ' +
      'resolves the destination from its own verified record. shadow = classify + ' +
      'count only, routing unchanged; warn = same routing plus a tool-result note ' +
      'so the model self-corrects; enforce = route a bound reply to the registry ' +
      'thread and REJECT a reply whose target matches no inbound this process ' +
      'delivered. The channel server can\'t evaluate flags, so the manager ' +
      'materializes the resolved value into the AGT_SLACK_REPLY_BINDING spawn env ' +
      '(the operator/canary override, ADR-0022). Ships shadow.',
    flagType: 'enum',
    allowedValues: ['shadow', 'warn', 'enforce'],
    // Declared safe value is `shadow`: observe + count only, no behaviour change.
    // enforce is the deliberate, audited flip that actually blocks/reroutes.
    defaultValue: 'shadow',
    envVar: 'AGT_SLACK_REPLY_BINDING',
    // enforce blocks/reroutes egress, so weakening or flipping it is worth an
    // explicit confirm (ADR-0022 §4).
    sensitive: true,
  },
  {
    key: 'notify-dispatch',
    description:
      'Membership-based channel-notify dispatch (ENG-7682 / notify Slice 1). ' +
      'off = today\'s behaviour: a non-@mention top-level channel message is dropped ' +
      'by the mention_only engagement gate (SLACK_CHANNEL_RESPONSE_MODE). ' +
      'membership = the agent\'s Slack MCP ADMITS non-@mention channel messages in ' +
      'channels its bot is a member of (the Socket Mode websocket already receives ' +
      'them), waking the agent in-thread via the SAME existing wake path used for ' +
      '@mentions/DMs. Only the mention_only engagement gate is relaxed - the ' +
      'echo/dedup, bot_id/self, and peer-classifier safety leaves still apply, so ' +
      'this never opens a bot-to-bot wake loop. The channel server can\'t evaluate ' +
      'flags, so the manager materializes the resolved value into the ' +
      'AGT_NOTIFY_DISPATCH spawn env (operator/canary override, ADR-0022). ' +
      'filter = like membership (opt-out) but honours a per-channel mute list: a ' +
      'member-channel message wakes the agent UNLESS the user muted that channel via ' +
      'the /notify slash command. The manager materializes the muted set into ' +
      'AGT_NOTIFY_MUTED_CHANNELS from agent_notify_channel_prefs (ENG-7682 Slice 2). ' +
      'addressed = like filter (same per-channel mute, checked FIRST so a mute ' +
      'outranks being named), but a member-channel message additionally has to ' +
      'address THIS agent to wake it: a real Slack mention of its bot user id, ' +
      '`@<code_name>` typed verbatim, or the code name used as a vocative at the ' +
      'start of the message ("koda:", "hey koda"). @team / @everyone still wake ' +
      'everyone. This is the fix for one named agent waking every agent in a ' +
      'shared channel (ENG-8657); matching is deliberately conservative, so a ' +
      'name mentioned mid-sentence does NOT wake, and an agent whose identity is ' +
      'unknown to the channel server stays asleep rather than waking on ' +
      'everything. ' +
      'Ships dark (default off).',
    flagType: 'enum',
    allowedValues: ['off', 'membership', 'filter', 'addressed'],
    // Declared safe value is `off`: the mention_only gate stays in force, byte-
    // identical to today. membership is the deliberate, per-agent flip that
    // widens what wakes the agent.
    defaultValue: 'off',
    envVar: 'AGT_NOTIFY_DISPATCH',
    // membership widens agent-wake ingress (more inbound wakes = more cost /
    // surface), so flipping it on is worth an explicit confirm (ADR-0022 §4).
    sensitive: true,
  },
  {
    key: 'workflows-down-sync',
    description:
      'Down-sync active dynamic workflows to agents via /host/refresh (ADR-0012, ENG-6352). When on, the API populates the per-agent workflow set (team default ∪ agent override) and the claude-code adapter writes each as .claude/workflows/<name>.js. Boolean gate; ships dark.',
    flagType: 'boolean',
    // Declared safe value is `false` (no down-sync). The fail-safe direction is
    // dark: if the API can't reach the flag DB, agents simply receive no
    // workflows — never a partial or stale set written to their project dir.
    // Per-org rollout: flip on for one org from the admin Feature Flags page.
    defaultValue: false,
    // Migration override (ADR-0022): the pre-flags env gate stays the
    // highest-precedence operator override so existing AGT_WORKFLOWS_ENABLED
    // hosts/stages keep working until the env var is retired.
    envVar: 'AGT_WORKFLOWS_ENABLED',
  },
  {
    key: 'integration-connectivity-escalation',
    description:
      'Arm integration-connectivity escalation (ENG-5641). When on, the connectivity-monitor cron opens/closes integration_down alerts; when off it stays shadow — computes and logs what it WOULD page but opens nothing. Boolean gate; ships dark.',
    flagType: 'boolean',
    // Declared safe value is `false` (shadow / no paging). It matches the cron's
    // compiled default since ENG-5641, so migrating the reader onto this flag
    // (ADR-0022) preserves fleet behaviour: escalation stays dark until an
    // operator deliberately arms it per-stage from the admin Feature Flags page.
    // `false` is also the fail-safe direction — a flag-DB read error must never
    // start paging on its own.
    defaultValue: false,
    // Migration override (ADR-0022): the pre-flags env gate stays the
    // highest-precedence operator override so existing
    // AUGMENTED_CONNECTIVITY_ESCALATION_ENABLED stages keep working until the
    // env var is retired.
    envVar: 'AUGMENTED_CONNECTIVITY_ESCALATION_ENABLED',
  },
  {
    key: 'integration-connectivity-reconcile',
    description:
      "Arm integration-connectivity STATUS reconciliation (ENG-6139, armable per-org by ENG-10473). When on, a hard-down connectivity observation (a `down` reading with consecutive_connectivity_failures at/over the hysteresis threshold) on an otherwise-healthy row downgrades it to status=error, status_message=connectivity_down, so the record stops asserting a live connection the probe has disproved; recovery restores status=active, but ONLY for rows this path downgraded. When off it stays shadow - logs what it WOULD downgrade and touches only the connectivity columns. ⚠️ A downgraded row drops out of host-runtime's `.in('status',['active','configured'])` provisioning filter, so the agent LOSES that MCP server: correct for a dead credential, and a provisioning change either way, which is why arming is a deliberate operator act. Sibling of integration-connectivity-escalation: that flag arms the alert, this one arms the record.",
    flagType: 'boolean',
    // Declared safe value is `false` (shadow / record untouched). It matches the
    // route's compiled default since ENG-6139, so migrating the reader onto this
    // flag (ADR-0022) preserves fleet behaviour exactly. `false` is also the
    // fail-safe direction: a flag-DB read error must never de-provision an
    // integration on its own.
    defaultValue: false,
    // Migration override (ADR-0022): the pre-flags env gate stays the
    // highest-precedence operator override. ENG-10473 deliberately does NOT wire
    // this var in sst.config.ts - ENG-8303 records what happens when a declared
    // envVar is pinned there: it outranks the DB value and makes the flag inert
    // everywhere it matters.
    envVar: 'AUGMENTED_CONNECTIVITY_RECONCILE_STATUS',
  },
  {
    key: 'slack-manifest-remediate',
    description:
      'Let the Slack manifest drift monitor PUSH, not just alert (ENG-9963). ' +
      'updateSlackManifest had exactly one caller — a console button — so nothing but a ' +
      'human click ever pushed a manifest; ENG-7762 changed the generator with no rollout ' +
      'mechanism and 26 agents went 53 days with no working per-agent slash commands. When ' +
      'on, the daily drift cron pushes the generated manifest for a drifted agent. When off ' +
      'it alerts exactly as before. NOTE what arming this authorises: a push REPLACES a ' +
      'customer-owned Slack app\'s entire manifest, so the cron only ever pushes drift whose ' +
      'diffs are purely additive (isSafelyRemediable) — an app with live-only entries a push ' +
      'would destroy is declined and left for a human. Boolean gate; ships dark.',
    flagType: 'boolean',
    // Declared safe value is `false` (alert-only), matching the cron's compiled
    // default. The fail-safe direction is the opposite of a paging gate's and
    // matters more: a flag-DB read error must never authorise a WRITE to a
    // customer's Slack app.
    defaultValue: false,
    // Operator override, same precedence and vocabulary as the connectivity
    // escalation gate above, so an operator who knows one knows both.
    envVar: 'AUGMENTED_SLACK_MANIFEST_REMEDIATE_ENABLED',
    // ADR-0022 §4. NOTE this is where it diverges from the connectivity gate it
    // otherwise mirrors, and the divergence is the point: arming THAT one only
    // causes alerts to open, while arming THIS one authorises a cron to REPLACE
    // manifests on customer-owned Slack apps. That is precisely the class the
    // sensitive marker exists for — a control whose relaxation should not be one
    // unguarded write away.
    sensitive: true,
  },
  {
    key: 'integration-reauth-reminders',
    description:
      'Re-notify an integration that stays in needs_reauth (ENG-8370). Failure notification is at-most-once PER TRANSITION, so an integration nobody repairs is announced once and then silent forever - prod carried a row failing every 30-minute tick for ~21 days on a single day-one notification. When on, the IntegrationReauthReminder cron re-fires the existing reconnect notification on a backoff measured from failure ONSET (+1d, +3d, +7d, then weekly), skipping rows an operator has muted. When off the cron still scans and logs what it WOULD have sent, so the volume can be sized before anyone is messaged. Boolean gate; ships dark.',
    flagType: 'boolean',
    // `false` is both the pre-ENG-8370 fleet behaviour AND the fail-safe
    // direction: this flag governs how often real humans get messaged, so a
    // flag-DB read error must never start sending on its own. Turning it on is
    // a deliberate per-org act from the admin Feature Flags page, never a
    // side-effect of a deploy. No env override - net-new gate, registry-only
    // (ADR-0022).
    defaultValue: false,
  },
  {
    key: 'managed-health-connectivity-flip',
    description:
      'Arm the managed-connection-health status flip on data-plane hard-down (ENG-7539). When on, the managed-connection-health cron flips a managed integration to needs_reauth when the host-side connectivity probe reports a sustained hard-down (consecutive_connectivity_failures at/over the hysteresis threshold with a down observation), even if Composio account-status still reads ACTIVE. When off it keeps the pre-ENG-7539 behaviour: status is driven only by the account-status probe, so a live 401 behind an ACTIVE account never flips status. Boolean gate; ships dark. Sibling of integration-connectivity-escalation: that flag arms the alert; this flag arms the status flip plus the reconnect notification.',
    flagType: 'boolean',
    // Declared safe value is `false` (no auto status flip). It preserves the
    // pre-ENG-7539 fleet behaviour where managed status derives solely from the
    // account-status probe, so merging this dark changes nothing until an
    // operator arms it per-stage from the admin Feature Flags page. `false` is
    // also the fail-safe direction: a flag-DB read error must never start
    // flipping statuses (and firing reconnect notifications) on its own. No env
    // override - this is a net-new gate, so it is registry-only (ADR-0022).
    defaultValue: false,
  },
  {
    key: 'projects-menu',
    description:
      'Show the Projects nav item in the webapp (ADR-0017 Projects soft launch, ENG-6342). Replaces the isAdmin/adminOnly gate with a per-org rollout flag.',
    flagType: 'boolean',
    // PUBLIC (ADR-0022 §2): this is a UI-rollout flag the browser legitimately
    // needs, so it's the one flag serialized to the client via GET /flags.
    // Evaluated per the caller's active org — flip it on per-org from the admin
    // Feature Flags page to reveal Projects for that org. No env override (UI
    // flag, not an operator gate). Ships dark (default false).
    public: true,
    defaultValue: false,
  },
  {
    key: 'ninjafy-notes-menu',
    description:
      'Show the "Notes" nav item in the webapp, linking to the Ninja Notes landing ' +
      'surface (ENG-9676). The landing page tailors itself to the org: an Opt-in / ' +
      'request-enablement flow when Ninja Notes (the `ninjafy-notes` premium ' +
      'integration) is not yet opted in, or the Mac app DMG download once it is. ' +
      'Per-org rollout gate, sibling to `projects-menu`.',
    flagType: 'boolean',
    // PUBLIC (ADR-0022 §2): a UI-rollout flag the browser needs (the nav shells
    // read it via usePublicBooleanFlag), so it's serialized to the client via
    // GET /flags and evaluated per the caller's active org. Flip it on per-org
    // from the admin Feature Flags page. No env override (UI flag, not an
    // operator gate). Ships dark (default false).
    public: true,
    defaultValue: false,
  },
  {
    key: 'platform-maintenance-mode',
    description:
      'Platform-wide maintenance mode (ENG-6506): when on, every agent across every ' +
      'team/org auto-replies to admitted human inbound with a "we are offline for ' +
      'maintenance" notice instead of dispatching to the agent — for whole-platform ' +
      'downtime such as DB upgrades. Read host-side per inbound from the flags cache ' +
      '(no MCP restart). Boolean gate; ships dark — when off, channels behave exactly ' +
      'as today. (Proactive "back online" follow-up is the ENG-6508 fast-follow, not ' +
      'this flag.)',
    flagType: 'boolean',
    // Declared safe value is `false` (normal operation). Fail-safe direction: a
    // flag-DB read error or absent flag map must never silence the fleet on its
    // own — agents stay responsive unless an operator deliberately flips this on
    // from the admin Feature Flags page. No envVar: net-new control, no
    // pre-flags env gate to migrate.
    defaultValue: false,
    // Flipping this takes the ENTIRE fleet offline to end users; a mis-flip is a
    // platform-wide availability incident, so mutations require explicit
    // confirmation (the admin flip-reach modal).
    sensitive: true,
  },
  {
    key: 'human-hours-given-back',
    description:
      'Show the Human Hours Given Back value metric on the agent productivity surface ' +
      '(ENG-6750): an acceptance-anchored estimate of the human time an agent gave back ' +
      '(kanban tasks completed and not reverted + end-user conversations the success-eval ' +
      'rated helpful, quality-weighted, priced by a versioned rate table). Augmentation ' +
      'framing, not a "does the work of N people" substitution claim. Ships dark; flip on ' +
      'per org from the admin Feature Flags page.',
    flagType: 'boolean',
    // PUBLIC (ADR-0022 §2): a UI-rollout flag the browser reads via GET /flags to
    // decide whether to render the metric block (mirrors projects-menu /
    // direct-chat-drawer). Declared safe value is `false`: the block stays hidden
    // until rolled out per org. No envVar — a browser-only UI gate has no
    // host-side override.
    defaultValue: false,
    public: true,
  },
  {
    key: 'cross-team-kanban-assign',
    description:
      'Allow an agent to assign a kanban task to an agent on a DIFFERENT team in ' +
      'the same organization (ENG-6906), reusing the cross-team peer-messaging ' +
      'consent model: an org set to `unrestricted` needs no grant, an org set to ' +
      '`consent_required` needs a live cross_team_peer_grant. Off = the assign ' +
      'route refuses any cross-team target (same-team kanban_assign is unaffected), ' +
      'so no agent can place a card on another team. Per-org activation gate; ' +
      'ships dark.',
    flagType: 'boolean',
    // Declared safe value is `false`: cross-team assignment is a net-new
    // capability that crosses a team trust boundary, so the fail-safe / absent-
    // flag direction is "no cross-team assignment". No envVar — net-new control
    // with no pre-flags env gate to migrate.
    defaultValue: false,
  },
  {
    key: 'pmf-survey-dispatch',
    description:
      'Fortnightly PMF survey dispatch cron (ENG-6936 / ENG-6958). When on, the ' +
      'daily pmf-survey-dispatcher selects each agent reports_to person due on ' +
      'their 14-day anniversary (anchored to their earliest reporting agent) and ' +
      'records a pending dispatch row for the email + in-app senders to fulfil. ' +
      'Global gate; ships dark. The DB stage value is the kill switch (no env ' +
      'override). Off = the cron does nothing, so no survey is ever dispatched.',
    flagType: 'boolean',
    // Declared safe value is `false`: this drives outbound surveys to customer
    // contacts, so the absent-flag / fail-safe direction is "send nothing".
    defaultValue: false,
  },
  {
    key: 'augmented-support-writes',
    description:
      'Self-remediation writes on the augmented-support host surface (ENG-7000, ADR-0032 ' +
      'Phase 2b). When on, the org-locked /host/support/* write routes (create_agent, ' +
      'modify_agent, install_integration) are reachable; they call the shared agent ' +
      'write-core scoped to the verified host org. Ships dark: the server-rendered HITL ' +
      'approval gate is ENG-7001 (Phase 2c), so until that lands and provisioning attaches ' +
      'the write scope, this flag stays OFF and the write routes return feature_disabled. ' +
      'The DB stage/org override is the control (no env override).',
    flagType: 'boolean',
    // Declared safe value is `false`: these are WRITES against customer infrastructure
    // (creating/modifying agents in the caller's org). The fail-safe / absent-flag
    // direction is "no self-remediation write is possible".
    defaultValue: false,
    // Customer-infrastructure write capability; flipping it on is worth an explicit
    // confirm (ADR-0022 §4), same posture as admin-send-keys.
    sensitive: true,
  },
  {
    key: 'augmented-support-auto-provision',
    description:
      'Org-create auto-provision of the per-org system_support concierge agent ' +
      '("Sherlock") (ENG-7026, ADR-0032 §7). This is the FINAL rollout ' +
      'stage: when on, every newly created organization gets a support agent ' +
      'provisioned (in draft - arming still needs first-run consent) by a ' +
      'best-effort hook in the org-create handler. The earlier rollout stages ' +
      '(dogfood Integrity Labs, then design partners) are driven by the explicit ' +
      'admin command (provision-support --org <slug>), NOT this flag. Ships dark: ' +
      'default OFF means the hook is inert, so there is no fleet-wide first deploy ' +
      '- existing orgs are never touched and new orgs get nothing until this is ' +
      'flipped on. The DB stage value is the control (no env override).',
    flagType: 'boolean',
    // Declared safe value is `false`: the fail-safe / absent-flag direction is
    // "no org auto-provisions a support agent", so a misread can never silently
    // create agents across the fleet.
    defaultValue: false,
    // Auto-creates an org-admin-equivalent agent in every new org; flipping it on
    // is fleet-shaping and worth an explicit confirm (ADR-0022 §4).
    sensitive: true,
  },
  {
    key: 'kanban-source-ambiguity-guard',
    description:
      'Kanban source ambiguity guard (ENG-7394 / WS1a, Slack cross-thread reply ' +
      'routing). The kanban add path auto-injects source_integration / ' +
      'source_external_id from the recent-inbound cache when the agent omits them. ' +
      'When ON for an org, that fallback only fires when EXACTLY ONE fresh inbound ' +
      'thread exists (read from agent_recent_inbound_sources within the freshness ' +
      'window); two or more live threads inject nothing instead of the most-recent ' +
      "thread, so a concurrent second user's card can't be attributed to the wrong " +
      'thread. Off (default) = legacy single-slot last-inbound-wins. Evaluated ' +
      'per-org in POST /host/kanban via getEvaluatedFlags(); rollout is a per-org ' +
      'override (no env override - this is an API-route gate, like admin-live-pane). ' +
      'Ships dark.',
    flagType: 'boolean',
    // Declared safe value is `false`: the fail-safe / absent-flag direction is the
    // current behaviour (single-slot injection). Turning it ON only makes the
    // fallback MORE conservative (injects less), so it removes no control.
    defaultValue: false,
  },
  {
    key: 'kanban-waiting-status',
    description:
      "Let managed agents set the 'waiting' kanban status via POST /host/kanban " +
      '(ADR-0044 / ENG-7493) - work that has started but is parked on a human decision or ' +
      'an external dependency (a PR review, an approval). This is the DEPLOY-ORDER gate ' +
      '(ADR-0044 section 8): the status value + its migration (20260709000003), the webapp ' +
      'Waiting column, and the ~7-day reaper backstop all ship first, and this flag is ' +
      'flipped ON per org LAST, once they are live - the moment agents are allowed to emit ' +
      '`waiting`. Off (default) = the API rejects an agent `waiting` write with the existing ' +
      '400, exactly as today. The API gate (evaluated per-org in POST /host/kanban via ' +
      'getEvaluatedFlags()) is the authoritative boundary. It is ALSO materialized host-side ' +
      'into AGT_KANBAN_WAITING_ENABLED (ENG-7591) so the kanban_move MCP tool only exposes ' +
      '`waiting` to agents once the host resolves the flag on - a host-grained UX hint, not ' +
      'the security boundary (the per-org API gate still decides). Ships dark.',
    flagType: 'boolean',
    // Declared safe value is `false`: agents cannot emit `waiting` until an operator
    // flips it on for the org, after the migration + webapp column + reaper backstop
    // are deployed. Turning it ON grants a new capability, so it is not sensitive in
    // the "relaxing a control" sense, but it MUST stay off until the deploy order is met.
    defaultValue: false,
    // Host-side materialization vehicle (ENG-7591): the manager writes this into the
    // agent's spawn env from the resolved flag so the stdio MCP server (which cannot
    // call the flag evaluator) can gate the kanban_move `waiting` option. Also the
    // highest-precedence operator override, per ADR-0022.
    envVar: 'AGT_KANBAN_WAITING_ENABLED',
  },
  {
    key: 'scheduled-task-nudge-durable',
    description:
      'Deliver the scheduled-task "work this card" nudge over the durable direct-chat ' +
      'notice rail instead of unverified tmux send-keys (ENG-8068, re-landing ENG-7971). ' +
      'When ON, routeScheduledTaskViaKanban enqueues the nudge via POST /host/scheduled-task/notify ' +
      '(kind:notice, push-and-consumed by the in-session MCP with a per-card consume-time ' +
      'revalidation) and closes the card + run on an enqueue failure, retiring the ENG-6351 ' +
      'orphaned-card gap where an unsubmitted keystroke left a scheduled task silently unrun. ' +
      'When OFF (default) the manager keeps the current tmux send-keys delivery, byte-for-byte ' +
      'as today. This is the kill switch: ENG-7971 was reverted once because the durable rail ' +
      '500d on a session_id bug (fixed in ENG-8061); flip OFF to fall back to send-keys instantly ' +
      'without a code rollback if the durable path misbehaves. Resolved host-side by the manager ' +
      'via hostFlagStore().getBoolean (the manager evaluates the decision itself, so no spawn-env ' +
      'materialization is needed). Ships dark.',
    flagType: 'boolean',
    // Declared safe value is `false`: the current, reverted-to send-keys delivery. Turning
    // it ON activates the durable rail per org/host once operators are confident. `false`
    // is also the fail-safe direction - a flag-DB read error must never silently switch a
    // fleet onto the rail that broke prod twice. Net-new gate, so registry-only (ADR-0022);
    // consumed by the manager's own logic, not an in-container MCP, so no env materialization.
    defaultValue: false,
  },
  {
    key: 'spawn-inject-channel-secrets',
    description:
      'ENG-9350 (host key-management hardening, epic ENG-9347): deliver a managed ' +
      "agent's channel bot tokens (SLACK_BOT_TOKEN, SLACK_APP_TOKEN, TELEGRAM_BOT_TOKEN, " +
      'MSTEAMS_CLIENT_SECRET) into the Claude Code spawn env via `tmux new-session -e ' +
      'KEY=VALUE` (the same posture as ANTHROPIC_API_KEY / AGT_API_KEY) instead of writing ' +
      'them as plaintext to the on-disk `.env.integrations` file. ON = the manager injects ' +
      'the four channel secrets at spawn and evicts them from `.env.integrations`; the ' +
      '`.mcp.json` `${VAR}` templates are unchanged and now resolve from the spawn env, so ' +
      'the value never touches persistent EBS (and, under Docker isolation, is name-only ' +
      "forwarded into the container the same way AGT_API_KEY is). OFF (default) = today's " +
      'behaviour, byte-for-byte: the tokens are written to `.env.integrations` (0600) and ' +
      'the wrapper sources them. Resolved host-side by the manager via ' +
      'hostFlagStore().getBooleanForAgent — the manager owns the spawn path, so no ' +
      'in-container MCP reads this; it materializes the VALUES, not the flag. A channel- ' +
      'token rotation already forces a session respawn (ENG-6062), so the injected value is ' +
      'bound at the same moment the file value used to be — the two paths are behaviourally ' +
      'equivalent, this one just keeps the plaintext off disk. Ships dark; flip per-agent/ ' +
      'host to soak before fleet convergence, flip OFF to fall back to the file path ' +
      'instantly without a code rollback. Integration credentials + the OAuth remote-MCP ' +
      'live-read proxy deliberately stay on the file (later slices / a permanent bounded ' +
      'carve-out, ADR-0062 Part 5).',
    flagType: 'boolean',
    // Declared safe value is `false`: the current on-disk `.env.integrations` delivery,
    // byte-for-byte. `false` is also the fail-safe direction — a flag-store read error must
    // never silently move a fleet onto the spawn-env path (a bug there = a channel with no
    // token = a silently dead channel). Net-new gate consumed by the manager's own spawn
    // logic, not an in-container MCP, so registry-only with NO envVar materialization
    // (mirrors scheduled-task-nudge-durable). Turning it ON removes plaintext at rest.
    defaultValue: false,
    // ENG-7997 flip-reach estimate: `since` is the first agt-cli build whose
    // dist carries this host-side reader. The ENG-9350 merge auto-published
    // 0.28.672, and `npm pack @integrity-labs/agt-cli@0.28.672` confirms its
    // bundled dist contains `spawn-inject-channel-secrets` (the reader) — every
    // earlier 0.28.x lacks it. So hosts on ≥ 0.28.672 honor a flip; older ones
    // no-op until they self-update. (Replaced the 99.0.0 fail-safe placeholder
    // the flag shipped with before this build was known.)
    since: '0.28.672',
  },
  {
    key: 'scheduled-task-confirmation-card',
    description:
      'Post the 👍/👎 "Task complete?" kanban confirmation card for completed scheduled_task ' +
      'runs (ENG-4576 / ENG-6048). OFF (default) kills these cards fleet-wide: ENG-8072 found ' +
      'they mis-thread onto the run\'s captured slack_delivery, which in the persistent-session ' +
      'model is frequently a conversational reply rather than the task\'s own output - so a ' +
      'routine cron\'s confirmation card lands in an unrelated human thread (e.g. a half-hourly ' +
      'reconciliation card posted into a "you\'re back online" DM), and a clean cron emits a ' +
      'human 👍/👎 prompt on every tick regardless. The card was net-negative, so it is disabled ' +
      'by default. Turn it ON per org to restore scheduled-task ratings for a customer who wants ' +
      'them (e.g. a daily digest delivered to a channel). Evaluated per-org in the API ' +
      '(POST /host/runs/finish + the manager rating-prompt route) via getEvaluatedFlags(); the ' +
      'decision is passed into postScheduledTaskKanbanConfirmation. API-only gate - no host env ' +
      'materialization. Ships dark (cards off).',
    flagType: 'boolean',
    // Declared safe value is `false`: cards suppressed. `false` is also the fail-closed
    // direction - a flag-DB read error must not resurrect the mis-threading/noise the kill
    // fixes. Net-new gate, registry-only (ADR-0022); API-evaluated, no env materialization.
    defaultValue: false,
  },
  {
    key: 'onboarding-msteams-channel',
    description:
      'Offer Microsoft Teams as a selectable channel on onboarding step 3 ("which channels ' +
      'should your org use?", ENG-8146). OFF (default) hides the Teams option entirely: it is ' +
      'not rendered, not selectable, and not named in the plan-upgrade note. Turn it ON per org ' +
      'to restore the option for a customer who genuinely runs on Teams. Resolved SERVER-SIDE ' +
      'in the step-3 server component for the URL\'s orgId (webapp lib/feature-flags-org-server.ts), ' +
      'NOT via the browser public map — the client flag path evaluates at the active-org cookie, ' +
      'which onboarding deliberately does not pin to the org being onboarded, so a client read ' +
      'would resolve the wrong org for a multi-org user. Hiding the option never revokes a ' +
      'channel: an org that already saved msteams keeps it (step 3 carries hidden-but-saved ' +
      'channels through the write). Ships dark.',
    flagType: 'boolean',
    // Declared safe value is `false`: Teams hidden. `false` is also the fail-closed
    // direction - a flag-DB read error must show the narrower channel set, never
    // surface an option the operator has not deliberately turned on. Net-new gate,
    // registry-only (ADR-0022). NOT `public`: it is resolved server-side only, so it
    // must never be serialized into the browser map.
    defaultValue: false,
  },
  {
    key: 'agent-unified-screen',
    description:
      'Unified agent screen redesign (ENG-8478 epic / ENG-8480). When ON, the webapp renders the ' +
      'new single-screen "viewing is editing" agent surface (stripped-back hero, working agent ' +
      'switcher, inline-editable profile, embedded operational panels + a live direct-chat drawer) ' +
      'in place of the separate agent view/edit pages; when OFF, the current view/edit pages are ' +
      'unchanged. Additive UI gate — no data-model change: each panel reads the same existing ' +
      'endpoints the current pages use. Composes independently with app-top-nav-shell (the two ' +
      'flags are reconciled in ENG-8488). ' +
      'NO LONGER DARK: ENG-9832 cutover step 1 flipped the default to true, so an org with no ' +
      'override now gets the unified screen. The key survives only until cutover step 2 ' +
      'deletes it — which is blocked on ENG-8547 and ENG-8559, because the flag-off branch ' +
      'is still the only home for the /edit tabs.',
    flagType: 'boolean',
    // ENG-9832 (cutover step 1): flipped `false` -> `true`. The comment this
    // replaces called `false` the safe direction — "the current view/edit
    // pages", never "a half-built redesign". That was true while the redesign
    // was half built. It has been the shipped surface for the pilot orgs since
    // ENG-8481, so `false` now points at the pages being retired, not at safety.
    // The old rationale is recorded rather than deleted because it was correct
    // under a premise that has since expired.
    //
    // MIND THE SPLIT. This value is consulted by the API evaluator when it
    // builds the `GET /flags` map for an org with NO override. It is NOT the
    // fallback the webapp uses when that fetch fails: `getPublicBooleanFlagServer`
    // and `usePublicBooleanFlag` both return the argument passed at the CALL
    // SITE and never consult this registry. Three call sites carry `true` to
    // match — agents/[agentId]/page.tsx, agents/[agentId]/edit/page.tsx and
    // `useAgentUnifiedScreenEnabled` in lib/redesign-flags.ts. Flipping the
    // registry alone leaves the cutover half-applied with no error anywhere;
    // eng-9832-unified-fallback-parity.test.ts fails if any of the three drifts.
    defaultValue: true,
    // Read BOTH server-side (agents/[agentId]/page.tsx and its /edit sibling, via
    // getPublicBooleanFlagServer) and client-side (dashboard-sherlock-shell.tsx,
    // via usePublicBooleanFlag), so the key must be in the browser-exposed public
    // map. Additive viewer gate, resolved within the active-org cookie's scope
    // — not sensitive.
    //
    // `public` MUST STAY until step 2 deletes the key, and the reason reads
    // backwards: the server helper resolves against the very SAME public-filtered
    // `GET /flags` the browser uses (routes/flags.ts filters to
    // listPublicFlagKeys()). Drop `public` and the key is simply absent from the
    // map, so both server readers fall through to their call-site argument
    // forever — silently, with no error raised anywhere.
    public: true,
  },
  {
    key: 'app-top-nav-shell',
    description:
      'Global top-navigation shell redesign (ENG-8478 epic / ENG-8486). When ON, the dashboard ' +
      'chrome replaces the left sidebar with a top navigation bar (team switcher · destinations · ' +
      'command palette + account) so the left side is free for an agent-scoped rail; when OFF, the ' +
      'existing left sidebar is unchanged. Additive layout gate — no data-model change. Composes ' +
      'independently with agent-unified-screen (the two flags are reconciled in ENG-8488). ' +
      'NO LONGER DARK: ENG-9831 cutover step 1 flipped the default to true, so an org with no ' +
      'override now gets the top-nav shell. The key survives only until cutover step 2 ' +
      'deletes it.',
    flagType: 'boolean',
    // ENG-9831 (cutover step 1): flipped `false` -> `true`. The comment this
    // replaces called `false` the fail-closed direction — "the shell that ships
    // today". That was true while the sidebar was the shell that shipped; the
    // top nav is now the one being cut over to, so `false` points at the layout
    // being retired. The old rationale is recorded rather than deleted because
    // it was correct under a premise that has expired.
    //
    // NOTE the same split as ninjafy-brand below: this value is consulted by the
    // API evaluator when building the `GET /flags` map for an org with no
    // override. It is NOT the fallback the webapp uses on an outage — that is
    // the argument passed at the call site. Four call sites carry `true` to
    // match: (dashboard)/layout.tsx and the three legacy redirect hubs
    // (settings, team, organization/settings). Flipping the registry alone
    // leaves the cutover half-applied.
    defaultValue: true,
    // ENG-9831: NO LONGER read client-side. It was — via usePublicBooleanFlag in
    // sidebar.tsx (twice) and top-nav.tsx — and that is precisely what this
    // slice removed: a client hook starts at its default and corrects after an
    // async GET /flags, so the console painted the sidebar and then replaced the
    // WHOLE dashboard chrome after hydration. The ENG-8858/ENG-9227 flash at
    // layout scale. It is now resolved once server-side in (dashboard)/
    // layout.tsx and passed to both shells as a required prop.
    //
    // `public` MUST STAY ANYWAY — the same trap documented on ninjafy-brand.
    // getPublicBooleanFlagServer resolves against the very same public-filtered
    // `GET /flags` the browser uses (routes/flags.ts filters to
    // listPublicFlagKeys()). Drop `public` and the key is simply absent from the
    // map, the server helper falls through to its default forever, and every org
    // silently gets whatever that default happens to be with no error anywhere.
    // `public` means "readable through the public map", not "read by a browser".
    public: true,
  },
  {
    key: 'ninjafy-brand',
    description:
      'Present the product under the Ninjafy brand instead of Augmented Team (ENG-8250). This ' +
      'is the UMBRELLA brand gate, not a one-off nav toggle: every subsequent rebrand surface ' +
      '(page titles, email templates, marketing-facing copy) reads THIS key rather than adding ' +
      'its own flag, so the whole rebrand keeps a single kill switch. First surface is the ' +
      'left-hand nav wordmark — ON replaces the human+robot mark and the "augmented.team" text ' +
      'with italic lowercase "ninjafy"; OFF renders exactly what shipped before. Scope is ' +
      'the USER-FACING BRAND PRESENTATION — text AND VISUAL THEME. ENG-8858 widened this from ' +
      'text alone: the Ninjafy palette is gated here too, because a colour theme is not text. ' +
      'It stays ONE key rather than gaining a `ninjafy-theme` sibling, so a half-branded state — ' +
      'ninjafy wordmark over Augmented green — is unreachable and the rebrand keeps a single ' +
      'kill switch. It must never gate a code identifier, package name, env var ' +
      'or CLI name. EXISTING identifiers stay `Augmented`/`agt` per the CLAUDE.md naming ' +
      'contract, which protects them from premature renaming — the deep rename is workstream C ' +
      'of docs/runbooks/rebrand-ninjafy-migration.md and is out of scope here. That contract ' +
      'does NOT require NEW code to carry the old brand: artefacts created specifically for the ' +
      'rebrand take Ninjafy naming (ENG-8078 decision 9; see the CLAUDE.md rebrand carve-out). ' +
      'Either way it is a naming rule, not a flag concern — this key gates presentation, ' +
      'never an identifier. Set the stage-wide default to flip a whole environment, or add a ' +
      'feature_flag_overrides row to pilot a subset while everyone else still sees Augmented. ' +
      'Overrides resolve at FOUR grains, most specific first — agent, team, host, then ' +
      'org-wide (evaluate.ts:79-81); organization_id is always required, so there is no global ' +
      'override. Org and team are the useful pilot grains for THIS flag: there is now exactly ' +
      'ONE consumer — (dashboard)/layout.tsx via getPublicBooleanFlagServer — evaluating in ' +
      'the active-org cookie scope, so a host- or agent-scoped row is accepted by the table ' +
      'but never reached by the console. It resolves the key ONCE per request and passes the ' +
      'boolean down to sidebar.tsx and top-nav.tsx as a required prop. ' +
      'Resolution is SERVER-side deliberately: the client hook starts at its default and ' +
      'updates after an async GET /flags, so anything gated on it paints Augmented and then ' +
      'repaints. ENG-8858 moved the PALETTE for that reason (a full-page colour flash); ' +
      'ENG-9227 moved the WORDMARK, which had been the last surface still swapping after ' +
      'hydration. A consequence worth knowing: /login and the marketing routes stay on ' +
      'the Augmented palette whatever an org sets, because fetchPublicFlags returns null ' +
      'without a verified session and a pre-auth page has neither a session nor an active ' +
      'org — a PER-ORG flag structurally cannot brand one. ENG-9830 CORRECTS what used to ' +
      'be written here ("those surfaces follow only when the stage-wide default flips"): ' +
      'they do not follow it at all. Two reasons. The value returned on a null fetch is the ' +
      'CALL SITE’s passed argument, not this registry default; and six such pages ' +
      '(/login, /reset-password, /auth/confirm, the pre-redeem member-signin gate and the ' +
      'two public Slack-install terminal pages) do not mount <BrandStamp/> AT ALL — ' +
      'deliberately, enumerated with per-page reasons in eng-9266-brand-stamp-coverage.test.ts. ' +
      'They become branded when the flag is REMOVED at cutover step 2 and the palette moves ' +
      'into bare :root, with no read left to gate it. ' +
      'NO LONGER DARK: ENG-9830 cutover step 1 flipped the default to true, so an org with ' +
      'no override now sees Ninjafy. The key survives only until cutover step 2 deletes it.',
    flagType: 'boolean',
    // ENG-9830 (cutover step 1): flipped `false` -> `true`. The comment this
    // replaces argued `false` was the fail-closed direction — "never leak an
    // UNANNOUNCED rebrand to every customer at once". That was correct for as
    // long as the rebrand was unannounced. It is not any more: Ninjafy is the
    // live, announced brand (ENG-9613 reconciled the console onto the master
    // assets while the flag was already ON in production), so `false` now points
    // at the brand we no longer ship. A flags outage that reverted a customer to
    // Augmented would be the incident, not the safe harbour.
    //
    // The old rationale is recorded rather than deleted because it is a correct
    // argument whose PREMISE expired — reading the change without it looks like
    // someone overriding a deliberate fail-closed default.
    //
    // NOTE this value is only consulted by the API evaluator when building the
    // `GET /flags` map for an org with no override. It is NOT the fallback the
    // webapp uses on an outage: `getPublicBooleanFlagServer(key, defaultValue)`
    // returns its own PASSED argument when the fetch fails, so the call sites in
    // (dashboard)/layout.tsx and components/brand-stamp.tsx carry `true` too.
    // Flipping only one of the two leaves the cutover half-applied.
    defaultValue: true,
    // ENG-9227: there are NO client readers of this key left. (dashboard)/
    // layout.tsx resolves it ONCE server-side via getPublicBooleanFlagServer and
    // passes the boolean down to sidebar.tsx and top-nav.tsx as a required prop.
    // "ONCE" is about the chrome, not the key: components/brand-stamp.tsx is a
    // SECOND, independent server read of `ninjafy-brand` (CodeRabbit, #5385).
    // Both are listed in the note above because both carry an outage fallback
    // that has to move together — reading this paragraph alone would leave you
    // updating one of the two.
    // ENG-8858 did that for the THEME; ENG-9227 finished the job for the
    // WORDMARK, which had kept usePublicBooleanFlag and so kept swapping
    // "augmented.team" to "ninjafy" after hydration.
    //
    // `public` MUST STAY ANYWAY, and this is the trap. It is tempting to read
    // "no client readers" as "no longer needs to be public" — but
    // getPublicBooleanFlagServer resolves against the very same `GET /flags`
    // endpoint the browser uses, and that route filters to listPublicFlagKeys()
    // (routes/flags.ts:72-75). A non-public key is simply absent from the map,
    // so the server helper would fall through to its default and return `false`
    // FOREVER — the brand quietly reverting to Augmented for every org, with no
    // error anywhere. `public` here means "readable through the public map",
    // not "read by a browser".
    //
    // Unlike onboarding-msteams-channel above there is no wrong-org hazard — the
    // single reader evaluates inside the active-org cookie's scope, which is
    // exactly the org whose brand should be shown.
    public: true,
  },
  {
    key: 'ninjafy-logo',
    description:
      'Which Ninjafy logo TREATMENT the console chrome renders (ENG-9365). This is a ' +
      'SUB-SELECTION under ninjafy-brand, not a second brand gate: it is consulted only ' +
      'once ninjafy-brand has already resolved ON, so with the brand off the value is inert ' +
      'and the half-branded state that flag\'s description calls unreachable stays ' +
      'unreachable. ninjafy-brand remains the single kill switch for the rebrand. ' +
      'It existed so candidate logo treatments could be put in front of a human and ' +
      'compared in the running console, rather than judged from a static export. That ' +
      'comparison has happened: ENG-9830 cutover step 1 chose TWO-TONE, so that is now ' +
      'the default and an unset flag renders it. NOTE this reverses the property this ' +
      'description used to claim — an unset flag was a no-op while the default was ' +
      'lockup (what ENG-9298 shipped), and that is deliberately given up at cutover: ' +
      'the default MOVING is the change. The key survives only until cutover step 2 ' +
      'hardcodes the chosen treatment and deletes it. ' +
      'lockup = mark and wordmark together, both in the brand blue — what ENG-9298 ' +
      'shipped. two-tone = the mark in brand blue with the ' +
      'wordmark in the neutral text colour (the treatment of the blue-icon/black-wordmark ' +
      'artwork); the chosen one. wordmark = the word alone, no mark. ' +
      'Every treatment is composed from the ENG-9298 geometry — ENG-9312 pins that the ' +
      'lockup\'s blue path IS the mark subpath followed by the wordmark subpaths, byte for ' +
      'byte — so a new treatment is a composition, never a fresh trace, and the three ' +
      'drawings cannot drift apart. ' +
      'Surfaces are the dashboard sidebar and the top nav. The top nav shows the WORDMARK ' +
      'portion at every width whatever the treatment, because ENG-9342 decided that ' +
      'deliberately (a full lockup at the header\'s icon height crowds the team switcher on ' +
      'a phone) — so a treatment changes the word\'s colour there, never whether a mark ' +
      'appears. The admin sidebar (ENG-9323) is NOT wired to this key and always renders the ' +
      'mark; widening it is a separate decision. ' +
      'Resolved SERVER-SIDE in (dashboard)/layout.tsx via getPublicEnumFlagServer, beside ' +
      'ninjafy-brand and in the same single GET /flags round trip, then passed down as a ' +
      'required prop. That is not a style preference: the client hook starts at its default ' +
      'and updates after an async fetch, so a client read would paint one logo and swap it ' +
      'after hydration — the exact flash ENG-9227 removed from the wordmark and ENG-8858 ' +
      'removed from the palette. ' +
      'Org and team are the useful pilot grains, as for ninjafy-brand: the single consumer ' +
      'evaluates in the active-org cookie scope, so a host- or agent-scoped override row is ' +
      'accepted by the table but never reached by the console.',
    flagType: 'enum',
    allowedValues: ['lockup', 'two-tone', 'wordmark'],
    // ENG-9830 (cutover step 1): `lockup` -> `two-tone`. The treatments existed
    // so they could be compared in the running console rather than judged from a
    // static export (ENG-9365); that comparison has now happened and `two-tone`
    // — the mark in brand blue with the wordmark in the neutral text colour — is
    // the chosen treatment. It becomes the hardcoded one when this key is
    // removed at cutover step 2.
    //
    // The rule this value obeys is unchanged: this flag has no "off" direction,
    // so fail-safe means "the treatment currently in production", not the
    // narrowest one. A flag-DB read error, an archived row, or a stored value
    // outside allowedValues all resolve here (evaluate.ts normalizeFlagValue),
    // which is what makes an experimental value impossible to strand. Only which
    // treatment holds that position has moved.
    //
    // Same split as ninjafy-brand above: this governs the API evaluation path
    // only. The webapp's outage fallback is the argument passed at the
    // (dashboard)/layout.tsx call site, which carries 'two-tone' to match.
    defaultValue: 'two-tone',
    // Same trap as ninjafy-brand, and it bites harder here because there are no
    // client readers of this key AT ALL — it is server-resolved by construction.
    // `public` does NOT mean "read by a browser"; it means "readable through the
    // public map". getPublicEnumFlagServer resolves against the same GET /flags
    // endpoint the browser uses, and that route filters to listPublicFlagKeys()
    // (routes/flags.ts). Drop `public` and the key is simply absent from the map,
    // so the helper falls through to the fallback passed at that call site and
    // returns it FOREVER — the dropdown appearing to do nothing, with no error
    // raised anywhere. Named as the call site rather than as a literal on
    // purpose: this comment said `lockup` for exactly as long as `lockup` was
    // the answer, and ENG-9830 flipping the winner is what made it a lie.
    public: true,
  },
  {
    key: 'agent-recruit',
    description:
      'The `recruit_agent` MCP tool (ENG-8159): lets an ordinary managed agent PROPOSE a new ' +
      'teammate in its own org. The proposal is filed under the `agent.recruit` approval verb ' +
      'and routed to an org owner as a server-rendered card, so this gates the ASK — nothing is ' +
      'created until a human taps Approve. Deliberately distinct from augmented-support-writes, ' +
      'which gates the concierge\'s own create_agent surface: the two requesters are separately ' +
      'controllable because an org may well want its support concierge to propose agents without ' +
      'giving every agent in the fleet the same power. Ships dark.',
    flagType: 'boolean',
    // Declared safe value is `false`. The write is human-gated either way, but an
    // unbounded ask is still an unbounded demand on an owner's attention, so the
    // fail-closed direction on a flag-DB fault is "an agent cannot ask".
    defaultValue: false,
    // Turning this on lets agents originate proposals that create customer
    // infrastructure once approved — same posture as augmented-support-writes.
    sensitive: true,
  },
  {
    key: 'github-broker-credentials',
    description:
      'Deliver GitHub App installation tokens to `git` and the `gh` CLI through the broker ' +
      'credential endpoint at USE time, instead of materializing them into the agent process ' +
      'environment at spawn (ENG-8344). A GitHub App installation token has a ~1h TTL, so the ' +
      'spawn-frozen GITHUB_ACCESS_TOKEN / GITHUB_TOKEN pair rotates hourly; the wrapper sources ' +
      '`.env.integrations` exactly once, so the only way a running agent sees the new value is a ' +
      'full session respawn (measured: 6 agents, ~144 kills/day). ENG-8343 stopped those kills ' +
      'landing mid-turn; this removes them. ' +
      'ON: `POST /host/agent-integrations` stops minting the installation token into the payload ' +
      'for auth_type=github_app rows (so computeIntegrationsHash stops seeing a rotating value) ' +
      'and marks the row credential_delivery=broker; the claudecode adapter then writes a git ' +
      'credential helper + a `gh` shim that fetch a fresh token per use from ' +
      'POST /host/agent-integrations/github/credential (ENG-8264 name addressing). ' +
      'OFF (default): byte-for-byte today\'s behaviour - the token is minted into the payload and ' +
      'materialized as GITHUB_ACCESS_TOKEN / GITHUB_TOKEN, no helper, no shim. ' +
      'NOTE the scope of the security claim: this removes the token from the agent\'s ENVIRONMENT, ' +
      'which is what stops the respawns. It does NOT make the token unreachable by a determined ' +
      'agent - the agent can invoke the helper itself. Strictly better than today, not a sandbox. ' +
      'Evaluated API-side only, per agent, in POST /host/agent-integrations; no host-side envVar. ' +
      'The host does not read this flag - it keys off the resulting credential_delivery marker on ' +
      'the row - so there is deliberately no host override that could put the token back on its ' +
      'own. Roll back from the flag.',
    flagType: 'boolean',
    // Declared safe value is `false` = today's env delivery. Fail-safe direction:
    // a flag-DB read error must never strip `git`/`gh` credentials off a live
    // agent, which is what the ON path does when its host has no helper writer.
    defaultValue: false,
    // Flipping ON changes how every GitHub App agent on the scope authenticates
    // `git push` / `gh`. A bad flip takes those away, so it is a deliberate,
    // confirmed action (ADR-0022 §4).
    sensitive: true,
    // Set even though the flag is evaluated centrally, because `since` is a
    // REACH threshold ("a host this old has no consumer code for the flag") and
    // that is exactly the hazard here: the host does not read the flag, but it
    // does need the claudecode adapter's helper/shim writer and the wrapper PATH
    // prepend. On a host too old for those, the API would stop shipping the
    // token and NOTHING would replace it. Without `since` the flip-reach modal
    // reports FULL reach and hides that.
    //
    // DELIBERATE UNREACHABLE PLACEHOLDER - NOT A REAL THRESHOLD YET.
    // The sibling entries in this registry (`slack-hot-thread-guard` 0.28.339,
    // `slack-scheduled-channel-guard` 0.28.421) are BISECTED from published npm
    // artifacts, which is only possible AFTER this merges and publishes. A
    // pre-merge guess cannot be right, and the two directions are not
    // symmetric:
    //   - too HIGH  -> hosts classify `stale`, the modal over-warns. Annoying.
    //   - too LOW   -> hosts classify `honors` while carrying NO helper writer,
    //                  the modal promises full reach, and a flip strips `git`/
    //                  `gh` credentials with nothing replacing them. Outage.
    // So this is pinned ABOVE every published release on purpose: it classifies
    // EVERY host as `stale`, so the modal reports no reach rather than reach it
    // has no basis for. That is the intended pre-bisect state.
    //
    // BUT `since` IS ADVISORY, NOT A GUARD. It is read in exactly one place -
    // `computeFlagReach` behind GET /flags/admin/reach?key=…, which renders the flip-reach
    // modal. `evaluate.ts` never reads it and neither does the override write
    // path. The placeholder therefore changes what an operator is TOLD; it does
    // not stop a flip on an under-`since` host. What actually holds the line
    // here is `defaultValue: false` plus `sensitive: true` plus the runbook.
    //
    // BEFORE THIS FLAG IS FLIPPED ANYWHERE: bisect the published dist for the
    // first agt-cli whose claudecode adapter writes agt-bin/ and whose wrapper
    // prepends it, then replace this with that exact version. The rollout
    // runbook gates on it. Leaving the placeholder in place is safe (nothing
    // flips); shipping a guessed number is not.
    since: '0.29.0',
  },
  {
    key: 'help-kb-auto-grant',
    description:
      'Auto-grant the `augmented-help-kb` integration at ORG scope when an organization is ' +
      'provisioned (ENG-8542), so every agent in that org gets product-doc search without a ' +
      'per-agent operator action. One organization_integrations row reaches every agent in the ' +
      'org: POST /host/agent-integrations merges org -> team -> agent and buildMcpJson\'s ' +
      'predicate reads that merged set with no scope filter. ' +
      'ON: the two org-provisioning call sites write the row (status=active, created_by=the org ' +
      'owner). OFF (default): they write nothing - byte-for-byte today\'s behaviour. ' +
      'STAGED ROLLOUT IS THE POINT. ENG-8332 argues against widening distribution of the help KB ' +
      'until two things close, because distributing a corpus with wrong articles "converts one ' +
      'agent\'s wrong answer into everyone\'s": (1) a live 200 from GET /host/kb/search as the ' +
      'anon role has still never been observed - ENG-7260 was closed on remediation evidence, not ' +
      'on a live call, and the deploy seed log is SERVICE-ROLE evidence that says nothing about ' +
      'the anon grant; (2) the `site-structure` article is unverified against the current IA. ' +
      'Turning this on for ONE org and watching that org\'s help-kb connectivity-probe verdicts ' +
      '(ENG-8332 added a real mcp_stdio probe that calls search_knowledge_base) is the cheap ' +
      'positive control for (1) - an ENG-7260-class 500 then reports as `down` per agent instead ' +
      'of as silence. Widen only after it comes back healthy. ' +
      'REVOKING IS DELETING THE ROW, not flipping this off: the flag gates GRANTING, so an ' +
      'already-granted org keeps its row when the flag goes off. ' +
      'Evaluated API-side only, per org, inside grantHelpKbToOrg; no host-side envVar and ' +
      'deliberately no sst.config.ts entry (a literal default there would pin it - ENG-8303).',
    flagType: 'boolean',
    // Declared safe value is `false` = grant nothing. A flag-store outage must
    // never auto-attach an integration nobody asked for, and both ENG-8332
    // prerequisites above are still open.
    defaultValue: false,
  },
  {
    key: 'host-right-sizing',
    description:
      'ADR-0072. Gates the HostRightSizer cron, which evaluates every managed EC2 host against 7 days ' +
      'of memory and CPU and (at enforce) resizes it one rung inside the host\'s own maintenance ' +
      'window. off = the cron returns immediately: nothing is read, nothing is written. shadow = ' +
      'evaluate, persist a verdict row per host, and post the weekly summary — NO resize, and no actor ' +
      'code runs. enforce = as shadow, plus enqueue resizes. AS OF PHASE 1 (ENG-9324) THERE IS NO ' +
      'ACTOR, SO `enforce` BEHAVES EXACTLY LIKE `shadow`: selecting it records the mode on each ' +
      'verdict row and changes nothing else, and no host is resized. This note comes out when the ' +
      'Phase 3 actor lands; until then treat enforce as shadow-with-a-label, not as an armed fleet. ' +
      'Ships dark: this mechanism ends in a ' +
      'stop/start of a customer production host, so an unreachable flag store must resolve to NOT ' +
      'acting. TWO BOUNDS WORTH KNOWING BEFORE FLIPPING. (1) off is not a stop button for in-flight ' +
      'work — HostResizeDispatcher reads only ec2_pending_instance_type and knows nothing about this ' +
      'flag, so flipping to off stops new enqueues and lets every already-enqueued host complete its ' +
      'stop/modify/start; combined with the ~5-minute eval cache the true blast unit is one tick of ' +
      'open windows. A real kill switch has to live inside the dispatcher. (2) BYO hosts (ADR-0028) ' +
      'are EXCLUDED in Phase 1, not merely clamped to shadow: their instances live in the customer AWS ' +
      'account while the cron authenticates only with the platform provisioner role, so their metrics ' +
      'read as empty and every evaluation would land as unknown. ADR-0072 §9 intends them to be ' +
      'shadow-only ("we recommend; they resize") and making that real needs the per-host ' +
      'cross_account_role_arn resolved - its own slice. Pool hosts (ADR-0042) are excluded too, while ' +
      'host_kind is unset in prod. Both are eligibility predicates applied by the cron, documented ' +
      'here so the admin page showing enforce for such an org is not read as behaviour.',
    flagType: 'enum',
    allowedValues: ['off', 'shadow', 'enforce'],
    // Ships dark at `off`, NOT `shadow`. Diverging deliberately from
    // slack-scheduled-channel-guard, whose shadow is a free local log line:
    // THIS shadow spends a fleet-wide CloudWatch GetMetricData sweep per tick
    // and writes a row per host, so defaulting to shadow would newly bill and
    // newly write for every org that never opted in. shadow is the per-canary
    // opt-in; enforce is the audited flip after the Phase 2 review.
    defaultValue: 'off',
    // Evaluated centrally, in the API cron — there is no host-side reader, so
    // no `since` and no dependence on fleet agt-cli convergence.
    // NOTE: deliberately NO envVar. ADR-0072 §9 wants this gate scoped per
    // org/host, and an envVar would be highest-precedence (ADR-0022) — a
    // literal in sst.config.ts would pin every stage and make the admin page a
    // control that does nothing, which is the ENG-8303 class exactly.
    // enforce arms automated downtime on a customer's production host.
    sensitive: true,
  },
  {
    key: 'live-meeting-advisor',
    description:
      'Live meeting advising in Ninja Notes (ENG-9381). When ON for an org, a person recording ' +
      'a meeting on the Mac may arm an agent to watch it in flight: audio chunks stream to ' +
      'POST /notes/live/:id/chunks, a server-side Haiku vet decides moment by moment whether ' +
      'to escalate, and escalations reach the agent in a server-minted chat thread. ' +
      'Gates the CAPABILITY, not a surface: the refusal is in startLiveAdvisorSession, so a ' +
      'client that ignores the flag still cannot open a session, and GET /notes/destinations ' +
      'reports the same answer as advisor_enabled so the app can skip the arming modal ' +
      'entirely. Boolean gate; ships dark. ' +
      'OFF NEVER COSTS THE RECORDING. An unentitled org — or a flag read that fails — costs ' +
      'the advisor only; the microphone still starts and the note is still transcribed and ' +
      'delivered. That direction is deliberate: the user pressed Record and a recording is ' +
      'what they are owed, while arming on a failed read would spend money per minute on a ' +
      'session nobody confirmed. ' +
      'Deliberately NO envVar. This is an org-scoped gate, and an envVar is highest-precedence ' +
      'per ADR-0022 — a literal in sst.config.ts would pin every stage and make the admin ' +
      'control do nothing, the ENG-8303 class exactly.',
    flagType: 'boolean',
    defaultValue: false,
    // Turning it ON arms a per-minute-metered capability AND starts streaming
    // meeting content to a server-side vet. Both halves are the deliberate,
    // audited decision ADR-0022 §4 reserves confirmation for — same treatment as
    // agent-hours-billing-enabled.
    sensitive: true,
  },
  {
    key: 'cloud-speech-to-text',
    description:
      'Cloud transcription in Ninja Notes (ENG-9913). When ON for an org, a person recording a ' +
      'meeting on the Mac may choose to have each capture window transcribed by xAI instead of ' +
      'on-device: the window is POSTed to /notes/transcribe, proxied to xAI with the platform ' +
      'key, metered as grok-voice/transcription per audio_hour, and the text comes back with no ' +
      'speaker labels. ' +
      'THIS IS OUR ROLLOUT GATE AND IT IS THE THIRD KEY, NOT THE FIRST. Three things must all ' +
      'be true before a window leaves the Mac: the org holds the grok-voice premium opt-in ' +
      '(authorisation, resolveCloudSttEntitlement), the user has elected it per-install ' +
      '(consent, CaptureSettings.cloudTranscriptionEnabled), and this flag is on (our own ' +
      'readiness). Without this flag the first two alone were sufficient, which is why it ' +
      'exists: resolveCloudSttEntitlement reports enabled for every org that ever bought Voice ' +
      'Chat, so the route would go live for all of them the moment it deployed. ' +
      'It is what finally lets the server emit `not_available` — CloudTranscriptionRefusal.' +
      'notRolledOut has shipped since ENG-9869 carrying the sentence "Cloud transcription is ' +
      'not available for your organisation yet" with nothing able to produce it. ' +
      'Boolean gate; ships dark. ' +
      'OFF NEVER COSTS THE RECORDING. An org without the flag — or a flag read that fails — ' +
      'transcribes on-device exactly as before; the microphone still starts and the note is ' +
      'still transcribed and delivered. Same direction as live-meeting-advisor and for the same ' +
      'reason: the user pressed Record and a recording is what they are owed. It is also what ' +
      'makes rollback cheap, because flipping this off mid-flight degrades in-progress meetings ' +
      'to on-device rather than breaking them. ' +
      'Deliberately NO envVar. This is an org-scoped gate, and an envVar is highest-precedence ' +
      'per ADR-0022 — a literal in sst.config.ts would pin every stage and make the admin ' +
      'control do nothing, the ENG-8303 class exactly. ' +
      'No `since`: the reader is the API route, not a host-side agt-cli consumer, so this does ' +
      'not depend on fleet convergence. The Mac client is a separate fleet that reports no ' +
      'version at all, which is precisely why the gate has to live server-side.',
    flagType: 'boolean',
    defaultValue: false,
    // Turning it ON starts sending meeting audio — INCLUDING the other side of
    // the call, whose participants have not been asked — to a third-party
    // vendor, and arms a per-audio-hour meter against the org's shared
    // grok-voice budget. Either half alone would warrant the confirm ADR-0022
    // §4 reserves for deliberate, audited flips.
    sensitive: true,
  },
  {
    key: 'first-party-token-refusal',
    description:
      'ENG-8972 AC4 / ADR-0076 decision 2 — whether `resolveUserId` (packages/api/src/middleware/auth.ts) ' +
      'REFUSES a bearer token carrying a first-party marker that is foreign to the host-exchange lane. ' +
      'SUPABASE_JWT_SECRET is symmetric and signs twelve distinct token families; the generic Bearer door ' +
      'has always admitted any of them that mints a uuid-shaped `sub`, so a token minted for one purpose ' +
      'authenticates as a user for every purpose. off = the capture never runs (no log, no refusal). ' +
      'shadow = record the population an enforcement flip would change, on BOTH admitting lanes ' +
      '(lane=gotrue, lane=sub-claim), and refuse nothing — this is exactly the behaviour compiled into ' +
      'prod today. enforce = return null with reason=foreign-first-party-marker, above the strategy split, ' +
      'so the token reaches neither GoTrue nor the sub-claim lane. ' +
      'DEFAULT IS `shadow`, NOT `off`, and the divergence is deliberate — the shadow lines have been live ' +
      'in prod since 68863974b, and they are the soak instrument. Defaulting to `off` would silently ' +
      'switch off the measurement this flag exists to act on: the channel-quarantine-mode landmine ' +
      '(ENG-6252) in its exact shape. ' +
      'A flag-read failure degrades to `shadow` for the same reason it fails open elsewhere on this path: ' +
      'this is the platform front door, and a `feature_flags` blip must not start refusing the fleet. ' +
      'Deliberately NO envVar. The consumer is the API Lambda, whose environment block is at the AWS 4KB ' +
      'cap (ENG-7972), and an envVar is highest-precedence per ADR-0022 — a literal in sst.config.ts would ' +
      'pin every stage and make the admin control do nothing (ENG-8303). The flag value IS the kill ' +
      'switch: a flip back to shadow takes effect within the ~60s evaluation cache, with no deploy.',
    flagType: 'enum',
    allowedValues: ['off', 'shadow', 'enforce'],
    // See the description: `shadow` is what prod compiles today, so it is the
    // only default that ships this flag as a no-op.
    defaultValue: 'shadow',
    // enforce changes the admission rule on the platform's front door. This is
    // precisely the deliberate, audited flip ADR-0022 §4 reserves confirmation
    // for, and it stays `sensitive` — but NOT for the reason first written here.
    // That read "the families it starts refusing include mcp-proxy, one of the
    // fleet's dominant callers", and ENG-9895 measured otherwise: mcp-proxy
    // verifies its own token on a route carrying no `authMiddleware`, so it is a
    // dominant caller of a different door and cannot be in the refused set. The
    // measured population at that door is ZERO over 298,839,353 prod log records
    // / 14 days, and the impersonation families are absent too — one
    // structurally (`X-Agent-Impersonation` header, own verifier), one by
    // measurement (307 requests through that door, zero lines). What keeps this
    // `sensitive` is therefore NOT an unresolved residue: it is that `enforce`
    // changes an admission rule on the front door, and that the predicate is
    // structural, so a FUTURE family 13 is born refusable without an edit here.
    // That is exactly the deliberate, audited flip ADR-0022 §4 reserves
    // confirmation for. docs/research/eng-9895-prod-shadow-measurement.md
    sensitive: true,
  },
  {
    key: 'artefact-export-jobs-serve',
    description:
      'ADR-0077 / ENG-9729 — the MASTER switch for the async artefact-export lane. ' +
      'ON (default) = a route may resolve an export request to an artefact_export_jobs ' +
      'row: attach to one that is already rendering, or hand back the bytes of one that ' +
      'has completed. OFF = the job lane is invisible and every export renders ' +
      'synchronously, which is the pre-ADR-0077 behaviour. ' +
      'DEFAULT true DOES NOT MEAN ASYNC IS ON. Nothing CREATES a job unless ' +
      'artefact-export-jobs-start is also on, so with the shipped defaults this flag ' +
      'governs an empty table and no caller can tell it exists. ' +
      'WHY THE LANE IS TWO FLAGS AND NOT ONE. Disarming the start lever while renders are ' +
      'in flight must not strand the callers holding their job ids: those renders keep ' +
      'running (a Lambda invocation nobody awaits still runs to completion), and their ' +
      'bytes land in the private bucket regardless. A single flag would take the only door ' +
      'to them away at the moment it was most needed. So "stop creating new jobs" and ' +
      '"keep serving the ones that exist" are separate, and this is the second one. ' +
      'READ-ONLY BY CONSTRUCTION: with start off, the route looks up a REUSABLE row and ' +
      'nothing more — it never inserts, never requeues a terminal row, and never dispatches ' +
      'a renderer. A requeue mints an attempt and fires an invoke, so a serve lever that ' +
      'requeued would start new renders under a disarmed start lever, which is the exact ' +
      'thing disarming it was meant to stop. ' +
      'Evaluated server-side per request (org-scoped), so a flip applies within the ' +
      'evaluation cache TTL with no deploy and no host update. ' +
      'Deliberately NO envVar: the consumer is the API Lambda, whose environment block is ' +
      'at the AWS 4KB cap (ENG-7972), and a literal in sst.config.ts would pin every stage ' +
      'and make the admin control inert (ENG-8303).',
    flagType: 'boolean',
    // ON is the no-op default here, which looks like the wrong direction until
    // you read it with the start lever: serving an empty table is
    // indistinguishable from not serving at all. Shipping it OFF would instead
    // mean that arming async later takes TWO flips, and that the second one is
    // the easy one to forget — leaving a fleet that creates jobs it refuses to
    // serve, i.e. every slow export failing while the flag page reads "async: on".
    defaultValue: true,
  },
  {
    key: 'artefact-export-jobs-start',
    description:
      'ADR-0077 / ENG-9729 — whether a route may CREATE an async artefact-export job: ' +
      'insert or requeue an artefact_export_jobs row, dispatch the worker, and answer a ' +
      'caller with a job id instead of bytes when the ~20s grace window is exceeded. ' +
      'OFF (default) = today\'s synchronous render on every export. ' +
      'THIS IS THE ROLLOUT GATE, AND IT IS ORDERED AGAINST A CLIENT, NOT A DEPLOY. ' +
      'The agent caller is `artefacts_download`, which ships inside the agt CLI to a ' +
      'permanently mixed-version fleet — hosts self-update overnight and some never do. ' +
      'Arming this against a CLI that cannot read a job-id body does not degrade an ' +
      'export, it BREAKS one: the pre-ENG-9729 tool does Buffer.from(data.base64) with no ' +
      'branch for any other shape, so a job id arrives as a TypeError deep in file-write ' +
      'logic. That is strictly worse than the 503 + Retry-After it would replace, which ' +
      'the tool already understands. ' +
      'A published CLI version is NOT evidence of convergence — measure hosts.agt_version ' +
      'across recently-seen hosts first. The procedure is docs/runbooks/async-export-rollout.md; ' +
      'arm this only after running it. ' +
      'Turning it back OFF stops new jobs and leaves artefact-export-jobs-serve delivering ' +
      'the in-flight ones, which is why the two are separate. ' +
      'Org-scoped, so it can be armed for one tenant at a time. Deliberately NO envVar ' +
      '(ENG-7972 4KB cap, ENG-8303 pin).',
    flagType: 'boolean',
    // Dark. The client half of this change publishes with the same merge, and
    // the fleet has not converged on it at merge time by construction — see the
    // runbook. Arming is an operator action taken against a measurement.
    defaultValue: false,
    // Arming this changes the RESPONSE SHAPE of a route whose callers include a
    // binary we do not control the version of. ADR-0022 section 4 reserves
    // confirmation for exactly this: a flip that a single unguarded write should
    // not be able to make.
    sensitive: true,
    // `since` USED SLIGHTLY OFF-LABEL, AND THAT IS THE POINT.
    //
    // The field's docblock says it records the CLI whose build first shipped
    // this flag's HOST-SIDE READER, and this flag has none — it is evaluated
    // server-side in the export route, so every host "honors" a flip the instant
    // it is made. Read literally, `since` should therefore be undefined.
    //
    // Undefined is the dangerous answer. `classifyHostReach` returns `honors`
    // for every host when `since` is unset, so the flip-reach modal — the screen
    // an operator is looking at AT THE MOMENT OF THE FLIP — would report full
    // reach for the one flag whose entire purpose is "do not flip this until you
    // have measured the fleet". It would be telling the truth about the
    // mechanism (the flip does reach every host immediately) while giving
    // exactly the wrong answer to the question being asked (can those hosts
    // survive it). The dependency is real; it is on the CLI's ability to SURVIVE
    // the flag, not to READ it, and the modal cannot tell those apart.
    //
    // VERIFIED AGAINST THE PUBLISHED TARBALL, NOT THE MERGE (2026-09-03).
    // `npm pack @integrity-labs/agt-cli@<version>`, then grep the extracted
    // `package/dist/mcp/index.js`. That file is present in BOTH builds and both
    // carry `exportArtefactToFile`, so the absences below are an absence of the
    // CODE rather than of the file:
    //
    //   0.28.773 — `classifyExportResponse`, `pendingExportText` and
    //              `artefact-export-response` all ABSENT.
    //   0.28.774 — all three PRESENT.
    //
    // Hence the pin. Do NOT read a replacement off `apps/cli/package.json`, and
    // do NOT trust a version produced by re-running a failed publish job
    // (ENG-9647: a re-run rebuilds the ORIGINAL tree under a HIGHER number, so
    // the version string would claim a capability the bytes do not carry). The
    // publish workflow's output only identifies a CANDIDATE; the tarball is what
    // proves it, so re-run the same two greps if this pin ever has to move.
    //
    // AND `since` IS ADVISORY, NOT A GUARD. It is read in exactly one place —
    // the flip-reach modal. Nothing in the evaluation or override-write path
    // consults it, so this changes what an operator is TOLD and does not stop a
    // flip on an under-`since` host. What holds the line is `defaultValue:
    // false` + `sensitive: true` + the runbook. It is likewise excluded from
    // `projectDefinition` in schema-version.ts, so moving it does not roll
    // FLAGS_SCHEMA_VERSION for a fleet that cannot read it anyway.
    since: '0.28.774',
  },
  {
    key: 'artefact-export-public-jobs',
    description:
      'ADR-0077 / ENG-9730 - whether the PUBLIC Download button may create an async ' +
      'artefact-export job. OFF (default) = today\'s synchronous render on the public ' +
      'route. ' +
      'SEPARATE FROM artefact-export-jobs-start ON PURPOSE, and the reason is the whole ' +
      'point of the flag. That one is ordered against the agt CLI fleet: a pre-ENG-9729 ' +
      'agent cannot read a job-id body, so arming it early BREAKS an export. This lane\'s ' +
      'client is a BROWSER, which has no version to converge on - the waiting page is ' +
      'generated fresh by the server on every request. Sharing one lever would hold the ' +
      'customer-facing half hostage to an agent-side constraint that does not apply to it. ' +
      'Note what this flag does NOT gate: a navigation always gets HTML rather than a JSON ' +
      'error body. That is the fix for a live defect (a renderer outage used to navigate a ' +
      'reader off their document to a raw error) and is not conditional on async. ' +
      'Reading artefact-export-jobs-serve as an AND still applies - the master switch ' +
      'turns off both lanes. ' +
      'ONE CHECK BEFORE ARMING, and it is not fleet convergence: the lane assumes a ' +
      'browser NAVIGATES to the waiting page rather than saving it, which holds because ' +
      'the cross-origin download attribute is ignored. Documented for Chrome/Firefox/ ' +
      'WebKit, but unverified on a real Safari - and a phone is where a contract link ' +
      'gets opened. Run the desktop+iOS Safari smoke check in the runbook first. ' +
      'Org-scoped, so it can be armed for one tenant at a time. Deliberately NO envVar ' +
      '(ENG-7972 4KB cap, ENG-8303 pin).',
    flagType: 'boolean',
    // Dark, like every other lane in this ADR. What it changes is real - a slow
    // render stops completing in place and starts arriving via a waiting page -
    // so it wants a deliberate flip per tenant, not a deploy-time default.
    defaultValue: false,
    // Deliberately NOT sensitive, and the contrast with artefact-export-jobs-start
    // is the argument. That flag is `sensitive` because arming it can hand a job
    // id to a binary that answers with a TypeError - an unguarded write breaks a
    // caller we do not control. Here the worst case is a reader seeing an
    // explanatory page a few seconds sooner than they otherwise would, on a
    // client that is re-served from this deploy on every request. Marking it
    // sensitive would spend a confirmation step on a reversible display change
    // and blunt the signal on the flags where confirmation is load-bearing.
    //
    // No `since` for the same reason: there is no host-side reader and no fleet
    // to converge, so `classifyHostReach` returning `honors` for every host is
    // the literal truth here rather than the trap it is one flag above.
  },
  {
    key: 'open-computer-use',
    description:
      'ENG-10133 (epic ENG-10127) - gates whether an agent gets the ' +
      '`open-computer-use` stdio MCP server, i.e. whether it can drive a human\'s ' +
      'macOS desktop at all. OFF (default) = the `.mcp.json` entry is not written, ' +
      'so the tools do not exist for the agent and `--allowedTools` never carries ' +
      'the wildcard (the allowlist is DERIVED from the server keys - see ' +
      'apps/cli/src/lib/claude-tools.ts, which is why there is no second lever to ' +
      'flip). ON = the entry is materialized for any agent that also holds the ' +
      'integration row; the flag is necessary, never sufficient. ' +
      'THE FLAG IS NOT THE SAFETY CONTROL AND MUST NOT BE READ AS ONE. Every ' +
      'forwarded call is gated per-action against a human-granted approval by ' +
      'packages/mcp/src/computer-use-gate.ts (ENG-10132); this flag decides ' +
      'whether the PATH EXISTS, the gate decides whether a call may travel it. ' +
      'Turning this on for an agent whose host runs a proxy build predating that ' +
      'gate hands it an ungated route to someone\'s desktop - which is why the ' +
      'gate ships in the same proxy binary rather than beside it, so there is no ' +
      'build in which the path exists and the gate does not. ' +
      'Org/agent-scoped rather than global: a desktop belongs to one person, and ' +
      'the blast radius of a wrong answer is that person\'s open windows, mail ' +
      'client and password manager. Deliberately NO envVar - a host-level env ' +
      'override would let a compromised or misconfigured host arm itself, and ' +
      'this is precisely the gate that must be armed centrally (ENG-8303 pin, ' +
      'ENG-7972 4KB cap).',
    flagType: 'boolean',
    // Dark. There is no "today's behaviour" to preserve - nothing on the fleet
    // has this integration - so the safe direction is unambiguous.
    defaultValue: false,
    // Enforcement gate (ADR-0022 section 4). Arming it is "relaxing a security
    // control" in the most literal sense available in this codebase: it is the
    // difference between an agent that can and cannot move a human's mouse.
    // One confirmation is a cheap price for that.
    sensitive: true,
    // Deliberately NOT public. The browser has no decision to make here; the
    // consumers are the host payload that builds `input.integrations` and the
    // grant route that refuses to install the row while the flag is off.
  },
  {
    key: 'ip-allowlist-enforce-admin-debug',
    description:
      'ENG-10594 - whether the IP allowlist on the admin-debug surface ' +
      '(/admin/debug/*, including the deliberately unauthenticated ' +
      'POST /admin/debug/token that exchanges a tlk_ key) REFUSES, or merely ' +
      'observes. OFF (default) = shadow: every request is still evaluated and ' +
      'logged with verdict=admit or verdict=would-refuse, and nothing is ever ' +
      'rejected. ON = a caller whose platform-attested source address is outside ' +
      'AGT_IP_ALLOWLIST_ADMIN_DEBUG gets a bare 403. ' +
      'THE SSM PARAMETER IS A SECOND LEVER AND BOTH ARE NEEDED: with the list ' +
      'unset this flag does nothing, because an unconfigured allowlist admits ' +
      'everyone by design. Arm in that order - populate the list, read the shadow ' +
      'logs until the would-refuse set is understood, then flip this. ' +
      'Deliberately global (evaluated with an empty context): the middleware runs ' +
      'BEFORE authentication, so there is no verified org to scope by, and taking ' +
      'one from an unverified request would let a caller choose its own ' +
      'enforcement posture. ' +
      'There is deliberately NO counterpart flag for the per-agent MCP surface: '
      + 'that one is shadow-only structurally (its ENFORCE_FLAG entry is null), so '
      + 'arming this one does not and cannot arm that one. See the comment below '
      + 'this entry for why.',
    flagType: 'boolean',
    // Shadow. The measurement comes first - and ENG-10664 corrected what the
    // measurement is OF. This comment used to say "nobody yet knows the real set
    // of staff addresses": there is no such set. Measured over 48h, all 315
    // requests to this surface came from ONE address, the agt-hosts AZ-2a NAT
    // EIP - an AGENT calling the debug_* tools, not a person. The decided policy
    // is that it stays that way (admin-debug is only ever reached from our own
    // NAT gateways). So the pre-arm check is not "does my laptop appear" but
    // "is every NAT EIP in the list", because an AZ whose NAT has not yet
    // carried a debug_* call is absent from the log and would be refused the
    // instant this flips. See docs/runbooks/ip-allowlist-rollout.md step 3.
    defaultValue: false,
    // Arming it changes who can reach production diagnostics. Worth one
    // confirmation.
    sensitive: true,
    // Not public: the browser makes no decision from this. The only consumer is
    // packages/api/src/middleware/ip-allowlist.ts.
  },
  // THERE IS DELIBERATELY NO `ip-allowlist-enforce-mcp` FLAG.
  //
  // The per-agent MCP surface (POST /agents/:codeName/mcp) is allowlist
  // SHADOW-ONLY, enforced structurally rather than by a default: its entry in
  // ENFORCE_FLAG (packages/api/src/middleware/ip-allowlist.ts) is null, so no
  // flag is consulted and no flag flip can arm it.
  //
  // Why it is not simply a second flag defaulting false (CORRECTED, ENG-10664):
  //
  // This comment used to say the callers are agent HOSTS, most of which have no
  // allowlistable address per ADR-0032. That is the wrong caller. Agent hosts do
  // not call this surface at all: its handler's first act is
  // validateMcpAccessToken() against `mcp_oauth_tokens` (routes/mcp.ts:87, token
  // check at :91), so a host JWT is rejected outright - routes/host-runtime.ts
  // :33421 says so in as many words, which is why the ENG-4724 /host/* twins
  // exist. The caller set is remote MCP OAuth clients, structurally.
  //
  // The conclusion is unchanged and ONLY the reason has moved. Allowlisting this
  // surface would mean pinning a THIRD PARTY's egress ranges rather than our own
  // NAT EIPs - a different control, a different owner, a different failure mode:
  // they move a range and agent MCP access breaks with no signal on our side.
  // Per docs/runbooks/composio-allowlist-rollout.md this class of change has no
  // fleet-level undo. A flag would leave that one toggle away during an
  // incident, from someone who had just armed the admin-debug surface and
  // reasonably assumed the pair moved together. Requiring a code change puts
  // review in front of an irreversible action, which is the right price.
  //
  // Keeping the false rationale would have been worse than having none: a reader
  // who checked it would find it does not hold and might "fix" it by adding the
  // flag. That is the failure this rewrite exists to prevent.
  //
  // If we ever DECIDE to take on that third-party dependency deliberately,
  // adding the flag here and a key in ENFORCE_FLAG is a small diff - but it is a
  // decision to own someone else's egress, not a discovery that the population
  // was allowlistable all along.
] as const;

const REGISTRY_BY_KEY: ReadonlyMap<string, FlagDefinition> = new Map(
  FLAG_REGISTRY.map((definition) => [definition.key, definition]),
);

export function getFlagDefinition(key: string): FlagDefinition | undefined {
  return REGISTRY_BY_KEY.get(key);
}

export function listFlagDefinitions(): readonly FlagDefinition[] {
  return FLAG_REGISTRY;
}

/**
 * Keys of flags safe to serialize to a browser client (ADR-0022 §2). Only
 * `public: true` flags qualify; the `GET /flags` endpoint filters its
 * evaluated map to this set so private/backend gates never reach the client.
 */
export function listPublicFlagKeys(): readonly string[] {
  return FLAG_REGISTRY.filter((definition) => definition.public === true).map(
    (definition) => definition.key,
  );
}

/** Is this flag a sensitive / enforcement gate (mutation needs confirmation)? */
export function isSensitiveFlag(key: string): boolean {
  return REGISTRY_BY_KEY.get(key)?.sensitive === true;
}

/** One flag's org-scoped override to stamp at organization creation. */
export interface NewOrgFlagOverride {
  key: string;
  /** `feature_flags.flag_type`, needed to materialise the row before the override. */
  flagType: FlagType;
  /** The value written into `feature_flag_overrides.value`. */
  value: FlagValue;
}

/**
 * The flags a NEWLY CREATED organization is armed with (ENG-9629) — the flag-side
 * mirror of `guardrail_definitions.default_for_new_orgs`.
 *
 * This is the single declarative answer to "what feature flags does a new org
 * get?". Before it existed the only such arm was hardcoded inside a
 * single-purpose `armEmailRequireApproval`, so the list was unreadable without
 * grepping the seeding code, and the second entry would have been copy-pasted
 * beside the first.
 *
 * Consumed by `armNewOrgFlags` (`packages/api/src/lib/org-default-flags.ts`).
 * Read the `newOrgOverride` doc in types.ts before adding one — in particular the
 * COVERAGE section, which ENG-9630 rewrote once the self-serve paths were covered.
 *
 * Every declared flag arms on every creation path. ENG-9630 briefly added an
 * origin filter so one flag could decline self-serve; ENG-9675 removed both the
 * declaration and the filter, because a declared-but-unread field is the same
 * phantom-control shape ENG-9611 dropped `approval_channel` to avoid.
 */
export function listNewOrgFlagOverrides(): readonly NewOrgFlagOverride[] {
  const out: NewOrgFlagOverride[] = [];
  for (const definition of FLAG_REGISTRY) {
    if (definition.newOrgOverride === undefined) continue;
    out.push({
      key: definition.key,
      flagType: definition.flagType,
      value: definition.newOrgOverride,
    });
  }
  return out;
}
